<template>
  <div>
    <ops-base page-title="Operations Dashboard">
      <template #controls>
        <div class="controls-group">
          <!-- User Selection - Shared across tabs that need it -->
          <div class="relative user-select-wrapper">
            <v-btn
              @click="toggleUserDropdown($event)"
              outlined
              color="primary"
              :disabled="loading || loadingBackgroundUsers"
            >
              {{ pendingSelectedUsers.length || 0 }} Selected Users
            </v-btn>

            <!-- Custom User Dropdown -->
            <div
              ref="userDropdown"
              v-if="showUserDropdown"
              class="user-dropdown"
              :class="{ 'right-aligned': dropdownRightAligned }"
            >
              <!-- Search Input -->
              <div class="my-2">
                <input
                  ref="userSearchInput"
                  type="text"
                  v-model="searchText"
                  placeholder="Search users..."
                  class="w-full px-2 py-1 border border-gray-300 rounded"
                  @input="filterUsers"
                />
              </div>

              <!-- Select All -->
              <div class="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  :checked="allUsersSelected"
                  @change="toggleSelectAllUsers"
                />
                <span class="font-medium">Select All</span>
              </div>

              <!-- Users List -->
              <div class="max-h-[400px] overflow-y-auto">
                <div
                  v-for="user in filteredUsersList"
                  :key="user.uid"
                  class="p-2 hover:bg-gray-50 group cursor-pointer"
                  @click="toggleUserSelection(user.uid)"
                >
                  <div class="flex items-start gap-2">
                    <input
                      type="checkbox"
                      :checked="pendingSelectedUsers.includes(user.uid)"
                      @click.stop
                      @change="toggleUserSelection(user.uid)"
                      class="mt-1"
                    />
                    <div class="flex flex-col flex-1 pointer-events-none">
                      <span class="text-md font-medium">{{
                        user.displayName
                      }}</span>
                      <span class="text-sm text-gray-500">{{
                        user.email
                      }}</span>
                    </div>
                    <!-- Only Link -->
                    <a
                      href="#"
                      class="text-blue-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity ml-auto pointer-events-auto"
                      @click.stop.prevent="setOnlyThisUser(user)"
                    >
                      Only
                    </a>
                  </div>
                </div>
              </div>

              <!-- No Users Found -->
              <div
                v-if="filteredUsersList.length === 0"
                class="p-4 text-center text-gray-500"
              >
                <v-icon color="grey">mdi-account-search</v-icon>
                <span>No users found</span>
              </div>
            </div>
          </div>

          <!-- Apply Button and Loading Indicator -->
          <div class="apply-button-wrapper ml-2">
            <v-btn
              color="primary"
              @click="applyUserSelection"
              :loading="loadingApplyButton"
              :disabled="
                !hasUserSelectionChanged ||
                loadingApplyButton ||
                loadingBackgroundUsers
              "
            >
              <v-icon left>mdi-check</v-icon>
              Apply
            </v-btn>

            <!-- Background Loading Indicator -->
            <v-progress-circular
              v-if="loadingBackgroundUsers"
              indeterminate
              size="20"
              width="2"
              color="primary"
              class="ml-2"
            ></v-progress-circular>
            <span v-if="loadingBackgroundUsers" class="ml-2 text-caption">
              Fetching new data...
            </span>
          </div>

          <!-- Action Buttons -->
          <!-- <v-btn
          color="primary"
          @click="refreshCurrentTab"
          :loading="loading"
          small
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn> -->

          <!-- <v-btn
          color="secondary"
          @click="exportCurrentTabData"
          :disabled="!hasDataForCurrentTab"
          small
        >
          <v-icon left>mdi-export</v-icon>
          Export
        </v-btn> -->

          <!-- <v-btn
          color="info"
          @click="showTrendChart = !showTrendChart"
          :disabled="!hasDataForCurrentTab"
          small
        >
          <v-icon left>mdi-chart-line</v-icon>
          {{ showTrendChart ? "Hide" : "Show" }} Trends
        </v-btn> -->
        </div>
      </template>

      <template #content>
        <div class="ops-dashboard-container">
          <!-- Timeline Row (appears after user selection) -->
          <div
            v-if="selectedUsers.length > 0 && availableDates.length > 0"
            class="timeline-container"
          >
            <!-- Show loading overlay on timeline when fetching background data -->
            <div v-if="loadingBackgroundUsers" class="timeline-loading-overlay">
              <v-progress-linear
                indeterminate
                color="primary"
                height="2"
              ></v-progress-linear>
            </div>
            <div class="timeline-header">
              <h4 class="timeline-title">
                <v-icon left>mdi-timeline-clock-outline</v-icon>
                Data Timeline
              </h4>
              <p class="timeline-description">
                Select a date to view data from that period
              </p>
            </div>

            <div class="timeline-dates">
              <v-chip-group
                v-model="selectedDateIndex"
                column
                active-class="primary--text"
                @change="onDateSelectionChange"
                :disabled="loading || loadingBackgroundUsers"
              >
                <div
                  v-for="(date, index) in availableDates"
                  :key="date"
                  class="timeline-chip-wrapper"
                >
                  <v-chip
                    :value="index"
                    outlined
                    :color="selectedDateIndex === index ? 'primary' : 'default'"
                    class="timeline-chip"
                    @click="
                      selectedDateIndex = index;
                      onDateSelectionChange();
                    "
                  >
                    <v-icon left small>mdi-calendar</v-icon>
                    {{ formatDateForDisplay(date) }}
                  </v-chip>
                  <v-chip
                    v-if="index === 0"
                    x-small
                    color="success"
                    text-color="white"
                    class="latest-chip"
                  >
                    Latest
                  </v-chip>
                </div>
              </v-chip-group>
            </div>
          </div>

          <!-- Tab Navigation -->
          <v-tabs
            v-model="currentTab"
            background-color="white"
            color="primary"
            grow
            class="ops-tabs"
          >
            <v-tab>
              <v-icon left>mdi-post-outline</v-icon>
              Content
            </v-tab>
            <v-tab>
              <v-icon left>mdi-chart-line</v-icon>
              Performance
            </v-tab>
            <!-- <v-tab>
            <v-icon left>mdi-refresh</v-icon>
            Refreshes
          </v-tab> -->
            <v-tab>
              <v-icon left>mdi-chart-timeline-variant</v-icon>
              Historical Trends
            </v-tab>
          </v-tabs>
          <!-- Tab Content -->
          <v-tabs-items v-model="currentTab" class="ops-tab-content">
            <!-- Content Tab -->
            <v-tab-item>
              <div class="tab-container">
                <!-- Loading State -->
                <div
                  v-if="loading && !hasPostsData && !hasExistingContentData"
                  class="loading-container"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                  ></v-progress-circular>
                  <p class="loading-text">Loading Content Data...</p>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="error-container">
                  <v-alert type="error" outlined>
                    {{ error }}
                  </v-alert>
                </div>

                <!-- Content Data Display -->
                <div
                  v-else-if="hasPostsData || hasExistingContentData"
                  class="data-container"
                >
                  <!-- Show inline loading indicator when fetching new users in background -->
                  <div
                    v-if="loadingBackgroundUsers"
                    class="background-loading-banner"
                  >
                    <v-alert type="info" dense outlined>
                      <v-row align="center" no-gutters>
                        <v-col cols="auto" class="mr-3">
                          <v-progress-circular
                            indeterminate
                            size="20"
                            width="2"
                            color="info"
                          ></v-progress-circular>
                        </v-col>
                        <v-col>
                          Fetching data for newly added users in background...
                        </v-col>
                      </v-row>
                    </v-alert>
                  </div>
                  <div class="hierarchical-table-container table-fixed-height">
                    <v-data-table
                      :headers="postsHierarchicalHeaders"
                      :items="postsHierarchicalData"
                      item-key="id"
                      dense
                      class="hierarchical-stats-table"
                      :items-per-page="-1"
                      hide-default-footer
                      :sort-by.sync="contentSortBy"
                      :sort-desc.sync="contentSortDesc"
                      :custom-sort="customSortContent"
                      must-sort
                      fixed-header
                      :height="600"
                    >
                      <!-- Header tooltips for specific columns -->
                      <template v-slot:header.lastVerified="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Date when the GMB listing was last verified with
                            Google</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.verificationHistory="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span>Timeline showing the verification history</span>
                        </v-tooltip>
                      </template>

                      <template v-slot:header.recentPost="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Date of the most recently published Google My
                            Business post</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.scheduledPosts="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Number of GMB posts that are scheduled to be
                            published in the future</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.scheduledUpToDate="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Latest date until which GMB posts are
                            scheduled</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.profileStrength="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Completion percentage of GMB profile fields
                            (description, hours, photos, etc.)</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.posts="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Total number of Google My Business posts published
                            weekly</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.photos="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Total count of photos uploaded to the GMB listing
                            weekly</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.lastPhotoAdded="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Date when the most recent photo was uploaded to the
                            GMB listing</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.comments="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Add internal comments or view existing comments for
                            this user/admin/store level</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:header.postValidation="{ header }">
                        <span>{{ header.text }}</span>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              class="ml-1 grey--text"
                              style="font-size: 14px"
                              >mdi-information-outline</v-icon
                            >
                          </template>
                          <span
                            >Validates if stores have published atleast 3 posts
                            per week per location.</span
                          >
                        </v-tooltip>
                      </template>

                      <template v-slot:item="{ item, expand, isExpanded }">
                        <tr :class="getRowClass(item)">
                          <td class="text-left">
                            <div
                              class="level-indicator"
                              :class="`level-${item.level}`"
                            >
                              <v-icon
                                v-if="item.hasChildren"
                                small
                                :class="{ 'rotate-90': isExpanded }"
                                class="expand-icon"
                                @click="expand(!isExpanded)"
                                style="cursor: pointer"
                              >
                                mdi-chevron-right
                              </v-icon>
                              <span v-else class="expand-placeholder"></span>
                              <div class="item-info">
                                <v-tooltip
                                  bottom
                                  v-if="
                                    item.displayName &&
                                    item.displayName.length > 15
                                  "
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <strong
                                      :class="
                                        item.level === 1
                                          ? 'user-name'
                                          : item.level === 2
                                          ? 'admin-name'
                                          : 'workspace-name'
                                      "
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      {{
                                        item.displayName.substring(0, 15) +
                                        "..."
                                      }}
                                    </strong>
                                  </template>
                                  <span>{{ item.displayName }}</span>
                                </v-tooltip>
                                <strong
                                  v-else
                                  :class="
                                    item.level === 1
                                      ? 'user-name'
                                      : item.level === 2
                                      ? 'admin-name'
                                      : 'workspace-name'
                                  "
                                >
                                  {{ item.displayName }}
                                </strong>
                                <div v-if="item.subtitle" class="item-subtitle">
                                  {{ item.subtitle }}
                                </div>
                              </div>
                            </div>
                          </td>

                          <!-- Verification History Column -->
                          <td class="text-right metric-cell">
                            <v-menu
                              v-if="
                                item.verificationStatus &&
                                item.verificationStatus !== 'unknown' &&
                                !item.hasChildren
                              "
                              bottom
                              offset-y
                              max-width="500"
                              :close-on-content-click="false"
                              :nudge-bottom="10"
                              :z-index="1000"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                  small
                                  :color="
                                    getVerificationStatusColor(
                                      item.verificationStatus
                                    )
                                  "
                                  dark
                                  v-bind="attrs"
                                  v-on="on"
                                  style="cursor: pointer"
                                  class="verification-status-chip"
                                  :class="item.verificationStatus"
                                >
                                  {{
                                    formatVerificationStatus(
                                      item.verificationStatus
                                    )
                                  }}
                                </v-chip>
                              </template>
                              <v-card
                                class="pa-2"
                                color="white"
                                elevation="8"
                                max-width="500"
                              >
                                <div
                                  class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                >
                                  <v-icon small color="primary" class="mr-1"
                                    >mdi-history</v-icon
                                  >
                                  Last
                                  {{ item.verificationHistoryCount }}
                                  Verification Runs
                                </div>
                                <div class="text-caption grey--text mb-2">
                                  Current status for
                                  {{ formatDateForDisplay(selectedDate) }}:
                                  <strong>{{
                                    formatVerificationStatus(
                                      getMostRecentVerificationStatus(
                                        item.verificationHistoryDetails
                                      )
                                    )
                                  }}</strong>
                                </div>

                                <!-- Verification Timeline -->
                                <div
                                  class="verification-timeline"
                                  style="max-height: 300px; overflow-y: auto"
                                >
                                  <div
                                    v-for="(
                                      attempt, index
                                    ) in getSortedVerificationHistory(
                                      item.verificationHistoryDetails
                                    )"
                                    :key="attempt.id || `attempt_${index}`"
                                    class="verification-attempt d-flex mb-2"
                                  >
                                    <!-- Timeline indicator -->
                                    <div
                                      class="verification-indicator d-flex flex-column align-center mr-3"
                                    >
                                      <v-icon
                                        small
                                        :color="
                                          getVerificationStatusColor(
                                            attempt.status
                                          )
                                        "
                                      >
                                        {{
                                          getVerificationStatusIcon(
                                            attempt.status
                                          )
                                        }}
                                      </v-icon>
                                      <div
                                        v-if="
                                          index <
                                          getSortedVerificationHistory(
                                            item.verificationHistoryDetails
                                          ).length -
                                            1
                                        "
                                        class="verification-connector"
                                      ></div>
                                    </div>

                                    <!-- Attempt details -->
                                    <div
                                      class="verification-content flex-grow-1"
                                    >
                                      <div class="d-flex align-center mb-1">
                                        <span
                                          class="font-weight-medium text-subtitle-2"
                                        >
                                          {{
                                            formatDateTimeForDisplay(
                                              attempt.timestamp
                                            )
                                          }}
                                        </span>
                                        <v-chip
                                          x-small
                                          :color="
                                            getVerificationStatusColor(
                                              attempt.status
                                            )
                                          "
                                          class="ml-2"
                                        >
                                          {{
                                            formatVerificationStatus(
                                              attempt.status
                                            )
                                          }}
                                        </v-chip>
                                      </div>

                                      <div
                                        v-if="attempt.reason"
                                        class="text-caption grey--text"
                                      >
                                        {{ attempt.reason }}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </v-card>
                            </v-menu>
                            <!-- For user/admin level (has children), show summary -->
                            <v-chip
                              v-else-if="
                                item.hasChildren &&
                                item.children &&
                                item.children.length > 0
                              "
                              small
                              :color="getVerificationSummaryColor(item)"
                              dark
                            >
                              {{ getVerificationSummary(item) }}
                            </v-chip>
                            <!-- For store level without history or with unknown status -->
                            <v-chip
                              v-else-if="!item.hasChildren"
                              small
                              :color="
                                getVerificationStatusColor(
                                  item.verificationStatus || 'unknown'
                                )
                              "
                              dark
                              class="verification-status-chip"
                              :class="item.verificationStatus || 'unknown'"
                            >
                              {{
                                formatVerificationStatus(
                                  item.verificationStatus || "unknown"
                                )
                              }}
                            </v-chip>
                            <v-chip v-else small color="grey" dark
                              >No data</v-chip
                            >
                          </td>

                          <td class="text-right metric-cell">
                            <div v-if="item.recentPost">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <div
                                    class="text-caption"
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    {{
                                      item.recentPostDaysAgo
                                        ? `${item.recentPostDaysAgo} day${
                                            item.recentPostDaysAgo !== 1
                                              ? "s"
                                              : ""
                                          } ago`
                                        : formatDateForDisplay(item.recentPost)
                                    }}
                                  </div>
                                </template>
                                <span>{{
                                  formatDateForDisplay(item.recentPost)
                                }}</span>
                              </v-tooltip>
                            </div>
                            <span v-else class="grey--text">No posts</span>
                          </td>
                          <td class="text-right metric-cell">
                            <v-menu
                              bottom
                              offset-y
                              max-width="400"
                              :close-on-content-click="false"
                              :nudge-bottom="10"
                              :z-index="1000"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                  small
                                  :color="
                                    item.scheduledPosts > 0 ? 'success' : 'grey'
                                  "
                                  dark
                                  v-bind="attrs"
                                  v-on="on"
                                  style="cursor: pointer"
                                >
                                  {{ item.scheduledPosts }}
                                </v-chip>
                              </template>
                              <v-card
                                v-if="
                                  item.scheduledPostsDetails &&
                                  item.scheduledPostsDetails.length > 0
                                "
                                class="pa-2"
                                color="white"
                                elevation="8"
                                max-width="400"
                              >
                                <div
                                  class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                >
                                  <v-icon small color="primary" class="mr-1"
                                    >mdi-timeline-clock</v-icon
                                  >
                                  Scheduled Timeline ({{ item.scheduledPosts }})
                                </div>

                                <!-- Compact Timeline View -->
                                <div
                                  class="timeline-container"
                                  style="max-height: 300px; overflow-y: auto"
                                >
                                  <div
                                    v-for="(
                                      dateGroup, index
                                    ) in getSortedScheduledPosts(
                                      item.scheduledPostsDetails
                                    )"
                                    v-bind:key="index"
                                    class="timeline-item d-flex mb-2"
                                  >
                                    <!-- Timeline Line -->
                                    <div
                                      class="timeline-line d-flex flex-column align-center mr-3"
                                    >
                                      <v-icon small color="primary"
                                        >mdi-circle</v-icon
                                      >
                                      <div
                                        v-if="
                                          index <
                                          getSortedScheduledPosts(
                                            item.scheduledPostsDetails
                                          ).length -
                                            1
                                        "
                                        class="timeline-connector"
                                      ></div>
                                    </div>

                                    <!-- Content -->
                                    <div class="timeline-content flex-grow-1">
                                      <div class="d-flex align-center mb-1">
                                        <span
                                          class="font-weight-medium text-subtitle-2 primary--text"
                                          >{{ dateGroup.date }}</span
                                        >
                                        <v-chip
                                          x-small
                                          color="primary"
                                          class="ml-2"
                                          >{{ dateGroup.count }}</v-chip
                                        >
                                      </div>

                                      <!-- Posts for this date -->
                                      <div
                                        v-for="(
                                          post, postIndex
                                        ) in dateGroup.posts"
                                        v-bind:key="postIndex"
                                        class="post-item d-flex align-center mb-1"
                                      >
                                        <v-icon
                                          x-small
                                          color="grey"
                                          class="mr-1"
                                          >mdi-clock-outline</v-icon
                                        >
                                        <span class="text-caption mr-2">{{
                                          formatTime(post.scheduledTime)
                                        }}</span>

                                        <!-- Platform icons inline -->
                                        <div class="d-flex">
                                          <v-tooltip
                                            v-for="platform in post.platforms ||
                                            []"
                                            :key="platform"
                                            top
                                          >
                                            <template
                                              v-slot:activator="{ on, attrs }"
                                            >
                                              <v-icon
                                                x-small
                                                :color="
                                                  getPlatformColor(platform)
                                                "
                                                class="mr-1"
                                                v-bind="attrs"
                                                v-on="on"
                                              >
                                                {{ getPlatformIcon(platform) }}
                                              </v-icon>
                                            </template>
                                            <span>{{
                                              getPlatformName(platform)
                                            }}</span>
                                          </v-tooltip>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </v-card>
                              <v-card
                                v-else
                                class="pa-3"
                                color="white"
                                elevation="8"
                              >
                                <span class="grey--text"
                                  >No scheduled posts</span
                                >
                              </v-card>
                            </v-menu>
                          </td>
                          <td class="text-right metric-cell">
                            <div
                              v-if="item.scheduledUpToDate"
                              class="text-caption"
                            >
                              {{ formatDateForDisplay(item.scheduledUpToDate) }}
                            </div>
                            <span v-else class="grey--text">No schedule</span>
                          </td>
                          <td class="text-right metric-cell">
                            <div v-if="item.profileStrength">
                              <v-menu
                                v-if="
                                  !isProfileComplete(
                                    item.profileStrength,
                                    item.level
                                  )
                                "
                                v-model="item.showProfileMenu"
                                :close-on-content-click="false"
                                :nudge-width="380"
                                offset-y
                                bottom
                                max-width="400"
                                content-class="profile-menu-content"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <div
                                    v-bind="attrs"
                                    v-on="on"
                                    style="cursor: pointer"
                                  >
                                    <div class="profile-strength-display">
                                      <v-chip
                                        small
                                        :color="
                                          getProfileStrengthColor(
                                            item.profileStrength,
                                            item.level
                                          )
                                        "
                                        dark
                                      >
                                        {{
                                          formatProfileStrengthDisplay(
                                            item.profileStrength,
                                            item.level
                                          )
                                        }}
                                      </v-chip>
                                    </div>
                                  </div>
                                </template>

                                <v-card
                                  v-if="item.profileStrength"
                                  class="pa-2"
                                  color="white"
                                  elevation="8"
                                  max-width="400"
                                >
                                  <div
                                    class="font-weight-bold mb-1 text-subtitle-2 primary--text d-flex align-center"
                                  >
                                    <v-icon small color="primary" class="mr-1"
                                      >mdi-account-check</v-icon
                                    >
                                    {{ item.displayName }}
                                  </div>

                                  <div
                                    class="profile-details-container"
                                    style="max-height: 250px; overflow-y: auto"
                                  >
                                    <div
                                      v-html="
                                        getProfileStrengthTooltip(
                                          item.profileStrength,
                                          item.level
                                        )
                                      "
                                    ></div>
                                  </div>
                                </v-card>

                                <v-card
                                  v-else
                                  class="pa-3"
                                  color="white"
                                  elevation="8"
                                >
                                  <span class="grey--text"
                                    >No profile data</span
                                  >
                                </v-card>
                              </v-menu>

                              <!-- Show chip directly if profile is 100% complete -->
                              <div
                                v-if="
                                  isProfileComplete(
                                    item.profileStrength,
                                    item.level
                                  )
                                "
                                class="profile-strength-display"
                              >
                                <v-chip
                                  small
                                  :color="
                                    getProfileStrengthColor(
                                      item.profileStrength,
                                      item.level
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    formatProfileStrengthDisplay(
                                      item.profileStrength,
                                      item.level
                                    )
                                  }}
                                </v-chip>
                              </div>
                            </div>
                            <span v-else class="grey--text">No data</span>
                          </td>
                          <!-- Profile Field Columns -->
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="getProfileFieldColor(item, 'description')"
                              dark
                            >
                              {{ getProfileFieldDisplay(item, "description") }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="getProfileFieldColor(item, 'websiteUri')"
                              dark
                            >
                              {{ getProfileFieldDisplay(item, "websiteUri") }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="
                                getProfileFieldColor(item, 'primaryCategory')
                              "
                              dark
                            >
                              {{
                                getProfileFieldDisplay(item, "primaryCategory")
                              }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="getProfileFieldColor(item, 'phoneNumber')"
                              dark
                            >
                              {{ getProfileFieldDisplay(item, "phoneNumber") }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="getProfileFieldColor(item, 'hasLogo')"
                              dark
                            >
                              {{ getProfileFieldDisplay(item, "hasLogo") }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="
                                getProfileFieldColor(item, 'hasCoverPhoto')
                              "
                              dark
                            >
                              {{
                                getProfileFieldDisplay(item, "hasCoverPhoto")
                              }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="getProfileFieldColor(item, 'hasPhotos')"
                              dark
                            >
                              {{ getProfileFieldDisplay(item, "hasPhotos") }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="
                                getProfileFieldColor(item, 'regularHours')
                              "
                              dark
                            >
                              {{ getProfileFieldDisplay(item, "regularHours") }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="
                                getProfileFieldColor(
                                  item,
                                  'additionalCategories'
                                )
                              "
                              dark
                            >
                              {{
                                getProfileFieldDisplay(
                                  item,
                                  "additionalCategories"
                                )
                              }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              small
                              :color="
                                getProfileFieldColor(item, 'attributeCount')
                              "
                              dark
                            >
                              {{
                                getProfileFieldDisplay(item, "attributeCount")
                              }}
                            </v-chip>
                          </td>
                          <td class="text-right metric-cell">
                            {{ formatNumber(item.posts || 0) }}
                          </td>
                          <td class="text-right metric-cell">
                            {{ formatNumber(item.photos || 0) }}
                          </td>
                          <td class="text-right metric-cell">
                            <div v-if="item.lastPhotoAdded">
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <div
                                    class="text-caption"
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    {{
                                      item.lastPhotoAddedDaysAgo
                                        ? `${item.lastPhotoAddedDaysAgo} day${
                                            item.lastPhotoAddedDaysAgo !== 1
                                              ? "s"
                                              : ""
                                          } ago`
                                        : formatDateForDisplay(
                                            item.lastPhotoAdded
                                          )
                                    }}
                                  </div>
                                </template>
                                <span>{{
                                  formatDateForDisplay(item.lastPhotoAdded)
                                }}</span>
                              </v-tooltip>
                            </div>
                            <span v-else class="grey--text">No photos</span>
                          </td>
                          <td class="text-center" style="min-width: 180px">
                            <div class="">
                              <!-- Add Comment Button -->
                              <v-btn
                                x-small
                                color="primary"
                                @click="openAddCommentDialog(item)"
                              >
                                Add
                              </v-btn>

                              <!-- Show Comments Button -->
                              <v-menu
                                offset-y
                                max-width="400"
                                :close-on-content-click="false"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn
                                    x-small
                                    color="grey"
                                    outlined
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="fetchComments(item, true)"
                                  >
                                    Show
                                  </v-btn>
                                </template>
                                <v-card>
                                  <v-card-title class="py-2">
                                    Comments
                                  </v-card-title>
                                  <v-divider></v-divider>
                                  <v-card-text
                                    style="max-height: 300px; overflow-y: auto"
                                  >
                                    <div
                                      v-if="loadingComments[item.id]"
                                      class="text-center py-3"
                                    >
                                      <v-progress-circular
                                        indeterminate
                                        size="24"
                                      ></v-progress-circular>
                                      <div class="caption mt-2">
                                        Loading comments...
                                      </div>
                                    </div>
                                    <div
                                      v-else-if="
                                        commentsCache[item.id] &&
                                        commentsCache[item.id].length
                                      "
                                    >
                                      <div
                                        v-for="(comment, idx) in commentsCache[
                                          item.id
                                        ]"
                                        :key="idx"
                                        class="comment-item mb-2"
                                      >
                                        <div
                                          class="d-flex justify-space-between align-center mb-1"
                                        >
                                          <span
                                            class="comment-author font-weight-bold"
                                            >{{ comment.author }}</span
                                          >
                                          <span
                                            class="comment-date caption grey--text"
                                            >{{
                                              formatDateTimeForDisplay(
                                                comment.timestamp
                                              )
                                            }}</span
                                          >
                                        </div>
                                        <div class="comment-text">
                                          {{ comment.text }}
                                        </div>
                                        <v-divider
                                          v-if="
                                            idx <
                                            commentsCache[item.id].length - 1
                                          "
                                          class="mt-2"
                                        ></v-divider>
                                      </div>
                                    </div>
                                    <div
                                      v-else
                                      class="grey--text text-center py-3"
                                    >
                                      No comments yet
                                    </div>
                                  </v-card-text>
                                </v-card>
                              </v-menu>
                            </div>
                          </td>

                          <!-- In the Content Tab v-data-table, add this column after "Comments" -->
                          <td class="text-right metric-cell">
                            <v-menu
                              v-if="
                                (item.level === 3 ||
                                  (item.level === 2 && !item.hasChildren)) &&
                                item.postValidationDetails &&
                                item.postValidationDetails.length > 1
                              "
                              bottom
                              offset-y
                              max-width="350"
                              :close-on-content-click="false"
                              :nudge-bottom="10"
                              :z-index="1000"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                  small
                                  :color="
                                    item.postValidation === 'Valid'
                                      ? 'success'
                                      : item.postValidation === 'Invalid'
                                      ? 'error'
                                      : 'grey'
                                  "
                                  dark
                                  v-bind="attrs"
                                  v-on="on"
                                  style="cursor: pointer"
                                >
                                  {{ item.postValidation }}
                                  <span
                                    v-if="item.posts !== undefined"
                                    class="ml-1"
                                    >({{ item.posts }})</span
                                  >
                                </v-chip>
                              </template>
                              <v-card
                                class="pa-2"
                                color="white"
                                elevation="8"
                                max-width="350"
                              >
                                <div
                                  class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                >
                                  <v-icon small color="primary" class="mr-1"
                                    >mdi-history</v-icon
                                  >
                                  Weekly Post Validation (last 5 weeks)
                                </div>
                                <div class="text-caption grey--text mb-2">
                                  <strong>Validation Rule:</strong> At least 3
                                  posts published per week per location.
                                </div>
                                <div
                                  style="max-height: 200px; overflow-y: auto"
                                >
                                  <table style="width: 100%; font-size: 13px">
                                    <thead>
                                      <tr>
                                        <th style="text-align: left">Week</th>
                                        <th style="text-align: right">Posts</th>
                                        <th style="text-align: center">
                                          Status
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr
                                        v-for="week in item.postValidationDetails"
                                        :key="week.week"
                                      >
                                        <td style="text-align: left">
                                          {{ formatDateForDisplay(week.week) }}
                                        </td>
                                        <td style="text-align: right">
                                          {{ week.count }}
                                        </td>
                                        <td style="text-align: center">
                                          <v-chip
                                            x-small
                                            :color="
                                              week.valid ? 'success' : 'error'
                                            "
                                            dark
                                          >
                                            {{
                                              week.valid ? "Valid" : "Invalid"
                                            }}
                                          </v-chip>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </v-card>
                            </v-menu>
                            <v-chip
                              v-else-if="
                                item.level === 3 ||
                                (item.level === 2 && !item.hasChildren)
                              "
                              small
                              :color="
                                item.postValidation === 'Valid'
                                  ? 'success'
                                  : item.postValidation === 'Invalid'
                                  ? 'error'
                                  : 'grey'
                              "
                              dark
                            >
                              {{ item.postValidation }}
                              <span v-if="item.posts !== undefined" class="ml-1"
                                >({{ item.posts }})</span
                              >
                            </v-chip>
                            <span v-else>-</span>
                          </td>
                        </tr>
                      </template>

                      <template v-slot:expanded-item="{ item }">
                        <template v-for="child in item.children">
                          <tr :key="child.id" :class="getRowClass(child)">
                            <td class="text-left">
                              <div
                                class="level-indicator"
                                :class="`level-${child.level}`"
                              >
                                <v-icon
                                  v-if="child.hasChildren"
                                  small
                                  @click="toggleChildExpand(child)"
                                  :class="{
                                    'rotate-90': child.expanded || false,
                                  }"
                                  class="expand-icon"
                                  style="cursor: pointer"
                                >
                                  mdi-chevron-right
                                </v-icon>
                                <span v-else class="expand-placeholder"></span>
                                <div class="item-info">
                                  <strong
                                    :class="
                                      child.level === 2
                                        ? 'admin-name'
                                        : 'workspace-name'
                                    "
                                  >
                                    <v-tooltip
                                      bottom
                                      v-if="
                                        child.displayName &&
                                        child.displayName.length > 15
                                      "
                                    >
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <span
                                          v-bind="attrs"
                                          v-on="on"
                                          @click="
                                            navigateToProfile(child.mapsUri)
                                          "
                                        >
                                          {{
                                            child.displayName.substring(0, 15) +
                                            "..."
                                          }}
                                        </span>
                                      </template>
                                      <span>{{ child.displayName }}</span>
                                    </v-tooltip>
                                    <span
                                      v-else
                                      @click="navigateToProfile(child.mapsUri)"
                                    >
                                      {{ child.displayName }}
                                    </span>
                                  </strong>
                                  <div
                                    v-if="child.subtitle"
                                    class="item-subtitle"
                                  >
                                    {{ child.subtitle }}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <!-- Verification History Column -->
                            <td class="text-right metric-cell">
                              <v-menu
                                v-if="
                                  child.verificationStatus &&
                                  child.verificationStatus !== 'unknown' &&
                                  !child.hasChildren
                                "
                                bottom
                                offset-y
                                max-width="500"
                                :close-on-content-click="false"
                                :nudge-bottom="10"
                                :z-index="1000"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-chip
                                    small
                                    :color="
                                      getVerificationStatusColor(
                                        child.verificationStatus
                                      )
                                    "
                                    dark
                                    v-bind="attrs"
                                    v-on="on"
                                    style="cursor: pointer"
                                    class="verification-status-chip"
                                    :class="child.verificationStatus"
                                  >
                                    {{
                                      formatVerificationStatus(
                                        child.verificationStatus
                                      )
                                    }}
                                  </v-chip>
                                </template>
                                <v-card
                                  class="pa-2"
                                  color="white"
                                  elevation="8"
                                  max-width="500"
                                >
                                  <div
                                    class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                  >
                                    <v-icon small color="primary" class="mr-1"
                                      >mdi-history</v-icon
                                    >
                                    Verification History ({{
                                      child.verificationHistoryCount
                                    }}
                                    runs)
                                  </div>
                                  <div class="text-caption grey--text mb-2">
                                    Current status for
                                    {{ formatDateForDisplay(selectedDate) }}:
                                    <strong>{{
                                      formatVerificationStatus(
                                        getMostRecentVerificationStatus(
                                          child.verificationHistoryDetails
                                        )
                                      )
                                    }}</strong>
                                  </div>

                                  <!-- Verification Timeline -->
                                  <div
                                    class="verification-timeline"
                                    style="max-height: 250px; overflow-y: auto"
                                  >
                                    <div
                                      v-for="(
                                        attempt, index
                                      ) in getSortedVerificationHistory(
                                        child.verificationHistoryDetails
                                      )"
                                      :key="
                                        attempt.id || `child_attempt_${index}`
                                      "
                                      class="verification-attempt d-flex mb-2"
                                    >
                                      <!-- Timeline indicator -->
                                      <div
                                        class="verification-indicator d-flex flex-column align-center mr-3"
                                      >
                                        <v-icon
                                          small
                                          :color="
                                            getVerificationStatusColor(
                                              attempt.status
                                            )
                                          "
                                        >
                                          {{
                                            getVerificationStatusIcon(
                                              attempt.status
                                            )
                                          }}
                                        </v-icon>
                                        <div
                                          v-if="
                                            index <
                                            getSortedVerificationHistory(
                                              child.verificationHistoryDetails
                                            ).length -
                                              1
                                          "
                                          class="verification-connector"
                                        ></div>
                                      </div>

                                      <!-- Attempt details -->
                                      <div
                                        class="verification-content flex-grow-1"
                                      >
                                        <div class="d-flex align-center mb-1">
                                          <span
                                            class="font-weight-medium text-subtitle-2"
                                          >
                                            {{
                                              formatDateTimeForDisplay(
                                                attempt.timestamp
                                              )
                                            }}
                                          </span>
                                          <v-chip
                                            x-small
                                            :color="
                                              getVerificationStatusColor(
                                                attempt.status
                                              )
                                            "
                                            class="ml-2"
                                          >
                                            {{
                                              formatVerificationStatus(
                                                attempt.status
                                              )
                                            }}
                                          </v-chip>
                                        </div>

                                        <div
                                          v-if="attempt.reason"
                                          class="text-caption grey--text"
                                        >
                                          {{ attempt.reason }}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </v-card>
                              </v-menu>
                              <!-- For user/admin level (level < 3), show summary -->
                              <v-chip
                                v-else-if="
                                  child.hasChildren &&
                                  child.children &&
                                  child.children.length > 0
                                "
                                small
                                :color="getVerificationSummaryColor(child)"
                                dark
                              >
                                {{ getVerificationSummary(child) }}
                              </v-chip>
                              <!-- For store level without history or with unknown status -->
                              <v-chip
                                v-else-if="!child.hasChildren"
                                small
                                :color="
                                  getVerificationStatusColor(
                                    child.verificationStatus || 'unknown'
                                  )
                                "
                                dark
                                class="verification-status-chip"
                                :class="child.verificationStatus || 'unknown'"
                              >
                                {{
                                  formatVerificationStatus(
                                    child.verificationStatus || "unknown"
                                  )
                                }}
                              </v-chip>
                              <v-chip v-else small color="grey" dark
                                >No data</v-chip
                              >
                            </td>

                            <td class="text-right metric-cell">
                              <div v-if="child.recentPost">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <div
                                      class="text-caption"
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      {{
                                        child.recentPostDaysAgo
                                          ? `${child.recentPostDaysAgo} day${
                                              child.recentPostDaysAgo !== 1
                                                ? "s"
                                                : ""
                                            } ago`
                                          : formatDateForDisplay(
                                              child.recentPost
                                            )
                                      }}
                                    </div>
                                  </template>
                                  <span>{{
                                    formatDateForDisplay(child.recentPost)
                                  }}</span>
                                </v-tooltip>
                              </div>
                              <span v-else class="grey--text">No posts</span>
                            </td>
                            <td class="text-right metric-cell">
                              <v-menu
                                bottom
                                offset-y
                                max-width="400"
                                :close-on-content-click="false"
                                :nudge-bottom="10"
                                :z-index="1000"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-chip
                                    small
                                    :color="
                                      child.scheduledPosts > 0
                                        ? 'success'
                                        : 'grey'
                                    "
                                    dark
                                    v-bind="attrs"
                                    v-on="on"
                                    style="cursor: pointer"
                                  >
                                    {{ child.scheduledPosts }}
                                  </v-chip>
                                </template>
                                <v-card
                                  v-if="
                                    child.scheduledPostsDetails &&
                                    child.scheduledPostsDetails.length > 0
                                  "
                                  class="pa-2"
                                  color="white"
                                  elevation="8"
                                  max-width="400"
                                >
                                  <div
                                    class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                  >
                                    <v-icon small color="primary" class="mr-1"
                                      >mdi-timeline-clock</v-icon
                                    >
                                    Scheduled Timeline ({{
                                      child.scheduledPosts
                                    }})
                                  </div>

                                  <!-- Compact Timeline View -->
                                  <div
                                    class="timeline-container"
                                    style="max-height: 250px; overflow-y: auto"
                                  >
                                    <div
                                      v-for="(
                                        dateGroup, index
                                      ) in getSortedScheduledPosts(
                                        child.scheduledPostsDetails
                                      )"
                                      v-bind:key="index"
                                      class="timeline-item d-flex mb-2"
                                    >
                                      <!-- Timeline Line -->
                                      <div
                                        class="timeline-line d-flex flex-column align-center mr-3"
                                      >
                                        <v-icon small color="primary"
                                          >mdi-circle</v-icon
                                        >
                                        <div
                                          v-if="
                                            index <
                                            getSortedScheduledPosts(
                                              child.scheduledPostsDetails
                                            ).length -
                                              1
                                          "
                                          class="timeline-connector"
                                        ></div>
                                      </div>

                                      <!-- Content -->
                                      <div class="timeline-content flex-grow-1">
                                        <div class="d-flex align-center mb-1">
                                          <span
                                            class="font-weight-medium text-subtitle-2 primary--text"
                                            >{{ dateGroup.date }}</span
                                          >
                                          <v-chip
                                            x-small
                                            color="primary"
                                            class="ml-2"
                                            >{{ dateGroup.count }}</v-chip
                                          >
                                        </div>

                                        <!-- Posts for this date -->
                                        <div
                                          v-for="(
                                            post, postIndex
                                          ) in dateGroup.posts"
                                          v-bind:key="postIndex"
                                          class="post-item d-flex align-center mb-1"
                                        >
                                          <v-icon
                                            x-small
                                            color="grey"
                                            class="mr-1"
                                            >mdi-clock-outline</v-icon
                                          >
                                          <span class="text-caption mr-2">{{
                                            formatTime(post.scheduledTime)
                                          }}</span>

                                          <!-- Platform icons inline -->
                                          <div class="d-flex">
                                            <v-tooltip
                                              v-for="platform in post.platforms ||
                                              []"
                                              :key="platform"
                                              top
                                            >
                                              <template
                                                v-slot:activator="{ on, attrs }"
                                              >
                                                <v-icon
                                                  x-small
                                                  :color="
                                                    getPlatformColor(platform)
                                                  "
                                                  class="mr-1"
                                                  v-bind="attrs"
                                                  v-on="on"
                                                >
                                                  {{
                                                    getPlatformIcon(platform)
                                                  }}
                                                </v-icon>
                                              </template>
                                              <span>{{
                                                getPlatformName(platform)
                                              }}</span>
                                            </v-tooltip>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </v-card>
                                <v-card
                                  v-else
                                  class="pa-3"
                                  color="white"
                                  elevation="8"
                                >
                                  <span class="grey--text"
                                    >No scheduled posts</span
                                  >
                                </v-card>
                              </v-menu>
                            </td>
                            <td class="text-right metric-cell">
                              <div
                                v-if="child.scheduledUpToDate"
                                class="text-caption"
                              >
                                {{
                                  formatDateForDisplay(child.scheduledUpToDate)
                                }}
                              </div>
                              <span v-else class="grey--text">No schedule</span>
                            </td>
                            <td class="text-right metric-cell">
                              <div v-if="child.profileStrength">
                                <v-menu
                                  v-if="
                                    !isProfileComplete(
                                      child.profileStrength,
                                      child.level
                                    )
                                  "
                                  v-model="child.showProfileMenu"
                                  :close-on-content-click="false"
                                  :nudge-width="380"
                                  bottom
                                  offset-y
                                  max-width="400"
                                  content-class="profile-menu-content"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <div
                                      v-bind="attrs"
                                      v-on="on"
                                      style="cursor: pointer"
                                    >
                                      <div class="profile-strength-display">
                                        <v-chip
                                          small
                                          :color="
                                            getProfileStrengthColor(
                                              child.profileStrength,
                                              child.level
                                            )
                                          "
                                          dark
                                        >
                                          {{
                                            formatProfileStrengthDisplay(
                                              child.profileStrength,
                                              child.level
                                            )
                                          }}
                                        </v-chip>
                                      </div>
                                    </div>
                                  </template>

                                  <v-card
                                    v-if="child.profileStrength"
                                    class="pa-3"
                                    color="white"
                                    elevation="8"
                                    max-width="420"
                                  >
                                    <div
                                      class="font-weight-bold mb-1 text-subtitle-2 primary--text d-flex align-center"
                                    >
                                      <v-icon small color="primary" class="mr-1"
                                        >mdi-account-check</v-icon
                                      >
                                      {{ child.displayName }}
                                    </div>

                                    <div
                                      class="profile-details-container"
                                      style="
                                        max-height: 250px;
                                        overflow-y: auto;
                                      "
                                    >
                                      <div
                                        v-html="
                                          getProfileStrengthTooltip(
                                            child.profileStrength,
                                            child.level
                                          )
                                        "
                                      ></div>
                                    </div>
                                  </v-card>

                                  <v-card
                                    v-else
                                    class="pa-3"
                                    color="white"
                                    elevation="8"
                                  >
                                    <span class="grey--text"
                                      >No profile data</span
                                    >
                                  </v-card>
                                </v-menu>

                                <!-- Show chip directly if profile is 100% complete -->
                                <div
                                  v-if="
                                    isProfileComplete(
                                      child.profileStrength,
                                      child.level
                                    )
                                  "
                                  class="profile-strength-display"
                                >
                                  <v-chip
                                    small
                                    :color="
                                      getProfileStrengthColor(
                                        child.profileStrength,
                                        child.level
                                      )
                                    "
                                    dark
                                  >
                                    {{
                                      formatProfileStrengthDisplay(
                                        child.profileStrength,
                                        child.level
                                      )
                                    }}
                                  </v-chip>
                                </div>
                              </div>
                              <span v-else class="grey--text">No data</span>
                            </td>
                            <!-- Profile Field Columns for Child -->
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'description')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(child, "description")
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'websiteUri')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(child, "websiteUri")
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'primaryCategory')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(
                                    child,
                                    "primaryCategory"
                                  )
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'phoneNumber')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(child, "phoneNumber")
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="getProfileFieldColor(child, 'hasLogo')"
                                dark
                              >
                                {{ getProfileFieldDisplay(child, "hasLogo") }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'hasCoverPhoto')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(child, "hasCoverPhoto")
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'hasPhotos')
                                "
                                dark
                              >
                                {{ getProfileFieldDisplay(child, "hasPhotos") }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'regularHours')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(child, "regularHours")
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(
                                    child,
                                    'additionalCategories'
                                  )
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(
                                    child,
                                    "additionalCategories"
                                  )
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                small
                                :color="
                                  getProfileFieldColor(child, 'attributeCount')
                                "
                                dark
                              >
                                {{
                                  getProfileFieldDisplay(
                                    child,
                                    "attributeCount"
                                  )
                                }}
                              </v-chip>
                            </td>
                            <td class="text-right metric-cell">
                              {{ formatNumber(child.posts || 0) }}
                            </td>
                            <td class="text-right metric-cell">
                              {{ formatNumber(child.photos || 0) }}
                            </td>
                            <td class="text-right metric-cell">
                              <div v-if="child.lastPhotoAdded">
                                <v-tooltip bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <div
                                      class="text-caption"
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      {{
                                        child.lastPhotoAddedDaysAgo
                                          ? `${
                                              child.lastPhotoAddedDaysAgo
                                            } day${
                                              child.lastPhotoAddedDaysAgo !== 1
                                                ? "s"
                                                : ""
                                            } ago`
                                          : formatDateForDisplay(
                                              child.lastPhotoAdded
                                            )
                                      }}
                                    </div>
                                  </template>
                                  <span>{{
                                    formatDateForDisplay(child.lastPhotoAdded)
                                  }}</span>
                                </v-tooltip>
                              </div>
                              <span v-else class="grey--text">No photos</span>
                            </td>
                            <td class="text-center" style="min-width: 180px">
                              <div class="">
                                <!-- Add Comment Button -->
                                <v-btn
                                  x-small
                                  color="primary"
                                  @click="openAddCommentDialog(child)"
                                >
                                  Add
                                </v-btn>

                                <!-- Show Comments Button -->
                                <v-menu
                                  offset-y
                                  max-width="400"
                                  :close-on-content-click="false"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                      x-small
                                      color="grey"
                                      outlined
                                      v-bind="attrs"
                                      v-on="on"
                                      @click="fetchComments(child, true)"
                                    >
                                      Show
                                    </v-btn>
                                  </template>
                                  <v-card>
                                    <v-card-title class="py-2">
                                      Comments
                                    </v-card-title>
                                    <v-divider></v-divider>
                                    <v-card-text
                                      style="
                                        max-height: 300px;
                                        overflow-y: auto;
                                      "
                                    >
                                      <div
                                        v-if="loadingComments[child.id]"
                                        class="text-center py-3"
                                      >
                                        <v-progress-circular
                                          indeterminate
                                          size="24"
                                        ></v-progress-circular>
                                        <div class="caption mt-2">
                                          Loading comments...
                                        </div>
                                      </div>
                                      <div
                                        v-else-if="
                                          commentsCache[child.id] &&
                                          commentsCache[child.id].length
                                        "
                                      >
                                        <div
                                          v-for="(
                                            comment, idx
                                          ) in commentsCache[child.id]"
                                          :key="idx"
                                          class="comment-item mb-2"
                                        >
                                          <div
                                            class="d-flex justify-space-between align-center mb-1"
                                          >
                                            <span
                                              class="comment-author font-weight-bold"
                                              >{{ comment.author }}</span
                                            >
                                            <span
                                              class="comment-date caption grey--text"
                                              >{{
                                                formatDateTimeForDisplay(
                                                  comment.timestamp
                                                )
                                              }}</span
                                            >
                                          </div>
                                          <div class="comment-text">
                                            {{ comment.text }}
                                          </div>
                                          <v-divider
                                            v-if="
                                              idx <
                                              commentsCache[child.id].length - 1
                                            "
                                            class="mt-2"
                                          ></v-divider>
                                        </div>
                                      </div>
                                      <div
                                        v-else
                                        class="grey--text text-center py-3"
                                      >
                                        No comments yet
                                      </div>
                                    </v-card-text>
                                  </v-card>
                                </v-menu>
                              </div>
                            </td>
                            <td class="text-right metric-cell">
                              <v-menu
                                v-if="
                                  (child.level === 3 ||
                                    (child.level === 2 &&
                                      !child.hasChildren)) &&
                                  child.postValidationDetails &&
                                  child.postValidationDetails.length > 1
                                "
                                bottom
                                offset-y
                                max-width="350"
                                :close-on-content-click="false"
                                :nudge-bottom="10"
                                :z-index="1000"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <v-chip
                                    small
                                    :color="
                                      child.postValidation === 'Valid'
                                        ? 'success'
                                        : child.postValidation === 'Invalid'
                                        ? 'error'
                                        : 'grey'
                                    "
                                    dark
                                    v-bind="attrs"
                                    v-on="on"
                                    style="cursor: pointer"
                                  >
                                    {{ child.postValidation }}
                                    <span
                                      v-if="child.posts !== undefined"
                                      class="ml-1"
                                      >({{ child.posts }})</span
                                    >
                                  </v-chip>
                                </template>
                                <v-card
                                  class="pa-2"
                                  color="white"
                                  elevation="8"
                                  max-width="350"
                                >
                                  <div
                                    class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                  >
                                    <v-icon small color="primary" class="mr-1"
                                      >mdi-history</v-icon
                                    >
                                    Weekly Post Validation (last 5 weeks)
                                  </div>
                                  <div class="text-caption grey--text mb-2">
                                    <strong>Validation Rule:</strong> At least 3
                                    posts published per week per location.
                                  </div>
                                  <div
                                    style="max-height: 200px; overflow-y: auto"
                                  >
                                    <table style="width: 100%; font-size: 13px">
                                      <thead>
                                        <tr>
                                          <th style="text-align: left">Week</th>
                                          <th style="text-align: right">
                                            Posts
                                          </th>
                                          <th style="text-align: center">
                                            Status
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr
                                          v-for="week in child.postValidationDetails"
                                          :key="week.week"
                                        >
                                          <td style="text-align: left">
                                            {{
                                              formatDateForDisplay(week.week)
                                            }}
                                          </td>
                                          <td style="text-align: right">
                                            {{ week.count }}
                                          </td>
                                          <td style="text-align: center">
                                            <v-chip
                                              x-small
                                              :color="
                                                week.valid ? 'success' : 'error'
                                              "
                                              dark
                                            >
                                              {{
                                                week.valid ? "Valid" : "Invalid"
                                              }}
                                            </v-chip>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </v-card>
                              </v-menu>
                              <v-chip
                                v-else-if="
                                  child.level === 3 ||
                                  (child.level === 2 && !child.hasChildren)
                                "
                                small
                                :color="
                                  child.postValidation === 'Valid'
                                    ? 'success'
                                    : child.postValidation === 'Invalid'
                                    ? 'error'
                                    : 'grey'
                                "
                                dark
                              >
                                {{ child.postValidation }}
                                <span
                                  v-if="child.posts !== undefined"
                                  class="ml-1"
                                  >({{ child.posts }})</span
                                >
                              </v-chip>
                              <span v-else>-</span>
                            </td>
                          </tr>

                          <!-- Third level (Store Workspaces under Admin) -->
                          <template
                            v-if="
                              child.expanded &&
                              child.children &&
                              child.children.length > 0
                            "
                          >
                            <tr
                              v-for="grandchild in child.children"
                              :key="grandchild.id"
                              :class="getRowClass(grandchild)"
                            >
                              <td class="text-left">
                                <div
                                  class="level-indicator"
                                  :class="`level-${grandchild.level}`"
                                >
                                  <span class="expand-placeholder"></span>
                                  <div class="item-info">
                                    <strong class="workspace-name">
                                      <v-tooltip
                                        bottom
                                        v-if="
                                          grandchild.displayName &&
                                          grandchild.displayName.length > 15
                                        "
                                      >
                                        <template
                                          v-slot:activator="{ on, attrs }"
                                        >
                                          <span
                                            @click="
                                              navigateToProfile(
                                                grandchild.mapsUri
                                              )
                                            "
                                            class="primary--text cursor-pointer"
                                            style="
                                              cursor: pointer;
                                              text-decoration: underline;
                                            "
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            {{
                                              grandchild.displayName.substring(
                                                0,
                                                15
                                              ) + "..."
                                            }}
                                          </span>
                                        </template>
                                        <span>{{
                                          grandchild.displayName
                                        }}</span>
                                      </v-tooltip>
                                      <span
                                        v-else
                                        @click="
                                          navigateToProfile(grandchild.mapsUri)
                                        "
                                        class="primary--text cursor-pointer"
                                        style="
                                          cursor: pointer;
                                          text-decoration: underline;
                                        "
                                      >
                                        {{ grandchild.displayName }}
                                      </span>
                                    </strong>
                                    <div
                                      v-if="grandchild.subtitle"
                                      class="item-subtitle"
                                    >
                                      {{ grandchild.subtitle }}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <!-- Verification History Column -->
                              <td class="text-right metric-cell">
                                <v-menu
                                  v-if="
                                    grandchild.verificationHistoryCount > 0
                                  "
                                  bottom
                                  offset-y
                                  max-width="500"
                                  :close-on-content-click="false"
                                  :nudge-bottom="10"
                                  :z-index="1000"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                      small
                                      :color="
                                        getVerificationStatusColor(
                                          grandchild.verificationStatus
                                        )
                                      "
                                      dark
                                      v-bind="attrs"
                                      v-on="on"
                                      style="cursor: pointer"
                                      class="verification-status-chip"
                                      :class="grandchild.verificationStatus"
                                    >
                                      {{
                                        formatVerificationStatus(
                                          grandchild.verificationStatus
                                        )
                                      }}
                                    </v-chip>
                                  </template>
                                  <v-card
                                    class="pa-2"
                                    color="white"
                                    elevation="8"
                                    max-width="500"
                                  >
                                    <div
                                      class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                    >
                                      <v-icon small color="primary" class="mr-1"
                                        >mdi-history</v-icon
                                      >
                                      Verification History ({{
                                        grandchild.verificationHistoryCount
                                      }}
                                      runs)
                                    </div>
                                    <div class="text-caption grey--text mb-2">
                                      Current status for
                                      {{ formatDateForDisplay(selectedDate) }}:
                                      <strong>{{
                                        formatVerificationStatus(
                                          getMostRecentVerificationStatus(
                                            grandchild.verificationHistoryDetails
                                          )
                                        )
                                      }}</strong>
                                    </div>

                                    <!-- Verification Timeline -->
                                    <div
                                      class="verification-timeline"
                                      style="
                                        max-height: 250px;
                                        overflow-y: auto;
                                      "
                                    >
                                      <div
                                        v-for="(
                                          attempt, index
                                        ) in getSortedVerificationHistory(
                                          grandchild.verificationHistoryDetails
                                        )"
                                        :key="
                                          attempt.id ||
                                          `grandchild_attempt_${index}`
                                        "
                                        class="verification-attempt d-flex mb-2"
                                      >
                                        <!-- Timeline indicator -->
                                        <div
                                          class="verification-indicator d-flex flex-column align-center mr-3"
                                        >
                                          <v-icon
                                            small
                                            :color="
                                              getVerificationStatusColor(
                                                attempt.status
                                              )
                                            "
                                          >
                                            {{
                                              getVerificationStatusIcon(
                                                attempt.status
                                              )
                                            }}
                                          </v-icon>
                                          <div
                                            v-if="
                                              index <
                                              getSortedVerificationHistory(
                                                grandchild.verificationHistoryDetails
                                              ).length -
                                                1
                                            "
                                            class="verification-connector"
                                          ></div>
                                        </div>

                                        <!-- Attempt details -->
                                        <div
                                          class="verification-content flex-grow-1"
                                        >
                                          <div class="d-flex align-center mb-1">
                                            <span
                                              class="font-weight-medium text-subtitle-2"
                                            >
                                              {{
                                                formatDateTimeForDisplay(
                                                  attempt.timestamp
                                                )
                                              }}
                                            </span>
                                            <v-chip
                                              x-small
                                              :color="
                                                getVerificationStatusColor(
                                                  attempt.status
                                                )
                                              "
                                              class="ml-2"
                                            >
                                              {{
                                                formatVerificationStatus(
                                                  attempt.status
                                                )
                                              }}
                                            </v-chip>
                                          </div>

                                          <div
                                            v-if="attempt.reason"
                                            class="text-caption grey--text"
                                          >
                                            {{ attempt.reason }}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </v-card>
                                </v-menu>
                                <!-- For store level (grandchild is always store level) -->
                                <v-chip
                                  v-else
                                  small
                                  :color="
                                    getVerificationStatusColor(
                                      grandchild.verificationStatus || 'unknown'
                                    )
                                  "
                                  dark
                                  class="verification-status-chip"
                                  :class="
                                    grandchild.verificationStatus || 'unknown'
                                  "
                                >
                                  {{
                                    formatVerificationStatus(
                                      grandchild.verificationStatus || "unknown"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <!-- recent post -->
                              <td class="text-right metric-cell">
                                <div v-if="grandchild.recentPost">
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <div
                                        class="text-caption"
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        {{
                                          grandchild.recentPostDaysAgo
                                            ? `${
                                                grandchild.recentPostDaysAgo
                                              } day${
                                                grandchild.recentPostDaysAgo !==
                                                1
                                                  ? "s"
                                                  : ""
                                              } ago`
                                            : formatDateForDisplay(
                                                grandchild.recentPost
                                              )
                                        }}
                                      </div>
                                    </template>
                                    <span>{{
                                      formatDateForDisplay(
                                        grandchild.recentPost
                                      )
                                    }}</span>
                                  </v-tooltip>
                                </div>
                                <span v-else class="grey--text">No posts</span>
                              </td>
                              <td class="text-right metric-cell">
                                <v-menu
                                  bottom
                                  offset-y
                                  max-width="400"
                                  :close-on-content-click="false"
                                  :nudge-bottom="10"
                                  :z-index="1000"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                      small
                                      :color="
                                        grandchild.scheduledPosts > 0
                                          ? 'success'
                                          : 'grey'
                                      "
                                      dark
                                      v-bind="attrs"
                                      v-on="on"
                                      style="cursor: pointer"
                                    >
                                      {{ grandchild.scheduledPosts }}
                                    </v-chip>
                                  </template>
                                  <v-card
                                    v-if="
                                      grandchild.scheduledPostsDetails &&
                                      grandchild.scheduledPostsDetails.length >
                                        0
                                    "
                                    class="pa-2"
                                    color="white"
                                    elevation="8"
                                    max-width="400"
                                  >
                                    <div
                                      class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                    >
                                      <v-icon small color="primary" class="mr-1"
                                        >mdi-timeline-clock</v-icon
                                      >
                                      Scheduled Timeline ({{
                                        grandchild.scheduledPosts
                                      }})
                                    </div>

                                    <!-- Compact Timeline View -->
                                    <div
                                      class="timeline-container"
                                      style="
                                        max-height: 250px;
                                        overflow-y: auto;
                                      "
                                    >
                                      <div
                                        v-for="(
                                          dateGroup, index
                                        ) in getSortedScheduledPosts(
                                          grandchild.scheduledPostsDetails
                                        )"
                                        v-bind:key="index"
                                        class="timeline-item d-flex mb-2"
                                      >
                                        <!-- Timeline Line -->
                                        <div
                                          class="timeline-line d-flex flex-column align-center mr-3"
                                        >
                                          <v-icon small color="primary"
                                            >mdi-circle</v-icon
                                          >
                                          <div
                                            v-if="
                                              index <
                                              getSortedScheduledPosts(
                                                grandchild.scheduledPostsDetails
                                              ).length -
                                                1
                                            "
                                            class="timeline-connector"
                                          ></div>
                                        </div>

                                        <!-- Content -->
                                        <div
                                          class="timeline-content flex-grow-1"
                                        >
                                          <div class="d-flex align-center mb-1">
                                            <span
                                              class="font-weight-medium text-subtitle-2 primary--text"
                                              >{{ dateGroup.date }}</span
                                            >
                                            <v-chip
                                              x-small
                                              color="primary"
                                              class="ml-2"
                                              >{{ dateGroup.count }}</v-chip
                                            >
                                          </div>

                                          <!-- Posts for this date -->
                                          <div
                                            v-for="(
                                              post, postIndex
                                            ) in dateGroup.posts"
                                            v-bind:key="postIndex"
                                            class="post-item d-flex align-center mb-1"
                                          >
                                            <v-icon
                                              x-small
                                              color="grey"
                                              class="mr-1"
                                              >mdi-clock-outline</v-icon
                                            >
                                            <span class="text-caption mr-2">{{
                                              formatTime(post.scheduledTime)
                                            }}</span>

                                            <!-- Platform icons inline -->
                                            <div class="d-flex">
                                              <v-tooltip
                                                v-for="platform in post.platforms ||
                                                []"
                                                :key="platform"
                                                top
                                              >
                                                <template
                                                  v-slot:activator="{
                                                    on,
                                                    attrs,
                                                  }"
                                                >
                                                  <v-icon
                                                    x-small
                                                    :color="
                                                      getPlatformColor(platform)
                                                    "
                                                    class="mr-1"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                  >
                                                    {{
                                                      getPlatformIcon(platform)
                                                    }}
                                                  </v-icon>
                                                </template>
                                                <span>{{
                                                  getPlatformName(platform)
                                                }}</span>
                                              </v-tooltip>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </v-card>
                                  <v-card
                                    v-else
                                    class="pa-3"
                                    color="white"
                                    elevation="8"
                                  >
                                    <span class="grey--text"
                                      >No scheduled posts</span
                                    >
                                  </v-card>
                                </v-menu>
                              </td>
                              <td class="text-right metric-cell">
                                <div
                                  v-if="grandchild.scheduledUpToDate"
                                  class="text-caption"
                                >
                                  {{
                                    formatDateForDisplay(
                                      grandchild.scheduledUpToDate
                                    )
                                  }}
                                </div>
                                <span v-else class="grey--text"
                                  >No schedule</span
                                >
                              </td>
                              <td class="text-right metric-cell">
                                <div v-if="grandchild.profileStrength">
                                  <v-menu
                                    v-if="
                                      !isProfileComplete(
                                        grandchild.profileStrength,
                                        grandchild.level
                                      )
                                    "
                                    v-model="grandchild.showProfileMenu"
                                    :close-on-content-click="false"
                                    :nudge-width="380"
                                    bottom
                                    offset-y
                                    max-width="400"
                                    content-class="profile-menu-content"
                                  >
                                    <template v-slot:activator="{ on, attrs }">
                                      <div
                                        v-bind="attrs"
                                        v-on="on"
                                        style="cursor: pointer"
                                      >
                                        <div class="profile-strength-display">
                                          <v-chip
                                            small
                                            :color="
                                              getProfileStrengthColor(
                                                grandchild.profileStrength,
                                                grandchild.level
                                              )
                                            "
                                            dark
                                          >
                                            {{
                                              formatProfileStrengthDisplay(
                                                grandchild.profileStrength,
                                                grandchild.level
                                              )
                                            }}
                                          </v-chip>
                                        </div>
                                      </div>
                                    </template>

                                    <v-card
                                      v-if="grandchild.profileStrength"
                                      class="pa-3"
                                      color="white"
                                      elevation="8"
                                      max-width="420"
                                    >
                                      <div
                                        class="font-weight-bold mb-1 text-subtitle-2 primary--text d-flex align-center"
                                      >
                                        <v-icon
                                          small
                                          color="primary"
                                          class="mr-1"
                                          >mdi-account-check</v-icon
                                        >
                                        {{ grandchild.displayName }}
                                      </div>

                                      <div
                                        class="profile-details-container"
                                        style="
                                          max-height: 250px;
                                          overflow-y: auto;
                                        "
                                      >
                                        <div
                                          v-html="
                                            getProfileStrengthTooltip(
                                              grandchild.profileStrength,
                                              grandchild.level
                                            )
                                          "
                                        ></div>
                                      </div>
                                    </v-card>

                                    <v-card
                                      v-else
                                      class="pa-3"
                                      color="white"
                                      elevation="8"
                                    >
                                      <span class="grey--text"
                                        >No profile data</span
                                      >
                                    </v-card>
                                  </v-menu>

                                  <!-- Show chip directly if profile is 100% complete -->
                                  <div
                                    v-if="
                                      isProfileComplete(
                                        grandchild.profileStrength,
                                        grandchild.level
                                      )
                                    "
                                    class="profile-strength-display"
                                  >
                                    <v-chip
                                      small
                                      :color="
                                        getProfileStrengthColor(
                                          grandchild.profileStrength,
                                          grandchild.level
                                        )
                                      "
                                      dark
                                    >
                                      {{
                                        formatProfileStrengthDisplay(
                                          grandchild.profileStrength,
                                          grandchild.level
                                        )
                                      }}
                                    </v-chip>
                                  </div>
                                </div>
                                <span v-else class="grey--text">No data</span>
                              </td>
                              <!-- Profile Field Columns for Grandchild -->
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'description'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "description"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'websiteUri'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "websiteUri"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'primaryCategory'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "primaryCategory"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'phoneNumber'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "phoneNumber"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(grandchild, 'hasLogo')
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "hasLogo"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'hasCoverPhoto'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "hasCoverPhoto"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'hasPhotos'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "hasPhotos"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'regularHours'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "regularHours"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'additionalCategories'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "additionalCategories"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                <v-chip
                                  small
                                  :color="
                                    getProfileFieldColor(
                                      grandchild,
                                      'attributeCount'
                                    )
                                  "
                                  dark
                                >
                                  {{
                                    getProfileFieldDisplay(
                                      grandchild,
                                      "attributeCount"
                                    )
                                  }}
                                </v-chip>
                              </td>
                              <td class="text-right metric-cell">
                                {{ formatNumber(grandchild.posts || 0) }}
                              </td>
                              <td class="text-right metric-cell">
                                {{ formatNumber(grandchild.photos || 0) }}
                              </td>
                              <td class="text-right metric-cell">
                                <div v-if="grandchild.lastPhotoAdded">
                                  <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                      <div
                                        class="text-caption"
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        {{
                                          grandchild.lastPhotoAddedDaysAgo
                                            ? `${
                                                grandchild.lastPhotoAddedDaysAgo
                                              } day${
                                                grandchild.lastPhotoAddedDaysAgo !==
                                                1
                                                  ? "s"
                                                  : ""
                                              } ago`
                                            : formatDateForDisplay(
                                                grandchild.lastPhotoAdded
                                              )
                                        }}
                                      </div>
                                    </template>
                                    <span>{{
                                      formatDateForDisplay(
                                        grandchild.lastPhotoAdded
                                      )
                                    }}</span>
                                  </v-tooltip>
                                </div>
                                <span v-else class="grey--text">No photos</span>
                              </td>
                              <td class="text-center" style="min-width: 180px">
                                <div class="">
                                  <!-- Add Comment Button -->
                                  <v-btn
                                    x-small
                                    color="primary"
                                    @click="openAddCommentDialog(grandchild)"
                                  >
                                    Add
                                  </v-btn>

                                  <!-- Show Comments Button -->
                                  <v-menu
                                    offset-y
                                    max-width="400"
                                    :close-on-content-click="false"
                                  >
                                    <template v-slot:activator="{ on, attrs }">
                                      <v-btn
                                        x-small
                                        color="grey"
                                        outlined
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="fetchComments(grandchild, true)"
                                      >
                                        Show
                                      </v-btn>
                                    </template>
                                    <v-card>
                                      <v-card-title class="py-2">
                                        Comments
                                      </v-card-title>
                                      <v-divider></v-divider>
                                      <v-card-text
                                        style="
                                          max-height: 300px;
                                          overflow-y: auto;
                                        "
                                      >
                                        <div
                                          v-if="loadingComments[grandchild.id]"
                                          class="text-center py-3"
                                        >
                                          <v-progress-circular
                                            indeterminate
                                            size="24"
                                          ></v-progress-circular>
                                          <div class="caption mt-2">
                                            Loading comments...
                                          </div>
                                        </div>
                                        <div
                                          v-else-if="
                                            commentsCache[grandchild.id] &&
                                            commentsCache[grandchild.id].length
                                          "
                                        >
                                          <div
                                            v-for="(
                                              comment, idx
                                            ) in commentsCache[grandchild.id]"
                                            :key="idx"
                                            class="comment-item mb-2"
                                          >
                                            <div
                                              class="d-flex justify-space-between align-center mb-1"
                                            >
                                              <span
                                                class="comment-author font-weight-bold"
                                                >{{ comment.author }}</span
                                              >
                                              <span
                                                class="comment-date caption grey--text"
                                                >{{
                                                  formatDateTimeForDisplay(
                                                    comment.timestamp
                                                  )
                                                }}</span
                                              >
                                            </div>
                                            <div class="comment-text">
                                              {{ comment.text }}
                                            </div>
                                            <v-divider
                                              v-if="
                                                idx <
                                                commentsCache[grandchild.id]
                                                  .length -
                                                  1
                                              "
                                              class="mt-2"
                                            ></v-divider>
                                          </div>
                                        </div>
                                        <div
                                          v-else
                                          class="grey--text text-center py-3"
                                        >
                                          No comments yet
                                        </div>
                                      </v-card-text>
                                    </v-card>
                                  </v-menu>
                                </div>
                              </td>
                              <!-- Replace the current post validation cell (around line 959-1069) with this: -->
                              <td
                                v-if="true || grandchild.level === 3"
                                class="text-right metric-cell"
                              >
                                <v-menu
                                  v-if="
                                    grandchild.postValidationDetails &&
                                    grandchild.postValidationDetails.length > 1
                                  "
                                  bottom
                                  offset-y
                                  max-width="350"
                                  :close-on-content-click="false"
                                  :nudge-bottom="10"
                                  :z-index="1000"
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-chip
                                      small
                                      :color="
                                        grandchild.postValidation === 'Valid'
                                          ? 'success'
                                          : grandchild.postValidation ===
                                            'Invalid'
                                          ? 'error'
                                          : 'grey'
                                      "
                                      dark
                                      v-bind="attrs"
                                      v-on="on"
                                      style="cursor: pointer"
                                    >
                                      {{ grandchild.postValidation }}
                                      <span
                                        v-if="grandchild.posts !== undefined"
                                        class="ml-1"
                                        >({{ grandchild.posts }})</span
                                      >
                                    </v-chip>
                                  </template>
                                  <v-card
                                    class="pa-2"
                                    color="white"
                                    elevation="8"
                                    max-width="350"
                                  >
                                    <div
                                      class="font-weight-bold mb-2 text-subtitle-1 primary--text d-flex align-center"
                                    >
                                      <v-icon small color="primary" class="mr-1"
                                        >mdi-history</v-icon
                                      >
                                      Weekly Post Validation (last 5 weeks)
                                    </div>
                                    <div class="text-caption grey--text mb-2">
                                      <strong>Validation Rule:</strong> At least
                                      3 posts published per week per location.
                                    </div>
                                    <div
                                      style="
                                        max-height: 200px;
                                        overflow-y: auto;
                                      "
                                    >
                                      <table
                                        style="width: 100%; font-size: 13px"
                                      >
                                        <thead>
                                          <tr>
                                            <th style="text-align: left">
                                              Week
                                            </th>
                                            <th style="text-align: right">
                                              Posts
                                            </th>
                                            <th style="text-align: center">
                                              Status
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr
                                            v-for="week in grandchild.postValidationDetails"
                                            :key="week.week"
                                          >
                                            <td style="text-align: left">
                                              {{
                                                formatDateForDisplay(week.week)
                                              }}
                                            </td>
                                            <td style="text-align: right">
                                              {{ week.count }}
                                            </td>
                                            <td style="text-align: center">
                                              <v-chip
                                                x-small
                                                :color="
                                                  week.valid
                                                    ? 'success'
                                                    : 'error'
                                                "
                                                dark
                                              >
                                                {{
                                                  week.valid
                                                    ? "Valid"
                                                    : "Invalid"
                                                }}
                                              </v-chip>
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </v-card>
                                </v-menu>
                                <v-chip
                                  v-else
                                  small
                                  :color="
                                    grandchild.postValidation === 'Valid'
                                      ? 'success'
                                      : grandchild.postValidation === 'Invalid'
                                      ? 'error'
                                      : 'grey'
                                  "
                                  dark
                                >
                                  {{ grandchild.postValidation }}
                                  <span
                                    v-if="grandchild.posts !== undefined"
                                    class="ml-1"
                                    >({{ grandchild.posts }})</span
                                  >
                                </v-chip>
                              </td>
                            </tr>
                          </template>
                        </template>
                      </template>
                    </v-data-table>
                  </div>
                </div>

                <!-- No Data State -->
                <div v-else class="no-data-container">
                  <v-icon size="64" color="grey">mdi-post-outline</v-icon>
                  <p class="no-data-text">
                    {{
                      selectedUsers.length === 0
                        ? "Please select users to view their content data"
                        : "No content data available for selected users"
                    }}
                  </p>
                  <v-btn
                    color="primary"
                    @click="refreshCurrentTab"
                    v-if="selectedUsers.length > 0"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh Data
                  </v-btn>
                </div>
              </div>
            </v-tab-item>

            <!-- Performance Tab (formerly Stats Tab) -->
            <v-tab-item>
              <div class="tab-container">
                <!-- Loading State -->
                <div
                  v-if="loading && !hasStatsData && !hasExistingPerformanceData"
                  class="loading-container"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                  ></v-progress-circular>
                  <p class="loading-text">Loading Performance Data...</p>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="error-container">
                  <v-alert type="error" outlined>
                    {{ error }}
                  </v-alert>
                </div>

                <!-- Stats Data Display -->
                <div
                  v-else-if="hasStatsData || hasExistingPerformanceData"
                  class="data-container"
                >
                  <!-- Show inline loading indicator when fetching new users in background -->
                  <div
                    v-if="loadingBackgroundUsers"
                    class="background-loading-banner"
                  >
                    <v-alert type="info" dense outlined>
                      <v-row align="center" no-gutters>
                        <v-col cols="auto" class="mr-3">
                          <v-progress-circular
                            indeterminate
                            size="20"
                            width="2"
                            color="info"
                          ></v-progress-circular>
                        </v-col>
                        <v-col>
                          Fetching data for newly added users in background...
                        </v-col>
                      </v-row>
                    </v-alert>
                  </div>
                  <!-- Trend Chart -->
                  <div v-if="showTrendChart" class="trend-chart-container mb-6">
                    <div class="chart-header">
                      <h3 class="chart-title">GMB Stats Trends Over Time</h3>
                      <p class="chart-description">
                        Showing trends for selected date range
                      </p>
                    </div>
                    <div class="chart-wrapper">
                      <canvas
                        ref="statsTrendChart"
                        class="trend-chart"
                        width="800"
                        height="300"
                      ></canvas>
                    </div>
                  </div>

                  <div class="hierarchical-table-container table-fixed-height">
                    <!-- <h3 class="table-main-title">
                    GMB Stats - Hierarchical View
                  </h3>
                  <p class="table-description">
                    {{ selectedUsers.length }} users selected • Click rows to
                    expand: User → Admin Workspaces → Store Workspaces
                  </p> -->

                    <v-data-table
                      :headers="statsHierarchicalHeaders"
                      :items="statsHierarchicalData"
                      item-key="id"
                      dense
                      class="hierarchical-stats-table"
                      :items-per-page="-1"
                      hide-default-footer
                      :sort-by.sync="performanceSortBy"
                      :sort-desc.sync="performanceSortDesc"
                      :custom-sort="customSortPerformance"
                      must-sort
                      fixed-header
                      :height="600"
                    >
                      <template v-slot:item="{ item, expand, isExpanded }">
                        <tr :class="getRowClass(item)">
                          <td class="text-left">
                            <div
                              class="level-indicator"
                              :class="`level-${item.level}`"
                            >
                              <v-icon
                                v-if="item.hasChildren"
                                small
                                :class="{ 'rotate-90': isExpanded }"
                                class="expand-icon"
                                @click="expand(!isExpanded)"
                                style="cursor: pointer"
                              >
                                mdi-chevron-right
                              </v-icon>
                              <span v-else class="expand-placeholder"></span>
                              <div class="item-info">
                                <v-tooltip
                                  bottom
                                  v-if="
                                    item.displayName &&
                                    item.displayName.length > 15
                                  "
                                >
                                  <template v-slot:activator="{ on, attrs }">
                                    <strong
                                      :class="
                                        item.level === 1
                                          ? 'user-name'
                                          : item.level === 2
                                          ? 'admin-name'
                                          : 'workspace-name'
                                      "
                                      v-bind="attrs"
                                      v-on="on"
                                    >
                                      {{
                                        item.displayName.substring(0, 15) +
                                        "..."
                                      }}
                                    </strong>
                                  </template>
                                  <span>{{ item.displayName }}</span>
                                </v-tooltip>
                                <strong
                                  v-else
                                  :class="
                                    item.level === 1
                                      ? 'user-name'
                                      : item.level === 2
                                      ? 'admin-name'
                                      : 'workspace-name'
                                  "
                                >
                                  {{ item.displayName }}
                                </strong>
                                <div v-if="item.subtitle" class="item-subtitle">
                                  {{ item.subtitle }}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td
                            v-for="header in statsDynamicMetricHeaders"
                            :key="header.value"
                            class="text-right metric-cell position-relative"
                            @mouseenter="
                              showTooltip(item, header.value, $event)
                            "
                            @mouseleave="hideTooltip"
                          >
                            <template
                              v-if="
                                header.value === 'total_stores' &&
                                item.level === 3
                              "
                            >
                              -
                            </template>
                            <template v-else>
                              <div class="metric-value-container">
                                <span
                                  class="metric-value"
                                  :class="
                                    header.value === 'rank'
                                      ? getRankColorClass(item[header.value])
                                      : ''
                                  "
                                >
                                  {{
                                    formatStatsNumber(item[header.value] || 0)
                                  }}
                                </span>
                                <span
                                  v-if="
                                    item.percentageChanges &&
                                    item.percentageChanges[header.value]
                                  "
                                  class="percentage-change"
                                  :class="`change-${
                                    item.percentageChanges[header.value]
                                      .direction
                                  }`"
                                >
                                  {{
                                    item.percentageChanges[header.value].text
                                  }}
                                </span>
                              </div>
                            </template>
                          </td>
                        </tr>
                      </template>

                      <template v-slot:expanded-item="{ item }">
                        <template v-for="child in item.children">
                          <tr :key="child.id" :class="getRowClass(child)">
                            <td class="text-left">
                              <div
                                class="level-indicator"
                                :class="`level-${child.level}`"
                              >
                                <v-icon
                                  v-if="child.hasChildren"
                                  small
                                  @click="toggleChildExpand(child)"
                                  :class="{
                                    'rotate-90': child.expanded || false,
                                  }"
                                  class="expand-icon"
                                  style="cursor: pointer"
                                >
                                  mdi-chevron-right
                                </v-icon>
                                <span v-else class="expand-placeholder"></span>
                                <div class="item-info">
                                  <strong
                                    :class="
                                      child.level === 2
                                        ? 'admin-name'
                                        : 'workspace-name'
                                    "
                                  >
                                    <v-tooltip
                                      bottom
                                      v-if="
                                        child.displayName &&
                                        child.displayName.length > 15
                                      "
                                    >
                                      <template
                                        v-slot:activator="{ on, attrs }"
                                      >
                                        <span
                                          v-bind="attrs"
                                          v-on="on"
                                          @click="
                                            navigateToProfile(child.mapsUri)
                                          "
                                        >
                                          {{
                                            child.displayName.substring(0, 15) +
                                            "..."
                                          }}
                                        </span>
                                      </template>
                                      <span>{{ child.displayName }}</span>
                                    </v-tooltip>
                                    <span
                                      v-else
                                      @click="navigateToProfile(child.mapsUri)"
                                    >
                                      {{ child.displayName }}
                                    </span>
                                  </strong>
                                  <div
                                    v-if="child.subtitle"
                                    class="item-subtitle"
                                  >
                                    {{ child.subtitle }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td
                              v-for="header in statsDynamicMetricHeaders"
                              :key="header.value"
                              class="text-right metric-cell position-relative"
                              @mouseenter="
                                showTooltip(child, header.value, $event)
                              "
                              @mouseleave="hideTooltip"
                            >
                              <template
                                v-if="
                                  header.value === 'total_stores' &&
                                  child.level === 3
                                "
                              >
                                -
                              </template>
                              <template v-else>
                                <div class="metric-value-container">
                                  <span
                                    class="metric-value"
                                    :class="
                                      header.value === 'rank'
                                        ? getRankColorClass(child[header.value])
                                        : ''
                                    "
                                  >
                                    {{
                                      formatStatsNumber(
                                        child[header.value] || 0
                                      )
                                    }}
                                  </span>
                                  <span
                                    v-if="
                                      child.percentageChanges &&
                                      child.percentageChanges[header.value]
                                    "
                                    class="percentage-change"
                                    :class="`change-${
                                      child.percentageChanges[header.value]
                                        .direction
                                    }`"
                                  >
                                    {{
                                      child.percentageChanges[header.value].text
                                    }}
                                  </span>
                                </div>
                              </template>
                            </td>
                          </tr>

                          <!-- Third level (Store Workspaces under Admin) -->
                          <template
                            v-if="
                              child.expanded &&
                              child.children &&
                              child.children.length > 0
                            "
                          >
                            <tr
                              v-for="grandchild in child.children"
                              :key="grandchild.id"
                              :class="getRowClass(grandchild)"
                            >
                              <td class="text-left">
                                <div
                                  class="level-indicator"
                                  :class="`level-${grandchild.level}`"
                                >
                                  <span class="expand-placeholder"></span>
                                  <div class="item-info">
                                    <strong class="workspace-name">
                                      <v-tooltip
                                        bottom
                                        v-if="
                                          grandchild.displayName &&
                                          grandchild.displayName.length > 15
                                        "
                                      >
                                        <template
                                          v-slot:activator="{ on, attrs }"
                                        >
                                          <span
                                            @click="
                                              navigateToProfile(
                                                grandchild.mapsUri
                                              )
                                            "
                                            class="primary--text cursor-pointer"
                                            style="
                                              cursor: pointer;
                                              text-decoration: underline;
                                            "
                                            v-bind="attrs"
                                            v-on="on"
                                          >
                                            {{
                                              grandchild.displayName.substring(
                                                0,
                                                15
                                              ) + "..."
                                            }}
                                          </span>
                                        </template>
                                        <span>{{
                                          grandchild.displayName
                                        }}</span>
                                      </v-tooltip>
                                      <span
                                        v-else
                                        @click="
                                          navigateToProfile(grandchild.mapsUri)
                                        "
                                        class="primary--text cursor-pointer"
                                        style="
                                          cursor: pointer;
                                          text-decoration: underline;
                                        "
                                      >
                                        {{ grandchild.displayName }}
                                      </span>
                                    </strong>
                                    <div
                                      v-if="grandchild.subtitle"
                                      class="item-subtitle"
                                    >
                                      {{ grandchild.subtitle }}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td
                                v-for="header in statsDynamicMetricHeaders"
                                :key="header.value"
                                class="text-right metric-cell position-relative"
                                @mouseenter="
                                  showTooltip(grandchild, header.value, $event)
                                "
                                @mouseleave="hideTooltip"
                              >
                                <template
                                  v-if="
                                    header.value === 'total_stores' &&
                                    grandchild.level === 3
                                  "
                                >
                                  -
                                </template>
                                <template v-else>
                                  <div class="metric-value-container">
                                    <span
                                      class="metric-value"
                                      :class="
                                        header.value === 'rank'
                                          ? getRankColorClass(
                                              grandchild[header.value]
                                            )
                                          : ''
                                      "
                                    >
                                      {{
                                        formatStatsNumber(
                                          grandchild[header.value] || 0
                                        )
                                      }}
                                    </span>
                                    <span
                                      v-if="
                                        grandchild.percentageChanges &&
                                        grandchild.percentageChanges[
                                          header.value
                                        ]
                                      "
                                      class="percentage-change"
                                      :class="`change-${
                                        grandchild.percentageChanges[
                                          header.value
                                        ].direction
                                      }`"
                                    >
                                      {{
                                        grandchild.percentageChanges[
                                          header.value
                                        ].text
                                      }}
                                    </span>
                                  </div>
                                </template>
                              </td>
                            </tr>
                          </template>
                        </template>
                      </template>
                    </v-data-table>
                  </div>

                  <!-- Historical Data Tooltip -->
                  <div
                    v-if="showHistoricalTooltip"
                    class="historical-tooltip"
                    :style="tooltipStyle"
                  >
                    <div class="tooltip-header">
                      <strong>{{ tooltipMetricName }}</strong> - Historical Data
                    </div>
                    <div class="tooltip-content">
                      <div
                        v-for="(week, index) in tooltipData"
                        :key="week.date"
                        class="tooltip-week"
                        :class="{ 'current-week': index === 0 }"
                      >
                        <div class="week-info">
                          <span class="week-date">{{
                            week.formattedDate
                          }}</span>
                          <div class="week-metrics">
                            <span class="week-value">{{
                              formatStatsNumber(week.value || 0)
                            }}</span>
                            <span
                              v-if="week.changeText"
                              class="week-change"
                              :class="`change-${week.changeDirection}`"
                            >
                              {{ week.changeText }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- No Data State -->
                <div v-else class="no-data-container">
                  <v-icon size="64" color="grey">mdi-chart-line</v-icon>
                  <p class="no-data-text">
                    {{
                      selectedUsers.length === 0
                        ? "Please select users to view their stats"
                        : "No GMB stats data available for selected users"
                    }}
                  </p>
                  <v-btn
                    color="primary"
                    @click="refreshCurrentTab"
                    v-if="selectedUsers.length > 0"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh Data
                  </v-btn>
                </div>
              </div>
            </v-tab-item>

            <!-- Refreshes Tab -->
            <!-- <v-tab-item>
            <div class="tab-container">
              <div
                v-if="
                  refreshesLoading.users ||
                  refreshesLoading.workspaces ||
                  refreshesLoading.metrics ||
                  refreshesLoading.overall
                "
                class="loading-container"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="64"
                ></v-progress-circular>
                <p class="loading-text">
                  <span v-if="refreshesLoading.users"
                    >Loading user data...</span
                  >
                  <span v-else-if="refreshesLoading.workspaces"
                    >Loading workspaces...</span
                  >
                  <span v-else-if="refreshesLoading.metrics"
                    >Loading refresh metrics...</span
                  >
                  <span v-else-if="refreshesLoading.overall"
                    >Calculating statistics...</span
                  >
                </p>
                <v-progress-linear
                  v-if="refreshesLoading.metrics"
                  indeterminate
                  color="primary"
                  class="mt-2"
                ></v-progress-linear>
              </div>

              <div v-else-if="hasRefreshesData" class="data-container">
                <div class="hierarchical-table-container">
                  <v-data-table
                    :headers="refreshesHierarchicalHeaders"
                    :items="refreshesHierarchicalData"
                    item-key="id"
                    dense
                    class="hierarchical-stats-table"
                    :items-per-page="20"
                  >
                    <template v-slot:item="{ item, expand, isExpanded }">
                      <tr
                        :class="getRowClass(item)"
                      >
                        <td class="text-left">
                          <div
                            class="level-indicator"
                            :class="`level-${item.level}`"
                          >
                            <v-icon
                              v-if="item.hasChildren"
                              small
                              :class="{ 'rotate-90': isExpanded }"
                              class="expand-icon"
                              @click="expand(!isExpanded)"
                              style="cursor: pointer"
                            >
                              mdi-chevron-right
                            </v-icon>
                            <span v-else class="expand-placeholder"></span>
                            <div class="item-info">
                              <strong
                                :class="
                                  item.level === 1
                                    ? 'user-name'
                                    : item.level === 2
                                    ? 'admin-name'
                                    : 'workspace-name'
                                "
                              >
                                {{ item.displayName }}
                              </strong>
                              <div v-if="item.subtitle" class="item-subtitle">
                                {{ item.subtitle }}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.rankRefresh">
                            {{ formatDateForDisplay(item.rankRefresh) }}
                          </div>
                          <span v-else class="grey--text">Never</span>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.reviewInsights">
                            {{ formatDateForDisplay(item.reviewInsights) }}
                          </div>
                          <span v-else class="grey--text">Never</span>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.footfallAnalyticsDefault">
                            <v-tooltip bottom max-width="400">
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                  small
                                  :color="
                                    item.footfallAnalyticsDefault.isUpToDate
                                      ? 'success'
                                      : 'error'
                                  "
                                  dark
                                  v-bind="attrs"
                                  v-on="on"
                                  style="cursor: pointer"
                                >
                                  {{
                                    formatFootfallDate(
                                      item.footfallAnalyticsDefault.date
                                    )
                                  }}
                                </v-chip>
                              </template>
                              <div>
                                <div class="font-weight-bold">
                                  Footfall Analytics (Default)
                                </div>
                                <div class="text-caption">
                                  Date:
                                  {{
                                    formatFootfallDate(
                                      item.footfallAnalyticsDefault.date
                                    )
                                  }}
                                </div>
                                <div class="text-caption">
                                  Generated:
                                  {{
                                    formatDateFull(
                                      item.footfallAnalyticsDefault.generated_at
                                    )
                                  }}
                                </div>
                                <div class="text-caption">
                                  Status:
                                  {{
                                    item.footfallAnalyticsDefault.isUpToDate
                                      ? "Up to date"
                                      : "Needs refresh (Monday passed)"
                                  }}
                                </div>
                              </div>
                            </v-tooltip>
                          </div>
                          <span v-else class="grey--text">No data</span>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.keywordInsightsSummary">
                            <v-tooltip bottom max-width="400">
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                  small
                                  :color="
                                    item.keywordInsightsSummary.isUpToDate
                                      ? 'success'
                                      : 'warning'
                                  "
                                  dark
                                  v-bind="attrs"
                                  v-on="on"
                                  style="cursor: pointer"
                                >
                                  {{ item.keywordInsightsSummary.monthYear }}
                                </v-chip>
                              </template>
                              <div>
                                <div class="font-weight-bold">
                                  Keyword Insights Summary
                                </div>
                                <div class="text-caption">
                                  Month:
                                  {{ item.keywordInsightsSummary.monthYear }}
                                </div>
                                <div class="text-caption">
                                  Last Updated:
                                  {{
                                    formatDateForDisplay(
                                      item.keywordInsightsSummary.lastUpdated
                                    )
                                  }}
                                </div>
                                <div class="text-caption">
                                  Status:
                                  {{
                                    item.keywordInsightsSummary.isUpToDate
                                      ? "Up to date"
                                      : "Needs refresh (Monday passed)"
                                  }}
                                </div>
                              </div>
                            </v-tooltip>
                          </div>
                          <span v-else class="grey--text">No data</span>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.competitors">
                            {{ formatDateForDisplay(item.competitors) }}
                          </div>
                          <span v-else class="grey--text">Never</span>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.apifyFetch">
                            {{ formatDateForDisplay(item.apifyFetch) }}
                          </div>
                          <span v-else class="grey--text">Never</span>
                        </td>
                        <td class="text-right metric-cell">
                          <div v-if="item.footfallInsights">
                            {{ formatDateForDisplay(item.footfallInsights) }}
                          </div>
                          <span v-else class="grey--text">Never</span>
                        </td>
                      </tr>
                    </template>

                    <template v-slot:expanded-item="{ item }">
                      <template v-for="child in item.children">
                        <tr :key="child.id" :class="getRowClass(child)">
                          <td class="text-left">
                            <div
                              class="level-indicator"
                              :class="`level-${child.level}`"
                            >
                              <v-icon
                                v-if="child.hasChildren"
                                small
                                @click="toggleChildExpand(child)"
                                :class="{
                                  'rotate-90': child.expanded || false,
                                }"
                                class="expand-icon"
                                >mdi-chevron-right</v-icon
                              >
                              <span v-else class="expand-placeholder"></span>
                              <div class="item-info">
                                <strong
                                  :class="
                                    child.level === 2
                                      ? 'admin-name'
                                      : 'workspace-name'
                                  "
                                >
                                  <v-tooltip bottom v-if="child.displayName && child.displayName.length > 15">
                                    <template v-slot:activator="{ on, attrs }">
                                      <span
                                        v-bind="attrs"
                                        v-on="on"
                                      >
                                        {{ child.displayName.substring(0, 15) + '...' }}
                                      </span>
                                    </template>
                                    <span>{{ child.displayName }}</span>
                                  </v-tooltip>
                                  <span v-else>
                                    {{ child.displayName }}
                                  </span>
                                </strong>
                              </div>
                            </div>
                          </td>
                          <td class="text-right metric-cell">
                            {{
                              child.rankRefresh
                                ? formatDateForDisplay(child.rankRefresh)
                                : "Never"
                            }}
                          </td>
                          <td class="text-right metric-cell">
                            {{
                              child.reviewInsights
                                ? formatDateForDisplay(child.reviewInsights)
                                : "Never"
                            }}
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              v-if="child.footfallAnalyticsDefault"
                              small
                              :color="
                                child.footfallAnalyticsDefault.isUpToDate
                                  ? 'success'
                                  : 'error'
                              "
                              dark
                              >{{
                                formatFootfallDate(
                                  child.footfallAnalyticsDefault.date
                                )
                              }}</v-chip
                            >
                            <span v-else class="grey--text">No data</span>
                          </td>
                          <td class="text-right metric-cell">
                            <v-chip
                              v-if="child.keywordInsightsSummary"
                              small
                              :color="
                                child.keywordInsightsSummary.isUpToDate
                                  ? 'success'
                                  : 'warning'
                              "
                              dark
                              >{{
                                child.keywordInsightsSummary.monthYear
                              }}</v-chip
                            >
                            <span v-else class="grey--text">No data</span>
                          </td>
                          <td class="text-right metric-cell">
                            {{
                              child.competitors
                                ? formatDateForDisplay(child.competitors)
                                : "Never"
                            }}
                          </td>
                          <td class="text-right metric-cell">
                            {{
                              child.apifyFetch
                                ? formatDateForDisplay(child.apifyFetch)
                                : "Never"
                            }}
                          </td>
                          <td class="text-right metric-cell">
                            {{
                              child.footfallInsights
                                ? formatDateForDisplay(child.footfallInsights)
                                : "Never"
                            }}
                          </td>
                        </tr>

                        <template
                          v-if="
                            child.expanded &&
                            child.children &&
                            child.children.length > 0
                          "
                        >
                          <tr
                            v-for="grandchild in child.children"
                            :key="grandchild.id"
                            :class="getRowClass(grandchild)"
                          >
                            <td class="text-left">
                              <div
                                class="level-indicator"
                                :class="`level-${grandchild.level}`"
                              >
                                <span class="expand-placeholder"></span>
                                <div class="item-info">
                                  <strong class="workspace-name">
                                    <span
                                      @click="navigateToProfile(grandchild.mapsUri)"
                                      class="primary--text cursor-pointer"
                                      style="cursor: pointer; text-decoration: underline;"
                                    >
                                      {{ grandchild.displayName }}
                                    </span>
                                  </strong>
                                  <div
                                    v-if="grandchild.subtitle"
                                    class="item-subtitle"
                                  >
                                    {{ grandchild.subtitle }}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td class="text-right metric-cell">
                              {{
                                grandchild.rankRefresh
                                  ? formatDateForDisplay(grandchild.rankRefresh)
                                  : "Never"
                              }}
                            </td>
                            <td class="text-right metric-cell">
                              {{
                                grandchild.reviewInsights
                                  ? formatDateForDisplay(
                                      grandchild.reviewInsights
                                    )
                                  : "Never"
                              }}
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                v-if="grandchild.footfallAnalyticsDefault"
                                small
                                :color="
                                  grandchild.footfallAnalyticsDefault.isUpToDate
                                    ? 'success'
                                    : 'error'
                                "
                                dark
                                >{{
                                  formatFootfallDate(
                                    grandchild.footfallAnalyticsDefault.date
                                  )
                                }}</v-chip
                              >
                              <span v-else class="grey--text">No data</span>
                            </td>
                            <td class="text-right metric-cell">
                              <v-chip
                                v-if="grandchild.keywordInsightsSummary"
                                small
                                :color="
                                  grandchild.keywordInsightsSummary.isUpToDate
                                    ? 'success'
                                    : 'warning'
                                "
                                dark
                                >{{
                                  grandchild.keywordInsightsSummary.monthYear
                                }}</v-chip
                              >
                              <span v-else class="grey--text">No data</span>
                            </td>
                            <td class="text-right metric-cell">
                              {{
                                grandchild.competitors
                                  ? formatDateForDisplay(grandchild.competitors)
                                  : "Never"
                              }}
                            </td>
                            <td class="text-right metric-cell">
                              {{
                                grandchild.apifyFetch
                                  ? formatDateForDisplay(grandchild.apifyFetch)
                                  : "Never"
                              }}
                            </td>
                            <td class="text-right metric-cell">
                              {{
                                grandchild.footfallInsights
                                  ? formatDateForDisplay(
                                      grandchild.footfallInsights
                                    )
                                  : "Never"
                              }}
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </v-data-table>
                </div>
              </div>

              <div v-else class="no-data-container">
                <v-icon size="64" color="grey">mdi-refresh</v-icon>
                <p class="no-data-text">
                  {{
                    selectedUsers.length === 0
                      ? "Please select users to view their refresh data"
                      : "No refresh data available for selected users"
                  }}
                </p>
              </div>
            </div>
          </v-tab-item> -->

            <!-- Historical Trends Tab -->
            <v-tab-item>
              <div class="tab-container">
                <HistoricalMetricsDashboard
                  :selected-users="selectedUsers"
                  :selected-date="selectedDate"
                  :user-posts-data="userPostsData"
                  :key="`historical-${selectedDate}-${selectedUsers.join('-')}`"
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </template>
    </ops-base>

    <!-- Add Comment Dialog - Outside ops-base -->
    <v-dialog
      v-model="commentDialog"
      max-width="500px"
      persistent
      :attach="false"
    >
      <v-card>
        <v-card-title class="headline">
          Add Comment
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCommentDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="commentText"
            label="Enter your comment"
            rows="4"
            outlined
            auto-grow
            hide-details="auto"
            placeholder="Type your comment here..."
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeCommentDialog"> Cancel </v-btn>
          <v-btn
            color="primary"
            :disabled="!commentText.trim()"
            @click="submitComment"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import OpsBase from "./OpsBase.vue";
import HistoricalMetricsDashboard from "@/components/dashboards/HistoricalMetricsDashboard.vue";
import { db } from "@/utils/firebase.utils";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  startAfter,
  doc,
  getDoc,
  addDoc,
} from "firebase/firestore";
// import Chart from "chart.js/auto"; // TODO: Uncomment when implementing charts

export default {
  name: "OpsDashboard",
  components: {
    OpsBase,
    HistoricalMetricsDashboard,
  },
  data() {
    return {
      currentTab: this.getTabIndex(this.$route.query.tab) || 0,
      allUsers: [],
      selectedUsers: [],
      pendingSelectedUsers: [], // Users selected but not yet applied
      searchText: "", // Track search input text
      loadingBackgroundUsers: false, // Loading state for background fetch
      newUsersData: {}, // Temporary storage for newly fetched user data
      contentTabData: {}, // Cache for content tab data by date
      performanceTabData: {}, // Cache for performance tab data by date
      showUserDropdown: false, // Control dropdown visibility
      filteredUsersList: [], // Filtered users based on search
      dropdownRightAligned: false, // Control dropdown alignment
      loadingApplyButton: false, // Loading state for apply button specifically
      // Sorting states for each tab and level
      // Content tab sorting
      contentSortBy: "displayName",
      contentSortDesc: false,
      // Performance tab sorting
      performanceSortBy: "displayName",
      performanceSortDesc: false,

      // Date Selection for Timeline
      availableDates: [],
      selectedDateIndex: 0, // Index of selected date (0 = latest)
      showTrendChart: false,

      // Timeline Data (all dates)
      timelineData: {},

      // Unified Data Cache - stores all fetched data by user and date
      unifiedDataCache: new Map(), // Key: `${uid}_${date}`, Value: user data object

      // Tab-specific processed data (derived from unified cache)
      userStatsData: [],
      overallStats: null,
      userRankData: [],
      overallRankStats: null,
      userPostsData: [],
      overallPostsStats: null,
      userReviewsData: [],
      overallReviewsStats: null,
      userRefreshesData: [],
      overallRefreshesStats: null,

      // Track which tabs have been processed for current user/date selection
      processedTabs: {
        content: false,
        performance: false,
        refreshes: false,
      },

      expandedItems: {},

      // Chart instances
      trendCharts: {},

      // Common States
      loading: false,
      loadingUsers: false,
      error: null,
      batchSize: 10,

      // Refreshes-specific loading states
      refreshesLoading: {
        users: false,
        workspaces: false,
        metrics: false,
        overall: false,
      },

      // Cache for refresh data (15 minutes TTL for better performance)
      refreshDataCache: new Map(),
      cacheTimeout: 15 * 60 * 1000, // 15 minutes

      // Rate limiting for Firestore
      rateLimitDelay: 30, // Reduced delay for better performance
      maxConcurrentQueries: 8, // Increased concurrent queries

      // Tooltip States
      showHistoricalTooltip: false,
      tooltipStyle: {},
      tooltipData: [],
      tooltipMetric: "",
      tooltipMetricName: "",

      // Reactivity key to force updates
      dataUpdateKey: 0,
      newComments: {}, // { [itemId]: commentText }
      commentsCache: {}, // { [itemId]: [ {text, author, timestamp} ] }
      loadingComments: {}, // { [itemId]: boolean }
      commentDialog: false,
      selectedItemForComment: null,
      commentText: "",
    };
  },
  computed: {
    contentSortOptions() {
      // Return sorting options based on available columns
      return [
        { text: "Name", value: "displayName" },
        { text: "Verification Status", value: "verificationHistory" },
        { text: "Recent Post", value: "recentPost" },
        { text: "Scheduled Posts", value: "scheduledPosts" },
        { text: "Profile Strength", value: "profileStrength" },
        { text: "Posts Count", value: "posts" },
        { text: "Photos Count", value: "photos" },
        { text: "Last Photo Added", value: "lastPhotoAdded" },
        { text: "Post Validation", value: "postValidation" },
      ];
    },

    performanceSortOptions() {
      // Return sorting options for performance metrics
      const baseOptions = [{ text: "Name", value: "displayName" }];

      // Add dynamic metric options
      const metricOptions = this.statsDynamicMetricHeaders.map((header) => ({
        text: header.text,
        value: header.value,
      }));

      return [...baseOptions, ...metricOptions];
    },

    totalStoreCount() {
      if (!this.userPostsData || this.userPostsData.length === 0) {
        return 0;
      }

      let totalStores = 0;
      this.userPostsData.forEach((user) => {
        if (user.adminWorkspaces) {
          user.adminWorkspaces.forEach((admin) => {
            if (admin.storeWorkspaces) {
              totalStores += admin.storeWorkspaces.length;
            }
          });
        } else if (user.children) {
          // Handle direct store promotion case
          totalStores += user.children.length;
        }
      });

      return totalStores;
    },

    hasDataForCurrentTab() {
      const tabName = this.getTabName(this.currentTab);
      if (tabName === "content") {
        return this.hasPostsData;
      } else if (tabName === "performance") {
        return this.hasStatsData;
      } else if (tabName === "refreshes") {
        return this.hasRefreshesData;
      } else if (tabName === "historical") {
        // Historical trends component manages its own data state
        return this.selectedUsers.length > 0;
      }
      return false;
    },

    selectedDate() {
      if (this.availableDates.length > 0 && this.selectedDateIndex !== null) {
        return this.availableDates[this.selectedDateIndex];
      }
      return null;
    },

    hasStatsData() {
      const hasData = this.overallStats || this.userStatsData.length > 0;
      // console.log(
      //   "hasStatsData computed:",
      //   hasData,
      //   "overallStats:",
      //   this.overallStats,
      //   "userStatsData length:",
      //   this.userStatsData.length
      // );
      return hasData;
    },

    hasRankData() {
      const hasData = this.overallRankStats || this.userRankData.length > 0;
      // console.log(
      //   "hasRankData computed:",
      //   hasData,
      //   "overallRankStats:",
      //   this.overallRankStats,
      //   "userRankData length:",
      //   this.userRankData.length
      // );
      return hasData;
    },

    hasPostsData() {
      return this.overallPostsStats || this.userPostsData.length > 0;
    },

    hasReviewsData() {
      return this.overallReviewsStats || this.userReviewsData.length > 0;
    },

    hasRefreshesData() {
      return this.overallRefreshesStats || this.userRefreshesData.length > 0;
    },

    // Check if user selection has changed
    hasUserSelectionChanged() {
      if (this.pendingSelectedUsers.length !== this.selectedUsers.length) {
        return true;
      }
      return !this.pendingSelectedUsers.every((uid) =>
        this.selectedUsers.includes(uid)
      );
    },

    // Check if we have existing content data
    hasExistingContentData() {
      return this.userPostsData && this.userPostsData.length > 0;
    },

    // Check if we have existing performance data
    hasExistingPerformanceData() {
      return this.userStatsData && this.userStatsData.length > 0;
    },

    // Dynamic label for user selection
    userSelectionLabel() {
      if (this.pendingSelectedUsers.length === 0) {
        return "Search and Select Users";
      }
      const count = this.pendingSelectedUsers.length;
      return `${count} user${count === 1 ? "" : "s"} selected`;
    },

    // Check if all users are selected
    allUsersSelected() {
      return (
        this.filteredUsersList.length > 0 &&
        this.filteredUsersList.every((user) =>
          this.pendingSelectedUsers.includes(user.uid)
        )
      );
    },

    // Refreshes Computed Properties
    refreshesHierarchicalHeaders() {
      return [
        {
          text: "Name",
          value: "displayName",
          sortable: false,
          width: "250px",
          align: "start",
        },
        {
          text: "Rank Refresh",
          value: "rankRefresh",
          sortable: false,
          align: "end",
          width: "130px",
          tooltip:
            "Rank Refresh - Date when local search rankings were last updated for keyword tracking",
        },
        {
          text: "Review Insights",
          value: "reviewInsights",
          sortable: false,
          align: "end",
          width: "130px",
          tooltip:
            "Review Insights - Date when customer review analytics data was last refreshed",
        },
        {
          text: "Footfall Analytics",
          value: "footfallAnalyticsDefault",
          sortable: false,
          align: "end",
          width: "150px",
          tooltip:
            "Footfall Analytics - Date when visitor traffic and foot traffic analytics were last updated",
        },
        {
          text: "Keyword Insights",
          value: "keywordInsightsSummary",
          sortable: false,
          align: "end",
          width: "150px",
          tooltip:
            "Keyword Insights - Date when search keyword performance and insights data was last refreshed",
        },
        {
          text: "Competitors",
          value: "competitors",
          sortable: false,
          align: "end",
          width: "130px",
          tooltip:
            "Competitors - Date when competitor analysis and comparison data was last updated",
        },
        {
          text: "Apify Fetch",
          value: "apifyFetch",
          sortable: false,
          align: "end",
          width: "130px",
          tooltip:
            "Apify Fetch - Date when data scraping and automated data collection was last performed",
        },
        {
          text: "Footfall Insights",
          value: "footfallInsights",
          sortable: false,
          align: "end",
          width: "130px",
          tooltip:
            "Footfall Insights - Date when detailed visitor behavior and traffic pattern insights were last generated",
        },
      ];
    },

    refreshesHierarchicalData() {
      return this.userRefreshesData.map((user) => {
        const userAdminWorkspaces = user.adminWorkspaces || [];
        const userAggregatedMetrics = user.aggregatedMetrics || {};

        const totalStoreWorkspaces = userAdminWorkspaces.reduce(
          (total, adminWs) => {
            return total + (adminWs.storeWorkspaces || []).length;
          },
          0
        );

        // If there's only one admin workspace, skip the admin level and show admin data at user level
        if (userAdminWorkspaces.length === 1) {
          const singleAdmin = userAdminWorkspaces[0];
          const storeWorkspaces = singleAdmin.storeWorkspaces || [];
          const adminAggregatedMetrics = singleAdmin.aggregatedMetrics || {};

          // Debug: Log store workspace data to see available fields
          if (storeWorkspaces.length > 0) {
            // console.log(
            //   "RefreshesTab - Store workspace sample data:",
            //   storeWorkspaces[0]
            // );
            // console.log(
            //   "RefreshesTab - Available fields:",
            //   Object.keys(storeWorkspaces[0])
            // );
            // console.log(
            //   "RefreshesTab - short_address value:",
            //   storeWorkspaces[0].short_address
            // );
            // console.log(
            //   "RefreshesTab - title value:",
            //   storeWorkspaces[0].title
            // );
          }

          // Promote store workspaces to level 2 (directly under user)
          const promotedStoreEntries = storeWorkspaces.map((storeWs) => ({
            id:
              storeWs.id ||
              `store_${user.uid}_${singleAdmin.wid}_${storeWs.wid}`,
            wid: storeWs.wid,
            mapsUri: storeWs.mapsUri || null,
            level: 2,
            displayName:
              storeWs.short_address || storeWs.title || `Store ${storeWs.wid}`,
            subtitle: storeWs.connected_at ? `Connected: ${this.formatDateForDisplay(storeWs.connected_at)}` : null,
            hasChildren: false,
            expanded: false,
            childCount: "Store",
            rankRefresh: storeWs.rankRefresh || null,
            reviewInsights: storeWs.reviewInsights || null,
            footfallAnalyticsDefault: storeWs.footfallAnalyticsDefault || null,
            keywordInsightsSummary: storeWs.keywordInsightsSummary || null,
            competitors: storeWs.competitors || null,
            apifyFetch: storeWs.apifyFetch || null,
            footfallInsights: storeWs.footfallInsights || null,
          }));

          return {
            id: `user_${user.uid}`,
            level: 1,
            displayName: user.display_name || user.email || "Unknown User",
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            // Use admin's aggregated metrics instead of user's
            rankRefresh: adminAggregatedMetrics.rankRefresh || null,
            reviewInsights: adminAggregatedMetrics.reviewInsights || null,
            footfallAnalyticsDefault:
              adminAggregatedMetrics.footfallAnalyticsDefault || null,
            keywordInsightsSummary:
              adminAggregatedMetrics.keywordInsightsSummary || null,
            competitors: adminAggregatedMetrics.competitors || null,
            apifyFetch: adminAggregatedMetrics.apifyFetch || null,
            footfallInsights: adminAggregatedMetrics.footfallInsights || null,
            children: promotedStoreEntries,
          };
        }

        // Multiple admin workspaces - keep the original 3-level hierarchy
        const adminEntries = userAdminWorkspaces.map((adminWs) => {
          const storeWorkspaces = adminWs.storeWorkspaces || [];
          const adminAggregatedMetrics = adminWs.aggregatedMetrics || {};

          return {
            id: adminWs.id || `admin_${user.uid}_${adminWs.wid}`,
            level: 2,
            displayName: adminWs.title || `Admin Workspace ${adminWs.wid}`,
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            rankRefresh: adminAggregatedMetrics.rankRefresh || null,
            reviewInsights: adminAggregatedMetrics.reviewInsights || null,
            footfallAnalyticsDefault:
              adminAggregatedMetrics.footfallAnalyticsDefault || null,
            keywordInsightsSummary:
              adminAggregatedMetrics.keywordInsightsSummary || null,
            competitors: adminAggregatedMetrics.competitors || null,
            apifyFetch: adminAggregatedMetrics.apifyFetch || null,
            footfallInsights: adminAggregatedMetrics.footfallInsights || null,
            children: storeWorkspaces.map((storeWs) => ({
              id:
                storeWs.id || `store_${user.uid}_${adminWs.wid}_${storeWs.wid}`,
              wid: storeWs.wid,
              mapsUri: storeWs.mapsUri || null,
              level: 3,
              displayName:
                storeWs.short_address ||
                storeWs.title ||
                `Store ${storeWs.wid}`,
              hasChildren: false,
              expanded: false,
              childCount: "Store",
              rankRefresh: storeWs.rankRefresh || null,
              reviewInsights: storeWs.reviewInsights || null,
              footfallAnalyticsDefault:
                storeWs.footfallAnalyticsDefault || null,
              keywordInsightsSummary: storeWs.keywordInsightsSummary || null,
              competitors: storeWs.competitors || null,
              apifyFetch: storeWs.apifyFetch || null,
              footfallInsights: storeWs.footfallInsights || null,
            })),
          };
        });

        return {
          id: `user_${user.uid}`,
          level: 1,
          displayName: user.display_name || user.email || "Unknown User",
          subtitle: `${userAdminWorkspaces.length} admin workspace${
            userAdminWorkspaces.length !== 1 ? "s" : ""
          }, ${totalStoreWorkspaces} store${
            totalStoreWorkspaces !== 1 ? "s" : ""
          }`,
          hasChildren: adminEntries.length > 0,
          expanded: false,
          childCount: `${userAdminWorkspaces.length} admins`,
          rankRefresh: userAggregatedMetrics.rankRefresh || null,
          reviewInsights: userAggregatedMetrics.reviewInsights || null,
          footfallAnalyticsDefault:
            userAggregatedMetrics.footfallAnalyticsDefault || null,
          keywordInsightsSummary:
            userAggregatedMetrics.keywordInsightsSummary || null,
          competitors: userAggregatedMetrics.competitors || null,
          apifyFetch: userAggregatedMetrics.apifyFetch || null,
          footfallInsights: userAggregatedMetrics.footfallInsights || null,
          children: adminEntries,
        };
      });
    },

    // Posts Computed Properties
    postsHierarchicalHeaders() {
      return [
        {
          text: "Name",
          value: "displayName",
          sortable: true,
          width: "250px",
          align: "start",
        },
        {
          text: "Verification History",
          value: "verificationHistory",
          sortable: true,
          align: "end",
          width: "140px",
          tooltip:
            "Verification History - Timeline showing the last 12 verification attempts",
        },
        {
          text: "Recent Post",
          value: "recentPost",
          sortable: true,
          align: "end",
          width: "130px",
          tooltip:
            "Recent Post - Date of the most recently published Google My Business post",
        },
        {
          text: "Scheduled Posts",
          value: "scheduledPosts",
          sortable: true,
          align: "end",
          width: "130px",
          tooltip:
            "Scheduled Posts - Number of GMB posts that are scheduled to be published in the future",
        },
        {
          text: "Scheduled Up To",
          value: "scheduledUpToDate",
          sortable: true,
          align: "end",
          width: "130px",
          tooltip:
            "Scheduled Up To - Latest date until which GMB posts are scheduled",
        },
        {
          text: "Profile Strength",
          value: "profileStrength",
          sortable: true,
          align: "end",
          width: "150px",
          tooltip:
            "Profile Strength - Completion percentage of GMB profile fields (description, hours, photos, etc.)",
        },
        {
          text: "Description",
          value: "profileFields.description",
          sortable: true,
          align: "end",
          width: "80px",
          tooltip:
            "Description - Whether the GMB profile has a business description",
        },
        {
          text: "Website",
          value: "profileFields.websiteUri",
          sortable: true,
          align: "end",
          width: "70px",
          tooltip:
            "Website - Whether a website URL is configured in the GMB profile",
        },
        {
          text: "Category",
          value: "profileFields.primaryCategory",
          sortable: true,
          align: "end",
          width: "70px",
          tooltip:
            "Category - Whether the primary business category is set in GMB",
        },
        {
          text: "Phone",
          value: "profileFields.phoneNumber",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip:
            "Phone - Whether a phone number is configured in the GMB profile",
        },
        {
          text: "Logo",
          value: "profileFields.hasLogo",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip:
            "Logo - Whether a business logo is uploaded to the GMB profile",
        },
        {
          text: "Cover",
          value: "profileFields.hasCoverPhoto",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip: "Cover - Whether a cover photo is set for the GMB profile",
        },
        {
          text: "Photos",
          value: "profileFields.hasPhotos",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip:
            "Photos - Whether any photos are uploaded to the GMB listing",
        },
        {
          text: "Hours",
          value: "profileFields.regularHours",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip: "Hours - Whether business hours are configured in GMB",
        },
        {
          text: "Add Cat",
          value: "profileFields.additionalCategories",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip:
            "Additional Categories - Number of extra business categories added to the GMB profile beyond the primary category",
        },
        {
          text: "Attrs",
          value: "profileFields.attributeCount",
          sortable: true,
          align: "end",
          width: "60px",
          tooltip:
            "Attributes - Count of business attributes configured in the GMB profile",
        },
        {
          text: "Posts",
          value: "posts",
          sortable: true,
          align: "end",
          width: "130px",
          tooltip:
            "Posts - Total number of Google My Business posts published weekly",
        },
        {
          text: "Photos",
          value: "photos",
          sortable: true,
          align: "end",
          width: "100px",
          tooltip:
            "Photos - Total count of photos uploaded to the GMB listing weekly",
        },
        {
          text: "Last Photo Added",
          value: "lastPhotoAdded",
          sortable: true,
          align: "end",
          width: "150px",
          tooltip:
            "Last Photo Added - Date when the most recent photo was uploaded to the GMB listing",
        },
        {
          text: "Comments",
          value: "comments",
          sortable: true,
          align: "center",
          width: "180px",
          tooltip:
            "Comments - Add internal comments or view existing comments for this user/admin/store level",
        },
        {
          text: "Post Validation",
          value: "postValidation",
          sortable: true,
          align: "end",
          width: "150px",
          tooltip:
            "Post Validation - Validates if stores have published atleast 3 posts per week per location.",
        },
      ];
    },
    postsHierarchicalData() {
      // Force reactivity with dataUpdateKey
      this.dataUpdateKey;

      // Return data without internal sorting - let the table handle it
      return this.userPostsData.map((user) => {
        const userAdminWorkspaces = user.adminWorkspaces || [];
        const userAggregatedMetrics = user.aggregatedMetrics || {};

        // If there's only one admin workspace, skip the admin level and show admin data at user level
        if (userAdminWorkspaces.length === 1) {
          const singleAdmin = userAdminWorkspaces[0];
          const storeWorkspaces = singleAdmin.storeWorkspaces || [];

          // Promote store workspaces to level 2 (directly under user)
          const promotedStoreEntries = storeWorkspaces.map((storeWs) => ({
            id:
              storeWs.id ||
              `store_${user.uid}_${singleAdmin.wid}_${storeWs.wid}`,
            wid: storeWs.wid,
            mapsUri: storeWs.mapsUri || null,
            level: 2,
            displayName:
              storeWs.short_address || storeWs.title || `Store ${storeWs.wid}`,
            subtitle: storeWs.connected_at ? `Connected: ${this.formatDateForDisplay(storeWs.connected_at)}` : null,
            hasChildren: false,
            expanded: false,
            childCount: "Store",
            posts: storeWs.posts || 0,
            recentPost: storeWs.recentPost || null,
            recentPostDaysAgo: storeWs.recentPostDaysAgo || null,
            scheduledPosts: storeWs.scheduledPosts || 0,
            scheduledPostsDetails: storeWs.scheduledPostsDetails || [],
            scheduledUpToDate: storeWs.scheduledUpToDate || null,
            profileStrength: storeWs.profile_strength || null,
            showProfileMenu: false,
            photos: storeWs.photos || 0,
            lastPhotoAdded: storeWs.lastPhotoAdded || null,
            lastPhotoAddedDaysAgo: storeWs.lastPhotoAddedDaysAgo || null,
            verificationStatus: storeWs.verificationStatus || "unknown",
            lastVerified: storeWs.lastVerified || null,
            lastVerifiedDaysAgo: storeWs.lastVerifiedDaysAgo || null,
            verificationHistoryCount: storeWs.verificationHistoryCount || 0,
            verificationHistoryDetails:
              storeWs.verificationHistoryDetails || [],
            verificationReason: storeWs.verificationReason || null,
            lastVerificationAttempt: storeWs.lastVerificationAttempt || null,
            // Post Validation
            postValidation: storeWs.postValidation || "-",
            postValidationDetails: storeWs.postValidationDetails || [],
          }));

          return {
            id: `user_${user.uid}`,
            level: 1,
            displayName: user.display_name || user.email || "Unknown User",
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            posts: userAggregatedMetrics.posts || 0,
            recentPost: userAggregatedMetrics.recentPost || null,
            recentPostDaysAgo: userAggregatedMetrics.recentPostDaysAgo || null,
            scheduledPosts: userAggregatedMetrics.scheduledPosts || 0,
            scheduledPostsDetails:
              userAggregatedMetrics.scheduledPostsDetails || [],
            scheduledUpToDate: userAggregatedMetrics.scheduledUpToDate || null,
            profileStrength: userAggregatedMetrics.profile_strength || null,
            showProfileMenu: false,
            photos: userAggregatedMetrics.photos || 0,
            lastPhotoAdded: userAggregatedMetrics.lastPhotoAdded || null,
            lastPhotoAddedDaysAgo:
              userAggregatedMetrics.lastPhotoAddedDaysAgo || null,
            verificationStatus: user.verificationStatus || "unknown",
            lastVerified: user.lastVerified || null,
            lastVerifiedDaysAgo: user.lastVerifiedDaysAgo || null,
            verificationHistoryCount: user.verificationHistoryCount || 0,
            verificationHistoryDetails: user.verificationHistoryDetails || [],
            verificationReason: user.verificationReason || null,
            lastVerificationAttempt: user.lastVerificationAttempt || null,
            // Post Validation
            postValidation: "-", // Dash at user level
            postValidationDetails: [],
            children: promotedStoreEntries,
          };
        }

        // Multiple admin workspaces - keep the original 3-level hierarchy
        const adminEntries = userAdminWorkspaces.map((adminWs) => {
          const storeWorkspaces = adminWs.storeWorkspaces || [];
          const adminAggregatedMetrics = adminWs.aggregatedMetrics || {};

          return {
            id: adminWs.id || `admin_${user.uid}_${adminWs.wid}`,
            level: 2,
            displayName: adminWs.title || `Admin Workspace ${adminWs.wid}`,
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            posts: adminAggregatedMetrics.posts || 0,
            recentPost: adminAggregatedMetrics.recentPost || null,
            recentPostDaysAgo: adminAggregatedMetrics.recentPostDaysAgo || null,
            scheduledPosts: adminAggregatedMetrics.scheduledPosts || 0,
            scheduledPostsDetails:
              adminAggregatedMetrics.scheduledPostsDetails || [],
            scheduledUpToDate: adminAggregatedMetrics.scheduledUpToDate || null,
            profileStrength: adminAggregatedMetrics.profile_strength || null,
            showProfileMenu: false,
            photos: adminAggregatedMetrics.photos || 0,
            lastPhotoAdded: adminAggregatedMetrics.lastPhotoAdded || null,
            lastPhotoAddedDaysAgo:
              adminAggregatedMetrics.lastPhotoAddedDaysAgo || null,
            verificationStatus: adminWs.verificationStatus || "unknown",
            lastVerified: adminWs.lastVerified || null,
            lastVerifiedDaysAgo: adminWs.lastVerifiedDaysAgo || null,
            verificationHistoryCount: adminWs.verificationHistoryCount || 0,
            verificationHistoryDetails:
              adminWs.verificationHistoryDetails || [],
            verificationReason: adminWs.verificationReason || null,
            lastVerificationAttempt: adminWs.lastVerificationAttempt || null,
            // Post Validation
            postValidation: "-", // Dash at admin level
            postValidationDetails: [],
            children: storeWorkspaces.map((storeWs) => ({
              id:
                storeWs.id || `store_${user.uid}_${adminWs.wid}_${storeWs.wid}`,
              wid: storeWs.wid,
              mapsUri: storeWs.mapsUri || null,
              level: 3,
              displayName:
                storeWs.short_address ||
                storeWs.title ||
                `Store ${storeWs.wid}`,
              hasChildren: false,
              expanded: false,
              childCount: "Store",
              posts: storeWs.posts || 0,
              recentPost: storeWs.recentPost || null,
              recentPostDaysAgo: storeWs.recentPostDaysAgo || null,
              scheduledPosts: storeWs.scheduledPosts || 0,
              scheduledPostsDetails: storeWs.scheduledPostsDetails || [],
              scheduledUpToDate: storeWs.scheduledUpToDate || null,
              profileStrength: storeWs.profile_strength || null,
              showProfileMenu: false,
              photos: storeWs.photos || 0,
              lastPhotoAdded: storeWs.lastPhotoAdded || null,
              lastPhotoAddedDaysAgo: storeWs.lastPhotoAddedDaysAgo || null,
              verificationStatus: storeWs.verificationStatus || "unknown",
              lastVerified: storeWs.lastVerified || null,
              lastVerifiedDaysAgo: storeWs.lastVerifiedDaysAgo || null,
              verificationHistoryCount: storeWs.verificationHistoryCount || 0,
              verificationHistoryDetails:
                storeWs.verificationHistoryDetails || [],
              verificationReason: storeWs.verificationReason || null,
              lastVerificationAttempt: storeWs.lastVerificationAttempt || null,
              // Post Validation
              postValidation: storeWs.postValidation || "-",
              postValidationDetails: storeWs.postValidationDetails || [],
            })),
          };
        });

        return {
          id: `user_${user.uid}`,
          level: 1,
          displayName: user.display_name || user.email || "Unknown User",
          subtitle: `${userAdminWorkspaces.length} admin workspace${
            userAdminWorkspaces.length !== 1 ? "s" : ""
          }, ${userAdminWorkspaces.reduce(
            (total, adminWs) => total + (adminWs.storeWorkspaces || []).length,
            0
          )} store${
            userAdminWorkspaces.reduce(
              (total, adminWs) =>
                total + (adminWs.storeWorkspaces || []).length,
              0
            ) !== 1
              ? "s"
              : ""
          }`,
          hasChildren: adminEntries.length > 0,
          expanded: false,
          childCount: `${userAdminWorkspaces.length} admins`,
          posts: userAggregatedMetrics.posts || 0,
          recentPost: userAggregatedMetrics.recentPost || null,
          recentPostDaysAgo: userAggregatedMetrics.recentPostDaysAgo || null,
          scheduledPosts: userAggregatedMetrics.scheduledPosts || 0,
          scheduledPostsDetails:
            userAggregatedMetrics.scheduledPostsDetails || [],
          scheduledUpToDate: userAggregatedMetrics.scheduledUpToDate || null,
          profileStrength: userAggregatedMetrics.profile_strength || null,
          showProfileDialog: false,
          photos: userAggregatedMetrics.photos || 0,
          lastPhotoAdded: userAggregatedMetrics.lastPhotoAdded || null,
          lastPhotoAddedDaysAgo:
            userAggregatedMetrics.lastPhotoAddedDaysAgo || null,
          verificationStatus: user.verificationStatus || "unknown",
          lastVerified: user.lastVerified || null,
          lastVerifiedDaysAgo: user.lastVerifiedDaysAgo || null,
          verificationHistoryCount: user.verificationHistoryCount || 0,
          verificationHistoryDetails: user.verificationHistoryDetails || [],
          verificationReason: user.verificationReason || null,
          lastVerificationAttempt: user.lastVerificationAttempt || null,
          // Post Validation
          postValidation: "-", // Dash at user level
          postValidationDetails: [],
          children: adminEntries,
        };
      });
    },

    // Reviews Computed Properties
    reviewsHierarchicalHeaders() {
      return [
        {
          text: "Name",
          value: "displayName",
          sortable: false,
          width: "250px",
          align: "start",
        },
        {
          text: "Reviews",
          value: "reviews",
          sortable: false,
          align: "end",
          width: "130px",
        },
        {
          text: "Rating",
          value: "rating",
          sortable: false,
          align: "end",
          width: "130px",
        },
      ];
    },

    reviewsHierarchicalData() {
      return this.userReviewsData.map((user) => {
        const userAdminWorkspaces = user.adminWorkspaces || [];
        const userAggregatedMetrics = user.aggregatedMetrics || {};

        const totalStoreWorkspaces = userAdminWorkspaces.reduce(
          (total, adminWs) => {
            return total + (adminWs.storeWorkspaces || []).length;
          },
          0
        );

        const adminEntries = userAdminWorkspaces.map((adminWs) => {
          const storeWorkspaces = adminWs.storeWorkspaces || [];
          const adminAggregatedMetrics = adminWs.aggregatedMetrics || {};

          return {
            id: adminWs.id || `admin_${user.uid}_${adminWs.wid}`,
            level: 2,
            displayName: adminWs.title || `Admin Workspace ${adminWs.wid}`,
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            reviews: adminAggregatedMetrics.reviews || 0,
            rating: adminAggregatedMetrics.rating || null,
            children: storeWorkspaces.map((storeWs) => ({
              id:
                storeWs.id || `store_${user.uid}_${adminWs.wid}_${storeWs.wid}`,
              wid: storeWs.wid,
              mapsUri: storeWs.mapsUri || null,
              level: 3,
              displayName:
                storeWs.short_address ||
                storeWs.title ||
                `Store ${storeWs.wid}`,
              hasChildren: false,
              expanded: false,
              childCount: "Store",
              reviews: storeWs.reviews || 0,
              rating: storeWs.rating || null,
            })),
          };
        });

        return {
          id: `user_${user.uid}`,
          level: 1,
          displayName: user.display_name || user.email || "Unknown User",
          subtitle: `${userAdminWorkspaces.length} admin workspace${
            userAdminWorkspaces.length !== 1 ? "s" : ""
          }, ${totalStoreWorkspaces} store${
            totalStoreWorkspaces !== 1 ? "s" : ""
          }`,
          hasChildren: adminEntries.length > 0,
          expanded: false,
          childCount: `${userAdminWorkspaces.length} admins`,
          reviews: userAggregatedMetrics.reviews || 0,
          rating: userAggregatedMetrics.rating || null,
          children: adminEntries,
        };
      });
    },

    // Stats Computed Properties
    statsHierarchicalHeaders() {
      const baseHeaders = [
        {
          text: "Name",
          value: "displayName",
          sortable: true,
          width: "250px",
          align: "start",
        },
      ];

      const metricHeaders = this.statsDynamicMetricHeaders;
      return [...baseHeaders, ...metricHeaders];
    },

    statsDynamicMetricHeaders() {
      const columnOrder = [
        "maps_impressions", // Combined mobile + desktop in new structure
        "search_impressions", // Combined mobile + desktop in new structure
        "directions", // Was actions_driving_directions
        "call_clicks", // Was actions_phone
        "website_clicks", // Was actions_website
        "bookings", // Was actions_booking
        "mobile_maps_impressions", // Legacy field for backward compatibility
        "desktop_maps_impressions",
        "mobile_search_impressions",
        "desktop_search_impressions",
        "actions_driving_directions",
        "actions_phone",
        "actions_website",
        "actions_booking",
        "actions_food_orders",
        "actions_food_menu",
        "reviews",
        "rating",
        "rank",
        "total_stores",
      ];

      const headers = [];
      const availableMetrics = new Set();

      if (this.userStatsData && this.userStatsData.length > 0) {
        this.userStatsData.forEach((user) => {
          if (user && user.aggregatedMetrics) {
            Object.keys(user.aggregatedMetrics).forEach((metricKey) => {
              if (metricKey && typeof metricKey === "string") {
                availableMetrics.add(metricKey);
              }
            });
          }
        });
      }

      columnOrder.forEach((metricKey) => {
        if (availableMetrics.has(metricKey)) {
          headers.push({
            text: this.formatMetricName(metricKey),
            value: metricKey,
            sortable: true,
            align: "end",
            width: "130px",
          });
        }
      });

      availableMetrics.forEach((metricKey) => {
        if (
          !columnOrder.includes(metricKey) &&
          metricKey !== "posts" &&
          metricKey !== "profile_strength"
        ) {
          headers.push({
            text: this.formatMetricName(metricKey),
            value: metricKey,
            sortable: true,
            align: "end",
            width: "130px",
          });
        }
      });

      return headers;
    },

    statsHierarchicalData() {
      // Use dataUpdateKey to force reactivity
      this.dataUpdateKey;

      // console.log(
      //   "statsHierarchicalData computed - userStatsData:",
      //   this.userStatsData,
      //   "updateKey:",
      //   this.dataUpdateKey
      // );
      return this.userStatsData.map((user) => {
        // const userIndex = index; // Assign to avoid linter confusion
        // console.log(
        //   `Processing user ${userIndex} (${user.displayName}) in statsHierarchicalData:`,
        //   {
        //     hasHistoricalData: !!(
        //       user.historicalData && Object.keys(user.historicalData).length > 0
        //     ),
        //     historicalDataKeys: Object.keys(user.historicalData || {}),
        //     aggregatedMetrics: user.aggregatedMetrics,
        //   }
        // );

        const userAdminWorkspaces = user.adminWorkspaces || [];
        const userAggregatedMetrics = user.aggregatedMetrics || {};
        const userHistoricalData = user.historicalData || {};

        const totalStoreWorkspaces = userAdminWorkspaces.reduce(
          (total, adminWs) => {
            return total + (adminWs.storeWorkspaces || []).length;
          },
          0
        );

        // If there's only one admin workspace, skip the admin level and show admin data at user level
        if (userAdminWorkspaces.length === 1) {
          const singleAdmin = userAdminWorkspaces[0];
          const storeWorkspaces = singleAdmin.storeWorkspaces || [];
          const adminAggregatedMetrics = singleAdmin.aggregatedMetrics || {};
          const adminHistoricalData = singleAdmin.historicalData || {};

          // Promote store workspaces to level 2 (directly under user)
          const promotedStoreEntries = storeWorkspaces.map((storeWs) => ({
            id:
              storeWs.id ||
              `store_${user.uid}_${singleAdmin.wid}_${storeWs.wid}`,
            wid: storeWs.wid,
            mapsUri: storeWs.mapsUri || null,
            level: 2,
            displayName:
              storeWs.short_address || storeWs.title || `Store ${storeWs.wid}`,
            subtitle: storeWs.connected_at ? `Connected: ${this.formatDateForDisplay(storeWs.connected_at)}` : null,
            hasChildren: false,
            expanded: false,
            childCount: "Store",
            period: this.formatPeriod(storeWs.week_period),
            historicalData: storeWs.historicalData || {},
            ...(storeWs.allMetrics || {}),
            // Add percentage changes for store level
            percentageChanges: this.calculateAllMetricChanges(
              storeWs.allMetrics || {},
              storeWs.historicalData || {}
            ),
          }));

          return {
            id: `user_${user.uid}`,
            level: 1,
            displayName: user.display_name || user.email || "Unknown User",
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            period: "",
            historicalData: adminHistoricalData,
            // Use admin's aggregated metrics instead of user's
            ...adminAggregatedMetrics,
            // Add percentage changes for admin level (shown at user level)
            percentageChanges: this.calculateAllMetricChanges(
              adminAggregatedMetrics,
              adminHistoricalData
            ),
            children: promotedStoreEntries,
          };
        }

        // Multiple admin workspaces - keep the original 3-level hierarchy
        const adminEntries = userAdminWorkspaces.map((adminWs) => {
          const storeWorkspaces = adminWs.storeWorkspaces || [];
          const adminAggregatedMetrics = adminWs.aggregatedMetrics || {};
          const adminHistoricalData = adminWs.historicalData || {};

          return {
            id: adminWs.id || `admin_${user.uid}_${adminWs.wid}`,
            level: 2,
            displayName: adminWs.title || `Admin Workspace ${adminWs.wid}`,
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            expanded: false,
            childCount: `${storeWorkspaces.length} stores`,
            period: "",
            historicalData: adminHistoricalData,
            ...adminAggregatedMetrics,
            // Add percentage changes for admin level
            percentageChanges: this.calculateAllMetricChanges(
              adminAggregatedMetrics,
              adminHistoricalData
            ),
            children: storeWorkspaces.map((storeWs) => ({
              id:
                storeWs.id || `store_${user.uid}_${adminWs.wid}_${storeWs.wid}`,
              wid: storeWs.wid,
              mapsUri: storeWs.mapsUri || null,
              level: 3,
              displayName:
                storeWs.short_address ||
                storeWs.title ||
                `Store ${storeWs.wid}`,
              hasChildren: false,
              expanded: false,
              childCount: "Store",
              period: this.formatPeriod(storeWs.week_period),
              historicalData: storeWs.historicalData || {},
              ...(storeWs.allMetrics || {}),
              // Add percentage changes for store level
              percentageChanges: this.calculateAllMetricChanges(
                storeWs.allMetrics || {},
                storeWs.historicalData || {}
              ),
            })),
          };
        });

        return {
          id: `user_${user.uid}`,
          level: 1,
          displayName: user.displayName || user.email || "Unknown User",
          subtitle: `${userAdminWorkspaces.length} admin workspace${
            userAdminWorkspaces.length !== 1 ? "s" : ""
          }, ${totalStoreWorkspaces} store${
            totalStoreWorkspaces !== 1 ? "s" : ""
          }`,
          hasChildren: adminEntries.length > 0,
          expanded: false,
          childCount: `${userAdminWorkspaces.length} admins`,
          period: "",
          historicalData: userHistoricalData,
          ...userAggregatedMetrics,
          // Add percentage changes for user level
          percentageChanges: this.calculateAllMetricChanges(
            userAggregatedMetrics,
            userHistoricalData
          ),
          children: adminEntries,
        };
      });
    },

    // Rank Computed Properties
    rankDynamicMetricHeaders() {
      const columnOrder = [
        "total_keywords",
        "total_business_ranks", // Legacy field for backward compatibility
        "businesses_with_actual_rank",
        "businesses_with_rank_21",
        "average_excluding_21",
        "workspace_count",
        "average_rank", // Legacy field for backward compatibility
        "reviews", // New field in flattened structure
        "rating", // New field in flattened structure
        "rank", // Changed from average_rank in new structure
        "posts",
        "total_stores",
      ];

      const headers = [];
      const availableMetrics = new Set();

      // console.log(
      //   "rankDynamicMetricHeaders - userRankData:",
      //   this.userRankData
      // );

      if (this.userRankData && this.userRankData.length > 0) {
        this.userRankData.forEach((user) => {
          // console.log(
          //   `User ${index} aggregatedMetrics:`,
          //   user.aggregatedMetrics
          // );
          if (user && user.aggregatedMetrics) {
            Object.keys(user.aggregatedMetrics).forEach((metricKey) => {
              if (metricKey && typeof metricKey === "string") {
                availableMetrics.add(metricKey);
              }
            });
          }
        });
      }

      // console.log("Available metrics found:", Array.from(availableMetrics));

      columnOrder.forEach((metricKey) => {
        if (availableMetrics.has(metricKey)) {
          headers.push({
            text: this.formatRankMetricName(metricKey),
            value: metricKey,
            sortable: true,
            align: "end",
            width: "130px",
          });
        }
      });

      availableMetrics.forEach((metricKey) => {
        if (!columnOrder.includes(metricKey)) {
          headers.push({
            text: this.formatRankMetricName(metricKey),
            value: metricKey,
            sortable: true,
            align: "end",
            width: "130px",
          });
        }
      });

      // console.log("Final rankDynamicMetricHeaders:", headers);
      return headers;
    },

    rankFlatHeaders() {
      const baseHeaders = [
        {
          text: "Name",
          value: "displayName",
          sortable: false,
          width: "250px",
          align: "start",
        },
      ];

      return [...baseHeaders, ...this.rankDynamicMetricHeaders];
    },

    rankFlattenedData() {
      const flattened = [];

      this.rankHierarchicalData.forEach((user) => {
        flattened.push(user);

        if (this.expandedItems[user.id] && user.children) {
          user.children.forEach((admin) => {
            flattened.push(admin);

            if (this.expandedItems[admin.id] && admin.children) {
              admin.children.forEach((store) => {
                flattened.push(store);

                if (this.expandedItems[store.id] && store.children) {
                  store.children.forEach((keyword) => {
                    flattened.push(keyword);
                  });
                }
              });
            }
          });
        }
      });

      return flattened;
    },

    rankHierarchicalData() {
      // console.log(
      //   "rankHierarchicalData computed - userRankData:",
      //   this.userRankData
      // );
      return this.userRankData.map((user) => {
        const userAdminWorkspaces = user.adminWorkspaces || [];
        const userAggregatedMetrics = user.aggregatedMetrics || {};
        const userHistoricalData = user.historicalData || {};

        const totalStoreWorkspaces = userAdminWorkspaces.reduce(
          (total, adminWs) => {
            return total + (adminWs.storeWorkspaces || []).length;
          },
          0
        );

        const adminEntries = userAdminWorkspaces.map((adminWs) => {
          const storeWorkspaces = adminWs.storeWorkspaces || [];
          const adminAggregatedMetrics = adminWs.aggregatedMetrics || {};
          const adminHistoricalData = adminWs.historicalData || {};

          return {
            id: adminWs.id || `admin_${user.uid}_${adminWs.wid}`,
            level: 2,
            displayName: adminWs.title || `Admin Workspace ${adminWs.wid}`,
            subtitle: `${storeWorkspaces.length} store${
              storeWorkspaces.length !== 1 ? "s" : ""
            }`,
            hasChildren: storeWorkspaces.length > 0,
            childCount: `${storeWorkspaces.length} stores`,
            historicalData: adminHistoricalData,
            ...adminAggregatedMetrics,
            percentageChanges: this.calculateAllMetricChanges(
              adminAggregatedMetrics,
              adminHistoricalData
            ),
            children: storeWorkspaces.map((storeWs) => {
              const keywords = (storeWs.keywords || []).filter(
                (keyword) => keyword && typeof keyword === "object"
              );
              return {
                id:
                  storeWs.id ||
                  `store_${user.uid}_${adminWs.wid}_${storeWs.wid}`,
                wid: storeWs.wid,
                mapsUri: storeWs.mapsUri || null,
                level: 3,
                displayName:
                  storeWs.short_address ||
                  storeWs.title ||
                  `Store ${storeWs.wid}`,
                subtitle: `${keywords.length} keyword${
                  keywords.length !== 1 ? "s" : ""
                } tracked`,
                hasChildren: false, // Remove children, show keywords in tooltip instead
                childCount: `${keywords.length} keywords`,
                date: storeWs.date || "",
                historicalData: storeWs.historicalData || {},
                ...(storeWs.allMetrics || {}),
                percentageChanges: this.calculateAllMetricChanges(
                  storeWs.allMetrics || {},
                  storeWs.historicalData || {}
                ),
                // Store keywords data for tooltip display
                keywordsData: keywords,
              };
            }),
          };
        });

        const userPercentageChanges = this.calculateAllMetricChanges(
          userAggregatedMetrics,
          userHistoricalData
        );

        // console.log(
        //   `User (${user.displayName || user.email}) percentage changes:`,
        //   {
        //     userAggregatedMetrics,
        //     userHistoricalData: Object.keys(userHistoricalData),
        //     percentageChanges: userPercentageChanges,
        //   }
        // );

        return {
          id: `user_${user.uid}`,
          level: 1,
          displayName: user.display_name || user.email || "Unknown User",
          subtitle: `${userAdminWorkspaces.length} admin workspace${
            userAdminWorkspaces.length !== 1 ? "s" : ""
          }, ${totalStoreWorkspaces} store${
            totalStoreWorkspaces !== 1 ? "s" : ""
          }`,
          hasChildren: adminEntries.length > 0,
          childCount: `${userAdminWorkspaces.length} admins`,
          historicalData: userHistoricalData,
          ...userAggregatedMetrics,
          percentageChanges: userPercentageChanges,
          children: adminEntries,
        };
      });
    },
  },

  watch: {
    // Watch for all users changes
    allUsers: {
      handler(newUsers) {
        // Update filtered users list when all users change
        if (!this.showUserDropdown) {
          this.filteredUsersList = [...newUsers];
        }
      },
      deep: true,
    },

    // Watch for sort changes in Content tab
    contentSortBy() {
      this.dataUpdateKey++; // Force re-render with new sort
    },
    contentSortDesc() {
      this.dataUpdateKey++; // Force re-render with new sort
    },

    // Watch for sort changes in Performance tab
    performanceSortBy() {
      this.dataUpdateKey++; // Force re-render with new sort
    },
    performanceSortDesc() {
      this.dataUpdateKey++; // Force re-render with new sort
    },

    currentTab(newTab) {
      // Clear expansion state when switching tabs
      this.expandedItems = {};

      const newTabName = this.getTabName(newTab);

      // Update URL query parameter
      if (this.$route.query.tab !== newTabName) {
        this.$router.replace({
          name: this.$route.name,
          query: { ...this.$route.query, tab: newTabName },
        });
      }

      // Don't fetch data on tab switch - data should only be fetched when users or dates change
      // The data is already cached and will be displayed from cache
    },

    "$route.query.tab"(newTab) {
      // Handle external route changes (e.g., from redirects)
      const newTabIndex = this.getTabIndex(newTab);
      if (newTab && newTabIndex !== this.currentTab) {
        this.currentTab = newTabIndex;
      }
    },

    selectedDateIndex(newIndex, oldIndex) {
      // Watch for date index changes and trigger data refresh
      if (
        newIndex !== oldIndex &&
        newIndex !== null &&
        this.availableDates.length > 0
      ) {
        // console.log(`Date index changed from ${oldIndex} to ${newIndex}`);
        this.onDateSelectionChange();
      }
    },

    selectedUsers: {
      handler(newUsers, oldUsers) {
        // console.log("selectedUsers watcher triggered:", {
        //   oldCount: oldUsers?.length || 0,
        //   newCount: newUsers?.length || 0,
        //   newUsers: newUsers,
        // });

        // Force reactivity update when users change
        this.dataUpdateKey++;

        // If users were added (not just initial load), force data refresh
        if (oldUsers && newUsers.length > oldUsers.length) {
          // console.log("New users added, forcing data refresh...");
          this.$nextTick(() => {
            this.dataUpdateKey++;
            this.$forceUpdate();
          });
        }
      },
      deep: true,
      immediate: false,
    },
  },

  async mounted() {
    // Clear any existing comments cache to ensure fresh data
    this.clearCommentsCache();

    await this.fetchAllBoostUsers();

    // Sync pendingSelectedUsers with selectedUsers
    this.pendingSelectedUsers = [...this.selectedUsers];

    // Initialize filtered users list
    this.filteredUsersList = [...this.allUsers];

    // If users are already selected (from previous state or URL), load data
    if (this.selectedUsers.length > 0) {
      // console.log("Users already selected on mount, fetching data...");
      await this.onUserSelectionChange();
    }
  },

  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener("click", this.handleClickOutside);
  },

  methods: {
    // Open Google Maps URL in new tab
    navigateToProfile(mapsUri) {
      console.log("Opening Maps URI:", mapsUri);
      if (mapsUri) {
        window.open(mapsUri, "_blank");
      } else {
        console.error("No Maps URI provided");
      }
    },
    // Helper method to sort array by a specific field
    sortByField(array, field, ascending = true) {
      return [...array].sort((a, b) => {
        let aVal = this.getNestedValue(a, field);
        let bVal = this.getNestedValue(b, field);

        // Special handling for verification status
        if (field === "verificationHistory" || field === "verificationStatus") {
          // Priority order: unverified > failed > error > verified > unknown
          const statusOrder = {
            unverified: 0,
            failed: 1,
            error: 2,
            verified: 3,
            unknown: 4,
          };

          const aStatus = a.verificationStatus || "unknown";
          const bStatus = b.verificationStatus || "unknown";
          const aOrder = statusOrder[aStatus] ?? 4;
          const bOrder = statusOrder[bStatus] ?? 4;

          if (aOrder !== bOrder) {
            return ascending ? aOrder - bOrder : bOrder - aOrder;
          }
          // If same status, sort by verification history count
          const aCount = a.verificationHistoryCount || 0;
          const bCount = b.verificationHistoryCount || 0;
          return ascending ? bCount - aCount : aCount - bCount;
        }

        // Special handling for profile strength
        if (field === "profileStrength") {
          // Get the numerical value for sorting
          let aStrength = 0;
          let bStrength = 0;
          
          if (a.profileStrength) {
            // Store level has 'score' field
            if (a.profileStrength.score !== undefined) {
              aStrength = a.profileStrength.score;
            }
            // User/Admin level has 'average_score' field
            else if (a.profileStrength.average_score !== undefined) {
              aStrength = a.profileStrength.average_score;
            }
          }
          
          if (b.profileStrength) {
            // Store level has 'score' field
            if (b.profileStrength.score !== undefined) {
              bStrength = b.profileStrength.score;
            }
            // User/Admin level has 'average_score' field
            else if (b.profileStrength.average_score !== undefined) {
              bStrength = b.profileStrength.average_score;
            }
          }
          
          return ascending ? aStrength - bStrength : bStrength - aStrength;
        }

        // Special handling for profile fields
        if (field.startsWith("profileFields.")) {
          const fieldName = field.replace("profileFields.", "");
          
          // Get the values based on the data structure
          let aValue = 0;
          let bValue = 0;
          
          if (a.profileStrength) {
            // Check if this is store level (has completed_fields) vs user/admin level (has field_completion_rates)
            if (a.profileStrength.field_completion_rates) {
              // User/Admin level - use completion count
              const fieldData = a.profileStrength.field_completion_rates[fieldName];
              aValue = fieldData ? (fieldData.completed_count || 0) : 0;
            } else if (a.profileStrength.completed_fields) {
              // Store level - check if field is completed (1 for yes, 0 for no)
              aValue = a.profileStrength.completed_fields.includes(fieldName) ? 1 : 0;
            }
          }
          
          if (b.profileStrength) {
            // Check if this is store level (has completed_fields) vs user/admin level (has field_completion_rates)
            if (b.profileStrength.field_completion_rates) {
              // User/Admin level - use completion count
              const fieldData = b.profileStrength.field_completion_rates[fieldName];
              bValue = fieldData ? (fieldData.completed_count || 0) : 0;
            } else if (b.profileStrength.completed_fields) {
              // Store level - check if field is completed (1 for yes, 0 for no)
              bValue = b.profileStrength.completed_fields.includes(fieldName) ? 1 : 0;
            }
          }
          
          // Sort numerically
          return ascending ? aValue - bValue : bValue - aValue;
        }

        // Special handling for post validation
        if (field === "postValidation") {
          const validationOrder = {
            Invalid: 0,
            Valid: 1,
            "-": 2,
          };
          const aOrder = validationOrder[a.postValidation] ?? 2;
          const bOrder = validationOrder[b.postValidation] ?? 2;
          return ascending ? aOrder - bOrder : bOrder - aOrder;
        }

        // Special handling for scheduledPosts (numeric with "No posts" as 0)
        if (field === "scheduledPosts") {
          // These are already numeric (0 for no posts)
          const aCount = typeof aVal === 'number' ? aVal : 0;
          const bCount = typeof bVal === 'number' ? bVal : 0;
          return ascending ? aCount - bCount : bCount - aCount;
        }

        // Special handling for recentPost (date field with null for "No posts")
        if (field === "recentPost") {
          // Null/undefined means "No posts" - should sort last when ascending
          if ((aVal === null || aVal === undefined) && (bVal === null || bVal === undefined)) {
            return 0;
          }
          if (aVal === null || aVal === undefined) {
            return ascending ? 1 : -1; // No posts go to end when ascending
          }
          if (bVal === null || bVal === undefined) {
            return ascending ? -1 : 1; // No posts go to end when ascending
          }
          // Both have dates, compare them
          const dateA = new Date(aVal);
          const dateB = new Date(bVal);
          return ascending ? dateA - dateB : dateB - dateA;
        }

        // Special handling for scheduledUpToDate (date field with null for "No schedule")
        if (field === "scheduledUpToDate") {
          // Null/undefined means "No schedule" - should sort last when ascending
          if ((aVal === null || aVal === undefined) && (bVal === null || bVal === undefined)) {
            return 0;
          }
          if (aVal === null || aVal === undefined) {
            return ascending ? 1 : -1; // No schedule goes to end when ascending
          }
          if (bVal === null || bVal === undefined) {
            return ascending ? -1 : 1; // No schedule goes to end when ascending
          }
          // Both have dates, compare them
          const dateA = new Date(aVal);
          const dateB = new Date(bVal);
          return ascending ? dateA - dateB : dateB - dateA;
        }

        // Handle null/undefined values for other fields
        if (aVal === null || aVal === undefined) aVal = "";
        if (bVal === null || bVal === undefined) bVal = "";

        // Handle numeric values
        if (typeof aVal === "number" && typeof bVal === "number") {
          return ascending ? aVal - bVal : bVal - aVal;
        }

        // Handle date values for other date fields
        if (
          field.includes("Date") ||
          field === "lastPhotoAdded"
        ) {
          const dateA = new Date(aVal || 0);
          const dateB = new Date(bVal || 0);
          return ascending ? dateA - dateB : dateB - dateA;
        }

        // Handle string values
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();

        if (aVal < bVal) return ascending ? -1 : 1;
        if (aVal > bVal) return ascending ? 1 : -1;
        return 0;
      });
    },

    // Get nested value from object using dot notation
    getNestedValue(obj, path) {
      return path.split(".").reduce((curr, prop) => {
        return curr ? curr[prop] : undefined;
      }, obj);
    },

    // Sort hierarchical data with level-specific sorting
    sortHierarchicalDataByLevel(data, userSort, adminSort, storeSort) {
      // Sort top level (users)
      const sortedData = this.sortByField(data, userSort.field, !userSort.desc);

      // Sort admin level within each user
      return sortedData.map((user) => {
        if (user.children && user.children.length > 0) {
          const sortedAdmins = this.sortByField(
            user.children,
            adminSort.field,
            !adminSort.desc
          );

          // Sort store level within each admin
          const adminsWithSortedStores = sortedAdmins.map((admin) => {
            if (admin.children && admin.children.length > 0) {
              return {
                ...admin,
                children: this.sortByField(
                  admin.children,
                  storeSort.field,
                  !storeSort.desc
                ),
              };
            }
            return admin;
          });

          return {
            ...user,
            children: adminsWithSortedStores,
          };
        }
        return user;
      });
    },

    // Legacy method for backwards compatibility
    sortHierarchicalData(data, field, ascending = true) {
      // First sort the top level
      const sortedData = this.sortByField(data, field, ascending);

      // Then sort children at each level
      return sortedData.map((item) => {
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: this.sortHierarchicalData(
              item.children,
              field,
              ascending
            ),
          };
        }
        return item;
      });
    },

    // Custom sort for Content tab
    customSortContent(items, sortBy, sortDesc) {
      if (!sortBy || sortBy.length === 0) return items;

      const field = Array.isArray(sortBy) ? sortBy[0] : sortBy;
      const desc = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc;

      // Apply the same sorting to all levels
      return this.sortHierarchicalDataByLevel(
        items,
        { field: field, desc: desc },
        { field: field, desc: desc },
        { field: field, desc: desc }
      );
    },

    // Custom sort for Performance tab
    customSortPerformance(items, sortBy, sortDesc) {
      if (!sortBy || sortBy.length === 0) return items;

      const field = Array.isArray(sortBy) ? sortBy[0] : sortBy;
      const desc = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc;

      // Apply the same sorting to all levels
      return this.sortHierarchicalDataByLevel(
        items,
        { field: field, desc: desc },
        { field: field, desc: desc },
        { field: field, desc: desc }
      );
    },

    // Fetch comments for an item (user/admin/store)
    async fetchComments(item, showLoading = false) {
      // Set loading state if requested (for Show Comments button)
      if (showLoading) {
        this.$set(this.loadingComments, item.id, true);
        // Clear existing cache for this item to ensure fresh data
        this.$set(this.commentsCache, item.id, []);
      }

      try {
        let commentsRef;
        if (item.level === 1) {
          // User-level
          commentsRef = collection(
            db,
            "users",
            item.id.replace("user_", ""),
            "comments"
          );
        } else if (item.level === 2) {
          // Admin workspace
          const [uid, adminId] = item.id.replace("admin_", "").split("_");
          commentsRef = collection(
            db,
            "users",
            uid,
            "fly_workspaces",
            adminId,
            "comments"
          );
        } else if (item.level === 3) {
          // Store workspace
          const parts = item.id.replace("store_", "").split("_");
          const [uid, adminId, storeId] = parts;
          commentsRef = collection(
            db,
            "users",
            uid,
            "fly_workspaces",
            adminId,
            "store_wise",
            storeId,
            "comments"
          );
        }

        const snapshot = await getDocs(
          query(commentsRef, orderBy("timestamp", "desc"))
        );
        const comments = snapshot.docs.map((doc) => doc.data());

        // Force cache update using Vue.set for better reactivity
        this.$set(this.commentsCache, item.id, comments);
      } catch (error) {
        console.error("Error fetching comments:", error);
        if (showLoading) {
          this.$alert.show("Failed to load comments");
        }
      } finally {
        // Clear loading state
        if (showLoading) {
          this.$set(this.loadingComments, item.id, false);
        }
      }
    },

    // Add a comment for an item
    async addComment(item) {
      const text = this.newComments[item.id];
      if (!text) return;

      // Get workspace info from localStorage
      let workspace = {};
      try {
        workspace = JSON.parse(localStorage.getItem("CURRENT_WORKSPACE")) || {};
      } catch (e) {
        workspace = {};
      }
      const workspaceName = workspace.name || "Unknown";

      let commentsRef;
      if (item.level === 1) {
        commentsRef = collection(
          db,
          "users",
          item.id.replace("user_", ""),
          "comments"
        );
      } else if (item.level === 2) {
        const [uid, adminId] = item.id.replace("admin_", "").split("_");
        commentsRef = collection(
          db,
          "users",
          uid,
          "fly_workspaces",
          adminId,
          "comments"
        );
      } else if (item.level === 3) {
        const parts = item.id.replace("store_", "").split("_");
        const [uid, adminId, storeId] = parts;
        commentsRef = collection(
          db,
          "users",
          uid,
          "fly_workspaces",
          adminId,
          "store_wise",
          storeId,
          "comments"
        );
      }
      await addDoc(commentsRef, {
        text,
        timestamp: new Date(),
        uid: this.currentUID,
        wid: this.WID,
        author: workspaceName,
      });
      this.newComments[item.id] = "";
      await this.fetchComments(item);
    },

    // Open dialog for adding comment
    openAddCommentDialog(item) {
      // Use $set to ensure reactivity
      this.$set(this, "selectedItemForComment", item);
      this.$set(this, "commentText", "");
      this.$set(this, "commentDialog", true);
    },

    // Close the comment dialog
    closeCommentDialog() {
      this.commentDialog = false;
      this.selectedItemForComment = null;
      this.commentText = "";
    },

    // Submit comment from dialog
    async submitComment() {
      if (!this.commentText.trim() || !this.selectedItemForComment) return;

      // Set the comment text in newComments for the addComment method
      this.newComments[this.selectedItemForComment.id] =
        this.commentText.trim();

      try {
        await this.addComment(this.selectedItemForComment);
        this.closeCommentDialog();
        this.$alert.show("Comment added successfully", "success");
      } catch (error) {
        console.error("Error adding comment:", error);
        this.$alert.show("Failed to add comment");
      }
    },
    // Clear comments cache to ensure fresh fetching
    clearCommentsCache() {
      this.commentsCache = {};
      this.loadingComments = {};
    },

    // Call fetchComments for each item after data load - DISABLED to fetch comments only on demand
    async afterPostsDataLoad() {
      // Clear existing cache to prevent showing old/mixed comments
      this.clearCommentsCache();
      // Comments are now fetched only when user clicks "Show Comments" button
      // This improves performance and ensures level-specific comments
    },
    getTabIndex(tabName) {
      const tabMap = {
        content: 0,
        performance: 1,
        // refreshes: 2,
        historical: 2,
        stats: 1,
        posts: 0,
      };
      return tabMap[tabName] !== undefined ? tabMap[tabName] : 0;
    },

    getTabName(tabIndex) {
      const indexMap = {
        0: "content",
        1: "performance",
        // 2: "refreshes",
        2: "historical",
      };
      return indexMap[tabIndex] || "content";
    },

    customFilter(item, queryText) {
      if (!queryText) return true;

      const searchTerm = queryText.toLowerCase();
      const displayName = (item.displayName || "").toLowerCase();
      const email = (item.email || "").toLowerCase();
      const uid = (item.uid || "").toLowerCase();

      return (
        displayName.includes(searchTerm) ||
        email.includes(searchTerm) ||
        uid.includes(searchTerm)
      );
    },

    async fetchAllBoostUsers() {
      this.loadingUsers = true;
      try {
        this.allUsers = [];
        let lastDoc = null;
        let hasMore = true;

        while (hasMore) {
          let usersQuery = query(
            collection(db, "users"),
            where("fly_plan", "==", "boost"),
            orderBy("created_time", "desc"),
            limit(this.batchSize)
          );

          if (lastDoc) {
            usersQuery = query(
              collection(db, "users"),
              where("fly_plan", "==", "boost"),
              orderBy("created_time", "desc"),
              startAfter(lastDoc),
              limit(this.batchSize)
            );
          }

          const snapshot = await getDocs(usersQuery);

          if (snapshot.empty) {
            hasMore = false;
            break;
          }

          const batchUsers = snapshot.docs.map((doc) => {
            const userData = doc.data();
            return {
              uid: doc.id,
              displayName: userData.display_name || userData.email || doc.id,
              email: userData.email,
              plan: userData.fly_plan,
              created_at: userData.created_time,
            };
          });

          this.allUsers.push(...batchUsers);
          lastDoc = snapshot.docs[snapshot.docs.length - 1];

          if (snapshot.docs.length < this.batchSize) {
            hasMore = false;
          }
        }
        // console.log(`Fetched ${this.allUsers.length} boost users`);
      } catch (error) {
        console.error("Error fetching boost users:", error);
        this.error = "Failed to load boost users. Please try again.";
      } finally {
        this.loadingUsers = false;
      }
    },

    async onUserSelectionChange() {
      this.expandedItems = {};
      // Clear search text when user is selected
      this.searchText = "";

      // Reset processed tabs state
      this.processedTabs = {
        content: false,
        performance: false,
        refreshes: false,
      };

      // Clear unified data cache
      this.unifiedDataCache.clear();

      // Force complete data reset to ensure clean state
      this.userStatsData = [];
      this.overallStats = null;
      this.userRankData = [];
      this.overallRankStats = null;
      this.userPostsData = [];
      this.overallPostsStats = null;
      this.userReviewsData = [];
      this.overallReviewsStats = null;
      this.userRefreshesData = [];
      this.overallRefreshesStats = null;

      // Clear refresh data cache when clearing data
      this.clearRefreshCache();

      // Force reactivity update
      this.dataUpdateKey++;

      if (this.selectedUsers.length > 0) {
        // First fetch available dates
        await this.fetchAvailableDates();

        const tabName = this.getTabName(this.currentTab);
        // console.log(
        //   `User selection changed. Current tab: ${this.currentTab} (${tabName}), Selected users:`,
        //   this.selectedUsers
        // );

        // Fetch data for the active tab only if not already cached
        // Always fetch content data first as it's needed for user hierarchy in Historical tab
        if (!this.contentDataExists()) {
          await this.fetchSelectedUsersPostsData();
        }

        // Only fetch performance data if that tab is active and data doesn't exist
        if (tabName === "performance" && !this.peformanceDataExists()) {
          await this.fetchSelectedUsersStats();
        }
      } else {
        this.availableDates = [];
        this.selectedDateIndex = 0;
      }
    },
    contentDataExists() {
      const userData = this.contentTabData[this.selectedDate] || {};
      // check which uid data exists in userData
      const userDataExists = this.selectedUsers.every((uid) => userData[uid]);
      if (userDataExists) {
        let mergedUserData = [];
        this.selectedUsers.forEach((uid) => {
          if (this.contentTabData[this.selectedDate][uid]) {
            mergedUserData.push(this.contentTabData[this.selectedDate][uid]);
          }
        });
        if (mergedUserData?.length > 0) {
          this.$set(this, "userPostsData", mergedUserData);
          return true;
        }
      }
      return false;
    },
    peformanceDataExists() {
      const userData = this.performanceTabData[this.selectedDate] || {};
      const userDataExists = this.selectedUsers.every((uid) => userData[uid]);
      if (userDataExists) {
        let mergedUserData = [];
        this.selectedUsers.forEach((uid) => {
          if (this.performanceTabData[this.selectedDate][uid]) {
            mergedUserData.push(
              this.performanceTabData[this.selectedDate][uid]
            );
          }
        });
        if (mergedUserData?.length > 0) {
          mergedUserData?.forEach((user, index) => {
            this.$set(this.userStatsData, index, user);
          });
        }
        return true;
      }
      return false;
    },

    removeUser(uid) {
      this.selectedUsers = this.selectedUsers.filter((u) => u !== uid);
      this.onUserSelectionChange();
    },

    // Remove user from pending selection (before applying)
    removePendingUser(uid) {
      this.pendingSelectedUsers = this.pendingSelectedUsers.filter(
        (u) => u !== uid
      );
    },

    // Handle clear users action
    handleClearUsers() {
      this.pendingSelectedUsers = [];
      // If Apply button is clicked with no users, it will clear the data
    },

    // Toggle user dropdown
    toggleUserDropdown(event) {
      event.stopPropagation();
      this.showUserDropdown = !this.showUserDropdown;

      if (this.showUserDropdown) {
        // Initialize filtered list when opening
        this.filteredUsersList = [...this.allUsers];

        // Keep dropdown left-aligned by default (as shown in the image)
        this.dropdownRightAligned = false;

        // Focus search field after dropdown opens
        this.$nextTick(() => {
          if (this.$refs.userSearchInput) {
            this.$refs.userSearchInput.focus();
          }
        });

        // Add click outside listener
        setTimeout(() => {
          document.addEventListener("click", this.handleClickOutside);
        }, 0);
      } else {
        // Remove click outside listener
        document.removeEventListener("click", this.handleClickOutside);
      }
    },

    // Handle click outside dropdown
    handleClickOutside(event) {
      const dropdown = this.$refs.userDropdown;
      if (dropdown && !dropdown.contains(event.target)) {
        this.showUserDropdown = false;
        document.removeEventListener("click", this.handleClickOutside);
      }
    },

    // Filter users based on search
    filterUsers() {
      const search = this.searchText.toLowerCase();
      if (!search) {
        this.filteredUsersList = [...this.allUsers];
      } else {
        this.filteredUsersList = this.allUsers.filter(
          (user) =>
            user.displayName.toLowerCase().includes(search) ||
            user.email.toLowerCase().includes(search)
        );
      }
    },

    // Toggle user selection
    toggleUserSelection(uid) {
      const index = this.pendingSelectedUsers.indexOf(uid);
      if (index > -1) {
        this.pendingSelectedUsers.splice(index, 1);
      } else {
        this.pendingSelectedUsers.push(uid);
      }

      // Clear search and reset filtered list
      this.searchText = "";
      this.filteredUsersList = [...this.allUsers];
    },

    // Select all users
    toggleSelectAllUsers() {
      if (this.allUsersSelected) {
        // Deselect all filtered users
        this.filteredUsersList.forEach((user) => {
          const index = this.pendingSelectedUsers.indexOf(user.uid);
          if (index > -1) {
            this.pendingSelectedUsers.splice(index, 1);
          }
        });
      } else {
        // Select all filtered users
        this.filteredUsersList.forEach((user) => {
          if (!this.pendingSelectedUsers.includes(user.uid)) {
            this.pendingSelectedUsers.push(user.uid);
          }
        });
      }
    },

    // Set only this user
    setOnlyThisUser(user) {
      this.pendingSelectedUsers = [user.uid];

      // Clear search and reset filtered list
      this.searchText = "";
      this.filteredUsersList = [...this.allUsers];
    },

    // Apply user selection and fetch data
    async applyUserSelection() {
      this.loadingApplyButton = true;

      try {
        // Find newly added users
        const newUsers = this.pendingSelectedUsers.filter(
          (uid) => !this.selectedUsers.includes(uid)
        );
        const removedUsers = this.selectedUsers.filter(
          (uid) => !this.pendingSelectedUsers.includes(uid)
        );

        // Update selected users immediately
        this.selectedUsers = [...this.pendingSelectedUsers];

        // Clear existing data if all users were removed
        if (this.selectedUsers.length === 0) {
          this.clearAllData();
          return;
        }

        // If users were only removed, update data without fetching
        if (removedUsers.length > 0 && newUsers.length === 0) {
          this.removeUsersFromData(removedUsers);
          return;
        }

        // If there are new users, fetch their data
        if (newUsers.length > 0) {
          // If we have existing data, fetch new users in background
          if (this.hasExistingContentData || this.hasExistingPerformanceData) {
            await this.fetchNewUsersDataProgressive(newUsers);
          } else {
            // First load, fetch all users normally
            await this.onUserSelectionChange();
          }
        }
      } finally {
        this.loadingApplyButton = false;
      }
    },

    // Fetch data for newly added users progressively
    async fetchNewUsersDataProgressive(newUserIds) {
      this.loadingBackgroundUsers = true;

      try {
        // First fetch available dates for new users
        await this.fetchAvailableDates();

        const tabName = this.getTabName(this.currentTab);

        // Fetch data based on current tab
        if (tabName === "content" || tabName === "historical") {
          // Both Content and Historical tabs need content data
          await this.fetchNewUsersContentData(newUserIds);
        } else if (tabName === "performance") {
          await this.fetchNewUsersPerformanceData(newUserIds);
        }
      } catch (error) {
        console.error("Error fetching new users data:", error);
        this.error = "Failed to fetch data for new users";
      } finally {
        this.loadingBackgroundUsers = false;
      }
    },

    // Fetch content data for new users
    async fetchNewUsersContentData(userIds) {
      const userPostsPromises = userIds.map((uid) =>
        this.fetchUserPostsDataFlattened(uid, this.selectedDate, true)
      );

      const results = await Promise.all(userPostsPromises);

      // Merge with existing data
      results.forEach((userData) => {
        if (userData) {
          // Store in contentTabData cache for persistence
          if (!this.contentTabData[this.selectedDate]) {
            this.$set(this.contentTabData, this.selectedDate, {});
          }
          this.$set(
            this.contentTabData[this.selectedDate],
            userData.uid,
            userData
          );

          // Check if user already exists in userPostsData to avoid duplicates
          const existingIndex = this.userPostsData.findIndex(
            (u) => u.uid === userData.uid
          );
          if (existingIndex >= 0) {
            // Replace existing data
            this.$set(this.userPostsData, existingIndex, userData);
          } else {
            // Add new user data
            this.userPostsData.push(userData);
          }
        }
      });

      // Recalculate overall stats
      this.calculateOverallPostsStats();

      // Force re-computation of hierarchical data
      this.dataUpdateKey++;
    },

    // Fetch performance data for new users
    async fetchNewUsersPerformanceData(userIds) {
      const userStatsPromises = userIds.map((uid) =>
        this.fetchUserStatsFlattened(uid, this.selectedDate, true)
      );

      const results = await Promise.all(userStatsPromises);

      // Merge with existing data
      results.forEach((userData) => {
        if (userData) {
          // Store in performanceTabData cache for persistence
          if (!this.performanceTabData[this.selectedDate]) {
            this.$set(this.performanceTabData, this.selectedDate, {});
          }
          this.$set(
            this.performanceTabData[this.selectedDate],
            userData.uid,
            userData
          );

          // Check if user already exists in userStatsData to avoid duplicates
          const existingIndex = this.userStatsData.findIndex(
            (u) => u.uid === userData.uid
          );
          if (existingIndex >= 0) {
            // Replace existing data
            this.$set(this.userStatsData, existingIndex, userData);
          } else {
            // Add new user data
            this.userStatsData.push(userData);
          }
        }
      });

      // Recalculate overall stats
      this.calculateOverallStats();

      // Force re-computation of hierarchical data
      this.dataUpdateKey++;
    },

    // Remove users from existing data
    removeUsersFromData(userIds) {
      // Remove from content data
      this.userPostsData = this.userPostsData.filter(
        (user) => !userIds.includes(user.uid)
      );

      // Remove from performance data
      this.userStatsData = this.userStatsData.filter(
        (user) => !userIds.includes(user.uid)
      );

      // Remove from cache
      if (this.contentTabData && this.contentTabData[this.selectedDate]) {
        userIds.forEach((uid) => {
          delete this.contentTabData[this.selectedDate][uid];
        });
      }
      if (
        this.performanceTabData &&
        this.performanceTabData[this.selectedDate]
      ) {
        userIds.forEach((uid) => {
          delete this.performanceTabData[this.selectedDate][uid];
        });
      }

      // Force re-computation of hierarchical data
      this.dataUpdateKey++;
    },

    // Clear all data
    clearAllData() {
      this.userPostsData = [];
      this.userStatsData = [];
      this.userRankData = [];
      this.userReviewsData = [];
      this.userRefreshesData = [];
      this.overallStats = null;
      this.overallPostsStats = null;
      this.overallRankStats = null;
      this.overallReviewsStats = null;
      this.overallRefreshesStats = null;
      this.availableDates = [];
      this.selectedDateIndex = 0;
      this.contentTabData = {};
      this.performanceTabData = {};
      this.dataUpdateKey++;
    },

    // Unified Data Fetching Methods - Core Optimization
    // async fetchUnifiedUserData() {
    //   if (this.selectedUsers.length === 0) return;

    //   console.log(
    //     "📊 Starting unified data fetch for users:",
    //     this.selectedUsers
    //   );
    //   this.loading = true;
    //   this.error = null;

    //   try {
    //     // Fetch all user data in parallel from super_admin_metrices (serves both content and performance tabs)
    //     const userDataPromises = this.selectedUsers.map((uid) =>
    //       this.fetchUnifiedUserDataSingle(uid, this.selectedDate)
    //     );

    //     const userDataResults = await this.processBatch(
    //       userDataPromises,
    //       this.maxConcurrentQueries,
    //       (promise) => promise
    //     );

    //     const validResults = userDataResults.filter(
    //       (result) => result !== null
    //     );

    //     // Cache the unified data for reuse
    //     validResults.forEach((userData) => {
    //       const cacheKey = `${userData.uid}_${this.selectedDate || "latest"}`;
    //       this.unifiedDataCache.set(cacheKey, userData);
    //     });

    //     console.log(`✅ Unified data cached for ${validResults.length} users`);
    //   } catch (error) {
    //     console.error("Error fetching unified user data:", error);
    //     this.error = "Failed to load user data. Please try again.";
    //   } finally {
    //     this.loading = false;
    //   }
    // },

    // async fetchUnifiedUserDataSingle(uid, specificDate = null) {
    //   try {
    //     const userInfo = this.allUsers.find((u) => u.uid === uid);
    //     if (!userInfo) return null;

    //     console.log(
    //       `📈 Fetching unified data for user: ${uid}, date: ${
    //         specificDate || "latest"
    //       }`
    //     );

    //     // Get admin workspaces for this user
    //     const adminWorkspacesSnap = await getDocs(
    //       collection(db, "users", uid, "fly_workspaces")
    //     );
    //     const adminWorkspaceData = [];

    //     adminWorkspacesSnap.docs.forEach((doc) => {
    //       const data = doc.data();
    //       const id = doc.id;

    //       if (data.brandName && !data.admin) {
    //         adminWorkspaceData.push({ id, data });
    //       }
    //     });

    //     if (adminWorkspaceData.length === 0) {
    //       console.log(`No admin workspaces found for user ${uid}`);
    //       return {
    //         uid: uid,
    //         displayName: userInfo.displayName,
    //         email: userInfo.email,
    //         adminWorkspaces: [],
    //         aggregatedMetrics: {},
    //         rawSuperAdminData: null,
    //       };
    //     }

    //     // Fetch data from super_admin_metrices for all admin workspaces in parallel
    //     const adminDataPromises = adminWorkspaceData.map(
    //       async ({ id: adminId, data: adminData }) => {
    //         const metricsData = await this.fetchSuperAdminMetricesData(
    //           uid,
    //           adminId,
    //           specificDate
    //         );
    //         return {
    //           adminId,
    //           adminData,
    //           metricsData,
    //         };
    //       }
    //     );

    //     const adminResults = await Promise.all(adminDataPromises);
    //     const validAdminResults = adminResults.filter(
    //       (result) => result.metricsData !== null
    //     );

    //     // Process admin workspaces and aggregate data
    //     const processedAdminWorkspaces = [];
    //     let combinedAggregatedMetrics = {};

    //     for (const { adminId, adminData, metricsData } of validAdminResults) {
    //       const { aggregated, storeWiseData, dateInfo } = metricsData;

    //       // Process store workspaces from store_wise data
    //       const storeWorkspaces = Object.keys(storeWiseData).map((wid) => {
    //         const storeData = storeWiseData[wid];
    //         return {
    //           wid: wid,
    //           id: `store_${uid}_${adminId}_${wid}`,
    //           title: storeData.title || storeData.name || `Store ${wid}`,
    //           short_address: storeData.short_address,
    //           allMetrics: this.extractAllMetricsFlattened(storeData),
    //           ...storeData,
    //         };
    //       });

    //       // Create admin workspace structure with store workspaces
    //       processedAdminWorkspaces.push({
    //         id: adminId,
    //         wid: adminId,
    //         title: adminData.brandName || adminData.name || `Admin ${adminId}`,
    //         brandName: adminData.brandName,
    //         storeWorkspaces: storeWorkspaces,
    //         aggregatedMetrics: aggregated || {},
    //         dateInfo: dateInfo,
    //       });

    //       // Combine aggregated metrics from all admin workspaces
    //       if (aggregated) {
    //         Object.keys(aggregated).forEach((key) => {
    //           if (typeof aggregated[key] === "number") {
    //             combinedAggregatedMetrics[key] =
    //               (combinedAggregatedMetrics[key] || 0) + aggregated[key];
    //           } else if (
    //             aggregated[key] !== null &&
    //             aggregated[key] !== undefined
    //           ) {
    //             combinedAggregatedMetrics[key] = aggregated[key];
    //           }
    //         });
    //       }
    //     }

    //     // Process posts, photos, and verification data FIRST before creating final userData structure
    //     console.log(`🔄 Processing additional data for user ${uid}...`);

    //     const [postsData, photosData, verificationData] = await Promise.all([
    //       this.processPostsDataForUser(uid),
    //       this.processPhotosDataForUser(uid),
    //       this.processVerificationDataForUser(
    //         uid,
    //         { uid, displayName: userInfo.displayName },
    //         specificDate
    //       ),
    //     ]);

    //     console.log(`📊 Additional data processed for user ${uid}:`, {
    //       hasPostsData: !!postsData,
    //       hasPhotosData: !!photosData,
    //       hasVerificationData: !!verificationData,
    //       postsStoreKeys: Object.keys(postsData?.storePostsData || {}),
    //       photosStoreKeys: Object.keys(photosData?.storePhotosData || {}),
    //       verificationStoreKeys: Object.keys(
    //         verificationData?.storeVerificationData || {}
    //       ),
    //     });

    //     const userData = {
    //       uid: uid,
    //       displayName: userInfo.displayName,
    //       email: userInfo.email,
    //       adminWorkspaces: processedAdminWorkspaces,
    //       aggregatedMetrics: combinedAggregatedMetrics,
    //       fetchedDate: specificDate || "latest",
    //       // Add the processed data
    //       postsMetrics: postsData,
    //       photosMetrics: photosData,
    //       verificationMetrics: verificationData,
    //     };

    //     return userData;
    //   } catch (error) {
    //     console.error(`Error fetching unified data for user ${uid}:`, error);
    //     return null;
    //   }
    // },

    // async fetchSuperAdminMetricesData(uid, adminId, specificDate = null) {
    //   try {
    //     const metricsRef = collection(
    //       db,
    //       "users",
    //       uid,
    //       "fly_workspaces",
    //       adminId,
    //       "super_admin_metrices"
    //     );

    //     let fetchedDate, latestData;

    //     if (specificDate) {
    //       // Fetch specific date
    //       const specificDocRef = doc(metricsRef, specificDate);
    //       const specificDocSnap = await getDoc(specificDocRef);

    //       if (!specificDocSnap.exists()) {
    //         console.warn(
    //           `No super_admin_metrices data found for admin workspace: ${adminId} on date: ${specificDate}`
    //         );
    //         return null;
    //       }

    //       latestData = specificDocSnap.data();
    //       fetchedDate = specificDocSnap.id;
    //     } else {
    //       // Get the latest date with data
    //       const latestQuery = query(
    //         metricsRef,
    //         orderBy("created_at", "desc"),
    //         limit(1)
    //       );
    //       const latestSnapshot = await getDocs(latestQuery);

    //       if (latestSnapshot.empty) {
    //         console.log(
    //           `No super_admin_metrices data found for admin workspace: ${adminId}`
    //         );
    //         return null;
    //       }

    //       const latestDoc = latestSnapshot.docs[0];
    //       latestData = latestDoc.data();
    //       fetchedDate = latestDoc.id;
    //     }

    //     // Get store_wise data
    //     const storeWiseRef = collection(metricsRef, fetchedDate, "store_wise");
    //     const storeWiseSnapshot = await getDocs(storeWiseRef);

    //     const storeWiseData = {};
    //     storeWiseSnapshot.docs.forEach((doc) => {
    //       storeWiseData[doc.id] = doc.data();
    //     });

    //     return {
    //       aggregated: latestData.aggregated || {},
    //       storeWiseData: storeWiseData,
    //       dateInfo: {
    //         fetchedDate: fetchedDate,
    //         date_ranges: latestData.date_ranges || {},
    //         created_at: latestData.created_at,
    //         updated_at: latestData.updated_at,
    //       },
    //     };
    //   } catch (error) {
    //     console.error(
    //       `Error fetching super_admin_metrices for admin ${adminId}:`,
    //       error
    //     );
    //     return null;
    //   }
    // },

    // Cache Management Methods
    // hasCachedUnifiedData() {
    //   if (this.selectedUsers.length === 0) return false;

    //   const currentDateKey = this.selectedDate || "latest";
    //   return this.selectedUsers.every((uid) => {
    //     const cacheKey = `${uid}_${currentDateKey}`;
    //     return this.unifiedDataCache.has(cacheKey);
    //   });
    // },

    // getCachedUnifiedData() {
    //   const currentDateKey = this.selectedDate || "latest";
    //   return this.selectedUsers
    //     .map((uid) => {
    //       const cacheKey = `${uid}_${currentDateKey}`;
    //       return this.unifiedDataCache.get(cacheKey);
    //     })
    //     .filter((data) => data !== undefined);
    // },

    // // Removed complex caching logic - using original methods for reliability

    // // Additional Data Processing Methods for Unified Approach
    // async processPostsDataForUser(uid) {
    //   console.log(`📄 Processing posts data for user: ${uid}`);

    //   try {
    //     const postsData = await this.calculateUserPostsMetrics(uid);
    //     return postsData;
    //   } catch (error) {
    //     console.warn(`Error processing posts data for user ${uid}:`, error);
    //     return { storePostsData: {}, adminMetrics: {}, userMetrics: {} };
    //   }
    // },

    // async processPhotosDataForUser(uid) {
    //   console.log(`📸 Processing photos data for user: ${uid} (optimized)`);

    //   try {
    //     const photosData = await this.calculateUserPhotosMetricsOptimized(uid);
    //     return photosData;
    //   } catch (error) {
    //     console.warn(`Error processing photos data for user ${uid}:`, error);
    //     return { storePhotosData: {}, adminMetrics: {}, userMetrics: {} };
    //   }
    // },

    // async processVerificationDataForUser(uid, userData, specificDate = null) {
    //   console.log(`🔍 Processing verification data for user: ${uid}`);

    //   try {
    //     const verificationData = await this.calculateUserVerificationMetrics(
    //       uid,
    //       specificDate
    //     );
    //     return verificationData;
    //   } catch (error) {
    //     console.warn(
    //       `Error processing verification data for user ${uid}:`,
    //       error
    //     );
    //     return { storeVerificationData: {} };
    //   }
    // },

    // // Optimized Photos Processing Method - reads according to Firebase snapshot structure
    // async calculateUserPhotosMetricsOptimized(uid) {
    //   const metrics = {
    //     photos: 0,
    //     lastPhotoAdded: null,
    //     lastPhotoAddedDaysAgo: null,
    //   };

    //   try {
    //     console.log(
    //       `📸 Starting optimized photos calculation for user: ${uid}`
    //     );

    //     // Get all workspaces for this user in one call
    //     const workspacesRef = collection(db, "users", uid, "fly_workspaces");
    //     const workspacesSnapshot = await getDocs(workspacesRef);

    //     // Separate admin and store workspaces
    //     let adminWorkspaces = [];
    //     let storeWorkspaces = [];

    //     workspacesSnapshot.forEach((wsDoc) => {
    //       const wsData = wsDoc.data();
    //       const wsId = wsDoc.id;

    //       if (wsData.brandName && (!wsData.admin || wsData.admin === null)) {
    //         adminWorkspaces.push({
    //           id: wsId,
    //           data: wsData,
    //           brandName: wsData.brandName,
    //           title: wsData.name || wsData.brandName,
    //         });
    //       } else if (wsData.admin) {
    //         storeWorkspaces.push({
    //           id: wsId,
    //           data: wsData,
    //           adminId: wsData.admin,
    //           title: wsData.name || `Store ${wsId}`,
    //         });
    //       }
    //     });

    //     console.log(
    //       `Found ${storeWorkspaces.length} store workspaces for photos processing`
    //     );

    //     // Process photos from all store workspaces in parallel (according to Firebase snapshot structure)
    //     const storePhotosPromises = storeWorkspaces.map(async (storeWs) => {
    //       try {
    //         // Read from photo_gallery/photo structure as shown in the Firebase snapshot
    //         const photoDocRef = doc(
    //           db,
    //           "users",
    //           uid,
    //           "fly_workspaces",
    //           storeWs.id,
    //           "photo_gallery",
    //           "photo"
    //         );

    //         const photoDocSnapshot = await getDoc(photoDocRef);

    //         if (photoDocSnapshot.exists()) {
    //           const photoDocData = photoDocSnapshot.data();
    //           const mediaItems = photoDocData.mediaItems || [];

    //           let storePhotosCount = 0;
    //           let storeLatestPhoto = null;
    //           let storeLatestDaysAgo = null;

    //           // Process mediaItems according to the structure
    //           mediaItems.forEach((mediaItem) => {
    //             const createTime = mediaItem.createTime;

    //             if (createTime) {
    //               const photoDate = new Date(createTime);

    //               // Skip invalid dates (filter photos from 2020 onwards as in original logic)
    //               if (photoDate.getFullYear() >= 2020) {
    //                 storePhotosCount++;

    //                 // Track latest photo
    //                 if (
    //                   !storeLatestPhoto ||
    //                   photoDate > new Date(storeLatestPhoto)
    //                 ) {
    //                   storeLatestPhoto = createTime;
    //                   const now = new Date();
    //                   const diffTime = Math.abs(now - photoDate);
    //                   const diffDays = Math.ceil(
    //                     diffTime / (1000 * 60 * 60 * 24)
    //                   );
    //                   storeLatestDaysAgo = diffDays;
    //                 }
    //               }
    //             }
    //           });

    //           return {
    //             storeId: storeWs.id,
    //             photos: storePhotosCount,
    //             lastPhotoAdded: storeLatestPhoto,
    //             lastPhotoAddedDaysAgo: storeLatestDaysAgo,
    //           };
    //         } else {
    //           console.log(`No photo document found for store ${storeWs.id}`);
    //           return {
    //             storeId: storeWs.id,
    //             photos: 0,
    //             lastPhotoAdded: null,
    //             lastPhotoAddedDaysAgo: null,
    //           };
    //         }
    //       } catch (error) {
    //         console.warn(
    //           `Error processing photos for store ${storeWs.id}:`,
    //           error
    //         );
    //         return {
    //           storeId: storeWs.id,
    //           photos: 0,
    //           lastPhotoAdded: null,
    //           lastPhotoAddedDaysAgo: null,
    //         };
    //       }
    //     });

    //     const storePhotosResults = await Promise.all(storePhotosPromises);

    //     // Aggregate results
    //     let storePhotosData = {};
    //     let latestPhotoDate = null;

    //     storePhotosResults.forEach((result) => {
    //       storePhotosData[result.storeId] = {
    //         photos: result.photos,
    //         lastPhotoAdded: result.lastPhotoAdded,
    //         lastPhotoAddedDaysAgo: result.lastPhotoAddedDaysAgo,
    //       };

    //       // Update global metrics
    //       metrics.photos += result.photos;

    //       if (result.lastPhotoAdded) {
    //         const photoDate = new Date(result.lastPhotoAdded);
    //         if (!latestPhotoDate || photoDate > latestPhotoDate) {
    //           latestPhotoDate = photoDate;
    //           metrics.lastPhotoAdded = result.lastPhotoAdded;
    //           metrics.lastPhotoAddedDaysAgo = result.lastPhotoAddedDaysAgo;
    //         }
    //       }
    //     });

    //     // Calculate admin-level metrics
    //     const adminMetrics = {};
    //     adminWorkspaces.forEach((admin) => {
    //       const adminStores = storeWorkspaces.filter(
    //         (store) => store.adminId === admin.id
    //       );
    //       let adminPhotosCount = 0;
    //       let adminLatestPhoto = null;
    //       let adminLatestDaysAgo = null;

    //       adminStores.forEach((store) => {
    //         const storePhotosInfo = storePhotosData[store.id];
    //         if (storePhotosInfo) {
    //           adminPhotosCount += storePhotosInfo.photos;

    //           if (storePhotosInfo.lastPhotoAdded) {
    //             const storePhotoDate = new Date(storePhotosInfo.lastPhotoAdded);
    //             if (
    //               !adminLatestPhoto ||
    //               storePhotoDate > new Date(adminLatestPhoto)
    //             ) {
    //               adminLatestPhoto = storePhotosInfo.lastPhotoAdded;
    //               adminLatestDaysAgo = storePhotosInfo.lastPhotoAddedDaysAgo;
    //             }
    //           }
    //         }
    //       });

    //       adminMetrics[admin.id] = {
    //         photos: adminPhotosCount,
    //         lastPhotoAdded: adminLatestPhoto,
    //         lastPhotoAddedDaysAgo: adminLatestDaysAgo,
    //       };
    //     });

    //     console.log(
    //       `✅ Optimized photos metrics calculated for user ${uid}: ${metrics.photos} total photos`
    //     );

    //     return {
    //       ...metrics,
    //       adminWorkspaces: adminWorkspaces,
    //       storeWorkspaces: storeWorkspaces,
    //       storePhotosData: storePhotosData,
    //       adminMetrics: adminMetrics,
    //       userMetrics: metrics,
    //     };
    //   } catch (error) {
    //     console.warn(
    //       `Error in optimized photos calculation for user ${uid}:`,
    //       error
    //     );
    //     return {
    //       ...metrics,
    //       adminWorkspaces: [],
    //       storeWorkspaces: [],
    //       storePhotosData: {},
    //       adminMetrics: {},
    //       userMetrics: metrics,
    //     };
    //   }
    // },

    async refreshCurrentTab() {
      // This method is only called when user explicitly clicks refresh button
      // Force refresh the data for the current tab
      const tabName = this.getTabName(this.currentTab);

      if (this.selectedUsers.length === 0) {
        await this.fetchAllBoostUsers();
        return;
      }

      // Force fetch fresh data (bypassing cache)
      if (tabName === "content") {
        await this.fetchSelectedUsersPostsData();
      } else if (tabName === "performance") {
        await this.fetchSelectedUsersStats();
      } else if (tabName === "historical") {
        // Historical trends component handles its own refresh
      }
    },

    exportCurrentTabData() {
      const tabName = this.getTabName(this.currentTab);
      if (tabName === "stats") {
        this.exportStatsData();
      } else if (tabName === "rank") {
        this.exportRankData();
      } else if (tabName === "posts") {
        this.exportPostsData();
      } else if (tabName === "reviews") {
        this.exportReviewsData();
      }
    },

    exportStatsData() {
      const data = {
        selectedUsers: this.selectedUsers,
        overallStats: this.overallStats,
        userStatsData: this.userStatsData,
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gmb-stats-selected-users-${
        new Date().toISOString().split("T")[0]
      }.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    exportRankData() {
      const data = {
        selectedUsers: this.selectedUsers,
        overallRankStats: this.overallRankStats,
        userRankData: this.userRankData,
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rank-data-selected-users-${
        new Date().toISOString().split("T")[0]
      }.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    exportPostsData() {
      const data = {
        selectedUsers: this.selectedUsers,
        overallPostsStats: this.overallPostsStats,
        userPostsData: this.userPostsData,
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `posts-data-selected-users-${
        new Date().toISOString().split("T")[0]
      }.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    exportReviewsData() {
      const data = {
        selectedUsers: this.selectedUsers,
        overallReviewsStats: this.overallReviewsStats,
        userReviewsData: this.userReviewsData,
        exportedAt: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reviews-data-selected-users-${
        new Date().toISOString().split("T")[0]
      }.json`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // Stats Methods
    async fetchSelectedUsersStats() {
      if (this.selectedUsers.length === 0) return;

      // console.log(
      //   "Starting fetchSelectedUsersStats with new flattened structure...",
      //   "Selected date:",
      //   this.selectedDate
      // );
      this.loading = true;
      this.error = null;

      try {
        const userData = this.performanceTabData[this.selectedDate] || {};
        // console.log("userData:", userData);
        const userDataExists = this.selectedUsers.some((uid) => userData[uid]);
        let filteredOtherUserDataResults = [];
        if (userDataExists) {
          const otherUserData = this.selectedUsers.filter(
            (uid) => !userData[uid]
          );
          // console.log("otherUserData", otherUserData);
          if (otherUserData) {
            const otherUserDataPromises = otherUserData?.map((uid) =>
              this.fetchUserStatsFlattened(uid, this.selectedDate, true)
            );
            const otherUserDataResults = await Promise.all(
              otherUserDataPromises
            );
            filteredOtherUserDataResults = otherUserDataResults.filter(
              (result) => result !== null
            );
          }
          let mergedUserData = [];
          // mergedUserData.push(userData);
          this.selectedUsers.forEach((uid) => {
            if (this.performanceTabData[this.selectedDate][uid]) {
              mergedUserData.push(
                this.performanceTabData[this.selectedDate][uid]
              );
            }
          });
          if (filteredOtherUserDataResults.length > 0) {
            mergedUserData.push(...filteredOtherUserDataResults);
            // mergedUserData = [
            //   ...userData,
            //   ...filteredOtherUserDataResults,
            // ];
          }
          mergedUserData?.forEach((user, index) => {
            this.$set(this.userStatsData, index, user);
            this.$store.commit("SET_PERFORMANCE_TAB_DATA", {
              data: user,
              date: this.selectedDate,
              uid: user.uid,
            });
          });
          // console.log(
          //   "vuex store performanceTabData::",
          //   this.performanceTabData
          // );
        }

        const userStatsPromises = this.selectedUsers.map((uid) =>
          this.fetchUserStatsFlattened(uid, this.selectedDate, true)
        );

        const batchSize = 5;
        const userStatsResults = [];

        for (let i = 0; i < userStatsPromises.length; i += batchSize) {
          const batch = userStatsPromises.slice(i, i + batchSize);
          const batchResults = await Promise.all(batch);
          userStatsResults.push(...batchResults);
        }

        const filteredResults = userStatsResults.filter(
          (result) => result !== null
        );
        // console.log("Stats data fetched:", filteredResults);

        // Clear existing data first to ensure clean state
        this.userStatsData = [];

        // Use $set for each user individually to ensure deep reactivity
        filteredResults.forEach((user, index) => {
          this.$set(this.userStatsData, index, user);
          this.$store.commit("SET_PERFORMANCE_TAB_DATA", {
            data: user,
            date: this.selectedDate,
            uid: user.uid,
          });
        });

        this.calculateOverallStats();
        // console.log("Overall stats calculated:", this.overallStats);
        // console.log("hasStatsData after fetch:", this.hasStatsData);

        // Debug: Check if historical data is present for all users
        // filteredResults.forEach((user, index) => {
        // console.log(
        //   `User ${index} (${user.displayName}) historical data keys:`,
        //   Object.keys(user.historicalData || {})
        // );
        // console.log(
        //   `User ${index} percentage changes:`,
        //   user.percentageChanges || "No percentage changes"
        // );
        // });

        // Force Vue to re-evaluate computed properties
        this.dataUpdateKey++;

        // Use nextTick to ensure DOM updates after data changes
        this.$nextTick(() => {
          this.$forceUpdate();
          // console.log(
          //   "Force update completed for",
          //   filteredResults.length,
          //   "users"
          // );

          // Debug: Check if DOM elements have the correct data
          setTimeout(() => {
            // const metricCells = document.querySelectorAll(".metric-cell");
            // console.log(`Found ${metricCells.length} metric cells in DOM`);
            // const percentageElements =
            //   document.querySelectorAll(".percentage-change");
            // console.log(
            //   `Found ${percentageElements.length} percentage change elements in DOM`
            // );
            // Check hierarchical data structure
            // console.log(
            //   "Current statsHierarchicalData:",
            //   this.statsHierarchicalData
            // );
          }, 500);
        });
      } catch (error) {
        console.error("Error fetching selected users stats:", error);
        this.error = "Failed to load user stats. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async fetchUserStatsFlattened(
      uid,
      specificDate = null,
      fetchHistorical = true
    ) {
      try {
        const userInfo = this.allUsers.find((u) => u.uid === uid);
        if (!userInfo) return null;

        // console.log(
        //   `Fetching flattened stats for user: ${uid}${
        //     specificDate ? ` on date: ${specificDate}` : ""
        //   }${fetchHistorical ? " with 12 weeks historical data" : ""}`
        // );

        // First, identify all admin workspaces for this user (same logic as refresh data)
        const adminWorkspacesSnap = await getDocs(
          collection(db, "users", uid, "fly_workspaces")
        );
        const adminWorkspaceData = [];
        const storeWorkspacesByAdmin = new Map();

        adminWorkspacesSnap.docs.forEach((doc) => {
          const data = doc.data();
          const id = doc.id;

          if (!data.admin) {
            // This is an admin workspace
            adminWorkspaceData.push({ id, data });
            storeWorkspacesByAdmin.set(id, []);
          } else if (data.admin) {
            // This is a store workspace
            if (!storeWorkspacesByAdmin.has(data.admin)) {
              storeWorkspacesByAdmin.set(data.admin, []);
            }
            storeWorkspacesByAdmin.get(data.admin).push({ id, data });
          }
        });

        if (adminWorkspaceData.length === 0) {
          // console.log(`No admin workspaces found for user ${uid}`);
          return {
            uid: uid,
            displayName: userInfo.displayName,
            email: userInfo.email,
            adminWorkspaces: [],
            aggregatedMetrics: {},
          };
        }

        // Process all admin workspaces and collect their data
        const allAdminWorkspaces = [];
        let combinedAggregatedMetrics = {};
        let allStoreWorkspaces = [];

        for (const { id: adminId, data: adminData } of adminWorkspaceData) {
          // console.log(
          //   `Processing admin workspace: ${adminId} with brandName: ${adminData.brandName}`
          // );

          const metricsRef = collection(
            db,
            "users",
            uid,
            "fly_workspaces",
            adminId,
            "super_admin_metrices"
          );

          let fetchedDate,
            latestData,
            historicalData = {};

          if (specificDate) {
            // Fetch specific date
            try {
              const specificDocRef = doc(
                db,
                "users",
                uid,
                "fly_workspaces",
                adminId,
                "super_admin_metrices",
                specificDate
              );
              const specificDocSnap = await getDoc(specificDocRef);

              if (!specificDocSnap.exists()) {
                console.warn(
                  `No metrics data found for admin workspace: ${adminId} on date: ${specificDate}`
                );
                continue; // Skip this admin workspace, try the next one
              }

              latestData = specificDocSnap.data();
              fetchedDate = specificDocSnap.id;

              // Fetch historical data if requested
              if (fetchHistorical) {
                historicalData = await this.fetchHistoricalWeeks(
                  uid,
                  adminId,
                  specificDate,
                  12
                );
              }
            } catch (error) {
              console.error(
                `Error fetching specific date ${specificDate} for admin workspace ${adminId}:`,
                error
              );
              continue; // Skip this admin workspace, try the next one
            }
          } else {
            // Get the latest date with data
            const latestQuery = query(
              metricsRef,
              orderBy("created_at", "desc"),
              limit(1)
            );
            const latestSnapshot = await getDocs(latestQuery);

            if (latestSnapshot.empty) {
              // console.log(
              //   `No metrics data found for admin workspace: ${adminId}`
              // );
              continue; // Skip this admin workspace, try the next one
            }

            const latestDoc = latestSnapshot.docs[0];
            latestData = latestDoc.data();
            fetchedDate = latestDoc.id;

            // Fetch historical data if requested
            if (fetchHistorical) {
              historicalData = await this.fetchHistoricalWeeks(
                uid,
                adminId,
                fetchedDate,
                12
              );
            }
          }

          // console.log(
          //   `Found latest data for admin workspace ${adminId} on date: ${fetchedDate}`,
          //   latestData
          // );

          // Get aggregated metrics from this admin workspace
          const aggregatedMetrics = latestData.aggregated || {};
          // console.log(
          //   `Aggregated metrics for admin workspace ${adminId}:`,
          //   aggregatedMetrics
          // );

          // Combine metrics (sum up numeric values)
          Object.keys(aggregatedMetrics).forEach((key) => {
            if (key === "profile_strength" && aggregatedMetrics[key]) {
              // Special handling for profile strength aggregation
              const profileData = aggregatedMetrics[key];
              if (!combinedAggregatedMetrics[key]) {
                combinedAggregatedMetrics[key] = {
                  average_score: 0,
                  total_stores_analyzed: 0,
                  perfect_stores: 0,
                  excellent_stores: 0,
                  good_stores: 0,
                  fair_stores: 0,
                  poor_stores: 0,
                  critical_stores: 0,
                  empty_stores: 0,
                  field_completion_rates: {},
                };
              }

              // Sum up numeric profile strength fields
              const numericFields = [
                "total_stores_analyzed",
                "perfect_stores",
                "excellent_stores",
                "good_stores",
                "fair_stores",
                "poor_stores",
                "critical_stores",
                "empty_stores",
              ];
              numericFields.forEach((field) => {
                combinedAggregatedMetrics[key][field] =
                  (combinedAggregatedMetrics[key][field] || 0) +
                  (profileData[field] || 0);
              });

              // Calculate weighted average for average_score
              const totalStores =
                combinedAggregatedMetrics[key].total_stores_analyzed;
              const currentAvg =
                combinedAggregatedMetrics[key].average_score || 0;
              const newStores = profileData.total_stores_analyzed || 0;
              const newAvg = profileData.average_score || 0;

              if (totalStores > 0) {
                combinedAggregatedMetrics[key].average_score =
                  (currentAvg * (totalStores - newStores) +
                    newAvg * newStores) /
                  totalStores;
              } else {
                combinedAggregatedMetrics[key].average_score = newAvg;
              }

              // Merge field completion rates
              if (profileData.field_completion_rates) {
                combinedAggregatedMetrics[key].field_completion_rates = {
                  ...combinedAggregatedMetrics[key].field_completion_rates,
                  ...profileData.field_completion_rates,
                };
              }
            } else if (typeof aggregatedMetrics[key] === "number") {
              combinedAggregatedMetrics[key] =
                (combinedAggregatedMetrics[key] || 0) + aggregatedMetrics[key];
            } else if (
              aggregatedMetrics[key] !== null &&
              aggregatedMetrics[key] !== undefined
            ) {
              // For non-numeric values, take the latest value
              combinedAggregatedMetrics[key] = aggregatedMetrics[key];
            }
          });

          // Get store-wise data from this admin workspace
          const storeWiseRef = collection(
            db,
            "users",
            uid,
            "fly_workspaces",
            adminId,
            "super_admin_metrices",
            fetchedDate,
            "store_wise"
          );
          const storeWiseQuery = query(
            storeWiseRef,
            orderBy("created_at", "desc")
          );
          const storeWiseSnapshot = await getDocs(storeWiseQuery);

          const adminStoreWorkspaces = [];
          storeWiseSnapshot.docs.forEach((doc) => {
            const storeData = doc.data();
            const wid = doc.id;

            // console.log(
            //   `Store data for ${wid} in admin ${adminId}:`,
            //   storeData
            // );

            // Get historical data for this store
            const storeHistoricalData = {};
            Object.keys(historicalData).forEach((date) => {
              if (
                historicalData[date].stores &&
                historicalData[date].stores[wid]
              ) {
                storeHistoricalData[date] = historicalData[date].stores[wid];
              }
            });

            // Create store workspace object with all available metrics
            const storeWorkspace = {
              wid: wid,
              name: `Store ${wid}`,
              title: storeData.title || storeData.name || `Store ${wid}`,
              adminWorkspaceId: adminId, // Track which admin this belongs to
              // Include all metrics from the flattened structure
              posts: storeData.posts || 0,
              reviews: storeData.reviews || 0,
              rating: storeData.rating || null,
              rank: storeData.rank || null,
              total_keywords: storeData.total_keywords || 0,
              maps_impressions: storeData.maps_impressions || 0,
              search_impressions: storeData.search_impressions || 0,
              directions: storeData.directions || 0,
              call_clicks: storeData.call_clicks || 0,
              website_clicks: storeData.website_clicks || 0,
              bookings: storeData.bookings || 0,
              // Keep original data for compatibility
              ...storeData,
              allMetrics: this.extractAllMetricsFlattened(storeData),
              historicalData: storeHistoricalData,
            };

            adminStoreWorkspaces.push(storeWorkspace);
            allStoreWorkspaces.push(storeWorkspace);
          });

          // Create admin workspace structure
          const adminWorkspace = {
            id: adminId,
            wid: adminId,
            name: adminData.brandName || adminData.name || `Admin ${adminId}`,
            title: adminData.brandName || adminData.name || `Admin ${adminId}`,
            brandName: adminData.brandName, // Properly access brandName from actual admin workspace
            adminWorkspaceId: adminId,
            storeWorkspaces: adminStoreWorkspaces,
            aggregatedMetrics: aggregatedMetrics,
            historicalData: Object.keys(historicalData).reduce((acc, date) => {
              acc[date] = historicalData[date].aggregated || {};
              return acc;
            }, {}),
          };

          allAdminWorkspaces.push(adminWorkspace);
        }

        // console.log(
        //   `Found ${allStoreWorkspaces.length} total stores across ${allAdminWorkspaces.length} admin workspaces for user: ${uid}`,
        //   allStoreWorkspaces
        // );

        return {
          uid: uid,
          displayName: userInfo.displayName,
          email: userInfo.email,
          adminWorkspaces: allAdminWorkspaces,
          aggregatedMetrics: combinedAggregatedMetrics,
          totalDesktopImpressions:
            combinedAggregatedMetrics.maps_impressions || 0,
          totalMobileImpressions:
            combinedAggregatedMetrics.maps_impressions || 0, // Combined in new structure
          fetchedDate: specificDate || "latest", // Return the requested date or "latest"
          historicalData: {}, // Combined historical data would be complex, simplify for now
        };
      } catch (error) {
        console.error(`Error fetching flattened stats for user ${uid}:`, error);
        return null;
      }
    },

    async fetchHistoricalWeeks(uid, adminId, currentDate, weeksCount = 12) {
      try {
        // console.log(
        //   `Fetching ${weeksCount} weeks of historical data for user: ${uid}, admin: ${adminId} from date: ${currentDate}`
        // );

        const metricsRef = collection(
          db,
          "users",
          uid,
          "fly_workspaces",
          adminId,
          "super_admin_metrices"
        );

        // Get all available dates and sort them
        const allDocsQuery = query(metricsRef, orderBy("created_at", "desc"));
        const allDocsSnapshot = await getDocs(allDocsQuery);

        const allDates = allDocsSnapshot.docs
          .map((doc) => doc.id)
          .sort((a, b) => new Date(b) - new Date(a));

        // console.log(
        //   `Found ${allDates.length} total dates for user ${uid}:`,
        //   allDates
        // );

        // Find the current date index
        const currentDateIndex = allDates.indexOf(currentDate);
        if (currentDateIndex === -1) {
          // console.warn(
          //   `Current date ${currentDate} not found in available dates for user ${uid}`
          // );
          // console.warn(`Available dates:`, allDates);
          return {};
        }

        // Get previous weeks (excluding current week)
        const historicalDates = allDates.slice(
          currentDateIndex + 1,
          currentDateIndex + 1 + weeksCount
        );
        // console.log("Historical dates to fetch:", historicalDates);

        const historicalData = {};

        // Fetch data for each historical date
        for (const date of historicalDates) {
          try {
            const dateDocRef = doc(metricsRef, date);
            const dateDocSnap = await getDoc(dateDocRef);

            if (dateDocSnap.exists()) {
              const dateData = dateDocSnap.data();

              // Get aggregated metrics
              const aggregatedMetrics = dateData.aggregated || {};

              // Get store-wise data
              const storeWiseRef = collection(dateDocRef, "store_wise");
              const storeWiseSnapshot = await getDocs(storeWiseRef);

              const storeMetrics = {};
              storeWiseSnapshot.docs.forEach((storeDoc) => {
                storeMetrics[storeDoc.id] = storeDoc.data();
              });

              historicalData[date] = {
                aggregated: aggregatedMetrics,
                stores: storeMetrics,
              };
            }
          } catch (error) {
            console.error(
              `Error fetching historical data for date ${date}:`,
              error
            );
          }
        }

        // console.log(
        //   `Fetched historical data for ${
        //     Object.keys(historicalData).length
        //   } weeks for user ${uid}:`,
        //   Object.keys(historicalData)
        // );
        return historicalData;
      } catch (error) {
        console.error("Error fetching historical weeks:", error);
        return {};
      }
    },

    async fetchWorkspaceStats(
      uid,
      adminId,
      fetchedDate,
      workspaceId,
      workspaceName
    ) {
      try {
        // Access store-wise data from the flattened structure
        const storeWiseDocRef = doc(
          db,
          "users",
          uid,
          "fly_workspaces",
          adminId,
          "super_admin_metrices",
          fetchedDate,
          "store_wise",
          workspaceId
        );
        const storeWiseDoc = await getDoc(storeWiseDocRef);

        if (storeWiseDoc.exists()) {
          const storeData = storeWiseDoc.data();
          return {
            wid: workspaceId,
            name: workspaceName,
            // Map flattened structure fields to expected format
            maps_impressions: storeData.maps_impressions || 0,
            search_impressions: storeData.search_impressions || 0,
            directions: storeData.directions || 0,
            call_clicks: storeData.call_clicks || 0,
            website_clicks: storeData.website_clicks || 0,
            bookings: storeData.bookings || 0,
            stored_at: storeData.stored_at || storeData.last_updated,
            id: storeWiseDoc.id,
            ...storeData, // Include all other fields
          };
        }
        return null;
      } catch (error) {
        console.error(
          `Error fetching workspace stats for ${workspaceId}:`,
          error
        );
        return null;
      }
    },

    calculateOverallStats() {
      // console.log(
      //   "calculateOverallStats called with userStatsData:",
      //   this.userStatsData
      // );
      if (this.userStatsData.length === 0) {
        this.overallStats = null;
        // console.log("No user stats data, setting overallStats to null");
        return;
      }

      let totalAdminWorkspaces = 0;
      let totalStoreWorkspaces = 0;
      let aggregatedMetrics = {};

      this.userStatsData.forEach((userStats) => {
        const adminWorkspaces = userStats.adminWorkspaces || [];
        totalAdminWorkspaces += adminWorkspaces.length;

        adminWorkspaces.forEach((adminWs) => {
          totalStoreWorkspaces += (adminWs.storeWorkspaces || []).length;
        });

        Object.keys(userStats.aggregatedMetrics || {}).forEach((key) => {
          aggregatedMetrics[key] =
            (aggregatedMetrics[key] || 0) +
            (userStats.aggregatedMetrics[key] || 0);
        });
      });

      const desktop = aggregatedMetrics.desktop_maps_impressions || 0;
      const mobile = aggregatedMetrics.mobile_maps_impressions || 0;
      aggregatedMetrics.total_views = desktop + mobile;

      this.overallStats = {
        totalAdminWorkspaces,
        totalStoreWorkspaces,
        totalWorkspaces: totalAdminWorkspaces + totalStoreWorkspaces,
        aggregatedMetrics: aggregatedMetrics,
        totalDesktopImpressions: desktop,
        totalMobileImpressions: mobile,
      };
      // console.log("Overall stats set:", this.overallStats);
    },

    // Rank Methods
    async fetchSelectedUsersRankData() {
      if (this.selectedUsers.length === 0) return;

      // console.log(
      //   "Starting fetchSelectedUsersRankData with new flattened structure...",
      //   "Selected date:",
      //   this.selectedDate
      // );
      this.loading = true;
      this.error = null;

      try {
        const userRankPromises = this.selectedUsers.map((uid) =>
          this.fetchUserRankDataFlattened(uid, this.selectedDate, true)
        );

        const batchSize = 5;
        const userRankResults = [];

        for (let i = 0; i < userRankPromises.length; i += batchSize) {
          const batch = userRankPromises.slice(i, i + batchSize);
          const batchResults = await Promise.all(batch);
          userRankResults.push(...batchResults);
        }

        const filteredResults = userRankResults.filter(
          (result) => result !== null
        );
        // console.log("Rank data fetched:", filteredResults);

        // Use $set to ensure reactivity (same as stats)
        this.$set(this, "userRankData", filteredResults);
        this.calculateOverallRankStats();
        // console.log("Overall rank stats calculated:", this.overallRankStats);
      } catch (error) {
        console.error("Error fetching selected users rank data:", error);
        this.error = "Failed to load user rank data. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async fetchUserRankDataFlattened(
      uid,
      specificDate = null,
      fetchHistorical = true
    ) {
      try {
        const userInfo = this.allUsers.find((u) => u.uid === uid);
        if (!userInfo) return null;

        // console.log(
        //   `Fetching flattened rank data for user: ${uid}${
        //     specificDate ? ` on date: ${specificDate}` : ""
        //   }`
        // );

        // First, identify all admin workspaces for this user (same logic as other functions)
        const adminWorkspacesSnap = await getDocs(
          collection(db, "users", uid, "fly_workspaces")
        );
        const adminWorkspaceData = [];

        adminWorkspacesSnap.docs.forEach((doc) => {
          const data = doc.data();
          const id = doc.id;

          if (!data.admin) {
            // This is an admin workspace
            adminWorkspaceData.push({ id, data });
          }
        });

        if (adminWorkspaceData.length === 0) {
          console.log(`No admin workspaces found for user ${uid}`);
          return {
            uid: uid,
            displayName: userInfo.displayName,
            email: userInfo.email,
            adminWorkspaces: [],
            aggregatedMetrics: {},
          };
        }

        // Process ALL admin workspaces and collect their data (optimized with rate limiting)
        const allAdminWorkspaces = [];
        let combinedAggregatedMetrics = {};
        let allStoreWorkspaces = [];

        // console.log(
        //   `Processing ${adminWorkspaceData.length} admin workspaces for rank data (user: ${uid})`
        // );

        // Process admin workspaces in parallel with rate limiting
        const batchProcessor = async (adminWorkspaceInfo) => {
          const { id: adminId, data: adminData } = adminWorkspaceInfo;

          try {
            // console.log(
            //   `Processing admin workspace: ${adminId} with brandName: ${adminData.brandName} for rank data`
            // );

            const metricsRef = collection(
              db,
              "users",
              uid,
              "fly_workspaces",
              adminId,
              "super_admin_metrices"
            );

            let fetchedDate,
              latestData,
              historicalData = {};

            if (specificDate) {
              // Fetch specific date
              const specificDocRef = doc(
                db,
                "users",
                uid,
                "fly_workspaces",
                adminId,
                "super_admin_metrices",
                specificDate
              );
              const specificDocSnap = await getDoc(specificDocRef);

              if (!specificDocSnap.exists()) {
                console.warn(
                  `No rank metrics data found for admin workspace: ${adminId} on date: ${specificDate}`
                );
                return null; // Skip this admin workspace
              }

              latestData = specificDocSnap.data();
              fetchedDate = specificDocSnap.id;

              if (fetchHistorical) {
                historicalData = await this.fetchHistoricalWeeks(
                  uid,
                  adminId,
                  specificDate,
                  12
                );
              }
            } else {
              // Get the latest date with data
              const latestQuery = query(
                metricsRef,
                orderBy("created_at", "desc"),
                limit(1)
              );
              const latestSnapshot = await getDocs(latestQuery);

              if (latestSnapshot.empty) {
                console.log(
                  `No rank metrics data found for admin workspace: ${adminId}`
                );
                return null; // Skip this admin workspace
              }

              const latestDoc = latestSnapshot.docs[0];
              latestData = latestDoc.data();
              fetchedDate = latestDoc.id;

              if (fetchHistorical) {
                historicalData = await this.fetchHistoricalWeeks(
                  uid,
                  adminId,
                  fetchedDate,
                  12
                );
              }
            }

            // console.log(
            //   `Found rank data for admin workspace ${adminId} on date: ${fetchedDate}`
            // );

            // Get aggregated metrics from this admin workspace
            const aggregatedMetrics = latestData.aggregated || {};

            // Get store-wise data for this admin workspace
            const storeWiseRef = collection(
              db,
              "users",
              uid,
              "fly_workspaces",
              adminId,
              "super_admin_metrices",
              fetchedDate,
              "store_wise"
            );
            const storeWiseQuery = query(
              storeWiseRef,
              orderBy("created_at", "desc"),
              limit(100)
            );
            const storeWiseSnapshot = await getDocs(storeWiseQuery);

            const adminStoreWorkspaces = [];
            storeWiseSnapshot.docs.forEach((doc) => {
              const storeData = doc.data();
              const wid = doc.id;

              // Get historical data for this store
              const storeHistoricalData = {};
              Object.keys(historicalData).forEach((date) => {
                if (
                  historicalData[date].stores &&
                  historicalData[date].stores[wid]
                ) {
                  storeHistoricalData[date] = historicalData[date].stores[wid];
                }
              });

              const storeWorkspace = {
                wid: wid,
                name: `Store ${wid}`,
                title: storeData.title || storeData.name || `Store ${wid}`,
                adminWorkspaceId: adminId, // Track which admin this belongs to
                // Include all metrics from the flattened structure
                posts: storeData.posts || 0,
                reviews: storeData.reviews || 0,
                rating: storeData.rating || null,
                rank: storeData.rank || null,
                total_keywords: storeData.total_keywords || 0,
                maps_impressions: storeData.maps_impressions || 0,
                search_impressions: storeData.search_impressions || 0,
                directions: storeData.directions || 0,
                call_clicks: storeData.call_clicks || 0,
                website_clicks: storeData.website_clicks || 0,
                bookings: storeData.bookings || 0,
                // Keep original data for compatibility
                ...storeData,
                allMetrics: this.extractAllMetricsFlattened(storeData),
                historicalData: storeHistoricalData,
                keywords: this.createMockKeywords(storeData),
              };

              adminStoreWorkspaces.push(storeWorkspace);
            });

            // Create admin workspace structure
            const adminWorkspace = {
              id: adminId,
              wid: adminId,
              name: adminData.brandName || adminData.name || `Admin ${adminId}`,
              title:
                adminData.brandName || adminData.name || `Admin ${adminId}`,
              brandName: adminData.brandName,
              adminWorkspaceId: adminId,
              storeWorkspaces: adminStoreWorkspaces,
              aggregatedMetrics: aggregatedMetrics,
              historicalData: Object.keys(historicalData).reduce(
                (acc, date) => {
                  acc[date] = historicalData[date].aggregated || {};
                  return acc;
                },
                {}
              ),
            };

            return {
              adminWorkspace,
              storeWorkspaces: adminStoreWorkspaces,
              aggregatedMetrics,
            };
          } catch (error) {
            console.error(
              `Error processing admin workspace ${adminId} for rank data:`,
              error
            );
            return null;
          }
        };

        // Process admin workspaces in parallel with rate limiting
        const results = await this.processBatch(
          adminWorkspaceData,
          this.maxConcurrentQueries || 3, // Use rate limiting
          batchProcessor
        );

        const validResults = results.filter((result) => result !== null);

        // Aggregate data from all admin workspaces
        validResults.forEach(
          ({ adminWorkspace, storeWorkspaces, aggregatedMetrics }) => {
            allAdminWorkspaces.push(adminWorkspace);
            allStoreWorkspaces.push(...storeWorkspaces);

            // Combine metrics (sum up numeric values across all admin workspaces)
            Object.keys(aggregatedMetrics).forEach((key) => {
              if (typeof aggregatedMetrics[key] === "number") {
                combinedAggregatedMetrics[key] =
                  (combinedAggregatedMetrics[key] || 0) +
                  aggregatedMetrics[key];
              } else if (
                aggregatedMetrics[key] !== null &&
                aggregatedMetrics[key] !== undefined
              ) {
                // For non-numeric values, take the latest value
                combinedAggregatedMetrics[key] = aggregatedMetrics[key];
              }
            });
          }
        );

        // console.log(
        //   `Processed ${allAdminWorkspaces.length} admin workspaces with ${allStoreWorkspaces.length} total stores for rank data (user: ${uid})`
        // );

        return {
          uid: uid,
          displayName: userInfo.displayName,
          email: userInfo.email,
          adminWorkspaces: allAdminWorkspaces, // Use all admin workspaces
          aggregatedMetrics: combinedAggregatedMetrics, // Use combined metrics from all admin workspaces
          totalDesktopImpressions:
            combinedAggregatedMetrics.maps_impressions || 0,
          totalMobileImpressions:
            combinedAggregatedMetrics.maps_impressions || 0,
          fetchedDate: specificDate || "latest", // Return the requested date or "latest"
          historicalData: {}, // Combined historical data would be complex, simplify for now
        };
      } catch (error) {
        console.error(
          `Error fetching flattened rank data for user ${uid}:`,
          error
        );
        return null;
      }
    },

    calculateOverallRankStats() {
      if (this.userRankData.length === 0) {
        this.overallRankStats = null;
        return;
      }

      let totalAdminWorkspaces = 0;
      let totalStoreWorkspaces = 0;
      let aggregatedMetrics = {};
      let totalRanks = 0;
      let workspaceCount = 0;

      this.userRankData.forEach((userRankData) => {
        const adminWorkspaces = userRankData.adminWorkspaces || [];
        totalAdminWorkspaces += adminWorkspaces.length;

        adminWorkspaces.forEach((adminWs) => {
          totalStoreWorkspaces += (adminWs.storeWorkspaces || []).length;

          (adminWs.storeWorkspaces || []).forEach((storeWs) => {
            if (storeWs.average_rank) {
              totalRanks += storeWs.average_rank;
              workspaceCount++;
            }
          });
        });

        Object.keys(userRankData.aggregatedMetrics || {}).forEach((key) => {
          if (key === "average_rank") {
            return;
          }
          aggregatedMetrics[key] =
            (aggregatedMetrics[key] || 0) +
            (userRankData.aggregatedMetrics[key] || 0);
        });
      });

      if (workspaceCount > 0) {
        aggregatedMetrics.average_rank = totalRanks / workspaceCount;
      }

      this.overallRankStats = {
        totalAdminWorkspaces,
        totalStoreWorkspaces,
        totalWorkspaces: totalAdminWorkspaces + totalStoreWorkspaces,
        aggregatedMetrics: aggregatedMetrics,
      };
    },

    // Posts Methods
    async fetchSelectedUsersPostsData() {
      if (this.selectedUsers.length === 0) return;

      this.loading = true;
      this.error = null;

      try {
        // check the store data first, if partial user data is available, then fetch the  other user data and merge both else if none exist then fetch the full data, else if all exist return the vuex store data
        // check which user data exists in vuex store contentTabData
        const userData = this.contentTabData[this.selectedDate] || {};
        // check which uid data exists in userData
        const userDataExists = this.selectedUsers.some((uid) => userData[uid]);
        let filteredOtherUserDataResults = [];
        if (userDataExists) {
          // fetch the other user data and merge both
          const otherUserData = this.selectedUsers.filter(
            (uid) => !userData[uid]
          );
          if (otherUserData) {
            const otherUserDataPromises = otherUserData?.map((uid) =>
              this.fetchUserPostsDataFlattened(uid, this.selectedDate, true)
            );
            const otherUserDataResults = await Promise.all(
              otherUserDataPromises
            );
            filteredOtherUserDataResults = otherUserDataResults.filter(
              (result) => result !== null
            );
          }
          // push each uid data from vuex store to mergedUserData

          let mergedUserData = [];
          // mergedUserData.push(userData);
          this.selectedUsers.forEach((uid) => {
            if (this.contentTabData[this.selectedDate][uid]) {
              mergedUserData.push(this.contentTabData[this.selectedDate][uid]);
            }
          });
          if (filteredOtherUserDataResults.length > 0) {
            mergedUserData.push(...filteredOtherUserDataResults);
            // mergedUserData = [
            //   ...userData,
            //   ...filteredOtherUserDataResults,
            // ];
          }
          this.$set(this, "userPostsData", mergedUserData);
        } else {
          // fetch the full data
          const userPostsPromises = this.selectedUsers.map((uid) =>
            this.fetchUserPostsDataFlattened(uid, this.selectedDate, true)
          );

          const batchSize = 5;
          const userPostsResults = [];

          for (let i = 0; i < userPostsPromises.length; i += batchSize) {
            const batch = userPostsPromises.slice(i, i + batchSize);
            const batchResults = await Promise.all(batch);
            userPostsResults.push(...batchResults);
          }

          const filteredResults = userPostsResults.filter(
            (result) => result !== null
          );
          // console.log("Posts data fetched:", filteredResults);

          // Use $set to ensure reactivity
          this.$set(this, "userPostsData", filteredResults);
        }
        this.calculateOverallPostsStats();
      } catch (error) {
        console.error("Error fetching selected users posts data:", error);
        this.error = "Failed to load posts data. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async fetchUserPostsDataFlattened(
      uid,
      specificDate = null,
      fetchHistorical = true
    ) {
      try {
        const userInfo = this.allUsers.find((u) => u.uid === uid);
        if (!userInfo) return null;

        // Get user's stats data first (for basic metrics)
        const userStatsData = await this.fetchUserStatsFlattened(
          uid,
          specificDate,
          fetchHistorical
        );
        if (!userStatsData) return null;

        // Calculate posts metrics for all admin workspaces of this user
        const postsMetricsResult = await this.calculateUserPostsMetrics(uid);
        const postValidationResults = await this.calculatePostValidationForUser(
          uid,
          specificDate
        );
        // console.log("🔍 POST VALIDATION DEBUG - User:", uid, "SelectedDate:", specificDate, "Results:", postValidationResults);

        // Calculate photos metrics for all admin workspaces of this user
        const photosMetricsResult = await this.calculateUserPhotosMetrics(uid);

        // Calculate verification metrics for all admin workspaces of this user
        const verificationMetricsResult =
          await this.calculateUserVerificationMetrics(uid, specificDate);

        if (!postsMetricsResult || !postsMetricsResult.adminWorkspaces) {
          return userStatsData; // Return original data if no posts data
        }

        const {
          adminWorkspaces: postsAdminWorkspaces,
          storeWorkspaces: postsStoreWorkspaces,
          storePostsData,
        } = postsMetricsResult;

        const { storePhotosData } = photosMetricsResult || {
          storePhotosData: {},
        };

        const { storeVerificationData } = verificationMetricsResult || {
          storeVerificationData: {},
        };

        // Merge post validation results into storeWorkspaces
        const mergePostValidation = (storeWorkspaces) => {
          return storeWorkspaces.map((storeWs) => {
            const validation = postValidationResults[storeWs.id] || {
              postValidation: "-",
              postValidationDetails: [],
            };
            // console.log("🔗 MERGING POST VALIDATION - Store:", storeWs.id, "Validation:", validation);
            return {
              ...storeWs,
              postValidation: validation.postValidation,
              postValidationDetails: validation.postValidationDetails,
            };
          });
        };

        // Create proper admin workspaces structure with posts data
        const adminWorkspaces = postsAdminWorkspaces.map((postsAdminWs) => {
          // Find matching admin workspace from userStatsData
          const existingAdminWs = userStatsData.adminWorkspaces.find(
            (adminWs) => adminWs.wid === postsAdminWs.id
          ) || {
            wid: postsAdminWs.id,
            title: postsAdminWs.title,
            aggregatedMetrics: {},
            storeWorkspaces: [],
          };

          // Get store workspaces for this admin
          const adminStoreWorkspaces = mergePostValidation(
            postsStoreWorkspaces.filter(
              (storeWs) => storeWs.adminId === postsAdminWs.id
            )
          ).map((storeWs) => {
            // Find existing store workspace data from userStatsData
            const existingStoreWs = existingAdminWs.storeWorkspaces.find(
              (sw) => sw.wid === storeWs.id
            ) || {
              wid: storeWs.id,
              title: storeWs.title,
              posts: 0,
            };

            // Get posts data for this store
            const storePostsInfo = storePostsData[storeWs.id] || {
              recentPost: null,
              recentPostDaysAgo: null,
              scheduledPosts: 0,
              scheduledPostsDetails: [],
              scheduledUpToDate: null,
            };

            // Get photos data for this store
            const storePhotosInfo = storePhotosData[storeWs.id] || {
              photos: 0,
              lastPhotoAdded: null,
              lastPhotoAddedDaysAgo: null,
            };

            // Get verification data for this store
            const storeVerificationInfo = storeVerificationData[storeWs.id] || {
              verificationStatus: "unknown",
              lastVerified: null,
              lastVerifiedDaysAgo: null,
              verificationHistoryCount: 0,
              verificationHistoryDetails: [],
              verificationReason: null,
              lastVerificationAttempt: null,
            };

            return {
              ...existingStoreWs,
              ...storeWs,
              ...storePostsInfo,
              ...storePhotosInfo,
              ...storeVerificationInfo,
              id: `store_${uid}_${postsAdminWs.id}_${storeWs.id}`,
            };
          });

          // Calculate admin-level aggregated posts metrics
          const adminPostsMetrics =
            this.aggregatePostsDataFromStores(adminStoreWorkspaces);

          // Calculate admin-level aggregated photos metrics
          const adminPhotosMetrics =
            this.aggregatePhotosDataFromStores(adminStoreWorkspaces);

          // Calculate admin-level aggregated verification metrics
          const adminVerificationMetrics =
            this.aggregateVerificationDataFromStores(adminStoreWorkspaces);

          return {
            ...existingAdminWs,
            id: `admin_${uid}_${postsAdminWs.id}`,
            aggregatedMetrics: {
              ...existingAdminWs.aggregatedMetrics,
              ...adminPostsMetrics,
              ...adminPhotosMetrics,
            },
            ...adminVerificationMetrics,
            storeWorkspaces: adminStoreWorkspaces,
            // Post Validation at admin level
            postValidationDetails: [],
          };
        });

        // Calculate user-level aggregated posts metrics from all admin workspaces
        const userPostsMetrics = this.aggregatePostsDataFromStores(
          adminWorkspaces.flatMap((admin) => admin.storeWorkspaces || [])
        );

        // Calculate user-level aggregated photos metrics from all admin workspaces
        const userPhotosMetrics = this.aggregatePhotosDataFromStores(
          adminWorkspaces.flatMap((admin) => admin.storeWorkspaces || [])
        );

        // Calculate user-level aggregated verification metrics from all admin workspaces
        const userVerificationMetrics =
          this.aggregateVerificationDataFromStores(
            adminWorkspaces.flatMap((admin) => admin.storeWorkspaces || [])
          );

        let contentData = {
          ...userStatsData,
          adminWorkspaces: adminWorkspaces,
          aggregatedMetrics: {
            ...userStatsData.aggregatedMetrics,
            ...userPostsMetrics,
            ...userPhotosMetrics,
          },
          ...userVerificationMetrics,
          // Post Validation at user level
          postValidationDetails: [],
        };

        if (
          contentData &&
          (!this.contentTabData[this.selectedDate] ||
            !this.contentTabData[this.selectedDate][uid])
        ) {
          this.$store.commit("SET_CONTENT_TAB_DATA", {
            data: contentData,
            date: this.selectedDate,
            uid,
          });
        }
        return contentData;
      } catch (error) {
        console.error(`Error fetching posts data for user ${uid}:`, error);
        return null;
      }
    },
    async calculateUserPostsMetrics(uid) {
      const metrics = {
        recentPost: null,
        recentPostDaysAgo: null,
        scheduledPosts: 0,
        scheduledPostsDetails: [],
        scheduledUpToDate: null,
      };

      try {
        // console.log(`📊 Calculating posts metrics for user: ${uid}`);

        // Get all workspaces for this user to identify admin and store workspaces
        let adminWorkspaces = [];
        let storeWorkspaces = [];

        try {
          const workspacesRef = collection(db, "users", uid, "fly_workspaces");
          const workspacesSnapshot = await getDocs(workspacesRef);

          workspacesSnapshot.forEach((wsDoc) => {
            const wsData = wsDoc.data();
            const wsId = wsDoc.id;

            // Admin workspaces have brandName field and no admin field (or admin is null/undefined)
            if (!wsData.admin || wsData.admin === null) {
              adminWorkspaces.push({
                id: wsId,
                data: wsData,
                brandName: wsData.brandName,
                title: wsData.name || wsData.brandName,
              });
            }
            // Store workspaces have admin field pointing to an admin workspace
            else if (wsData.admin) {
              storeWorkspaces.push({
                id: wsId,
                data: wsData,
                adminId: wsData.admin,
                title: wsData.name || `Store ${wsId}`,
              });
            }
          });
        } catch (error) {
          console.warn(
            "Error getting workspaces for posts calculation:",
            error
          );
        }

        console.log(
          `Found ${adminWorkspaces.length} admin workspaces and ${storeWorkspaces.length} store workspaces for user ${uid}`
        );

        // Now get posts data for each admin workspace and associate with store workspaces
        let foundRecentPost = false;
        let allScheduledPosts = [];
        let storePostsData = {}; // Store posts data by store workspace ID

        for (const adminWs of adminWorkspaces) {
          try {
            // console.log(
            //   `Processing admin workspace: ${adminWs.brandName} (${adminWs.id})`
            // );

            const postsRef = collection(
              db,
              "users",
              uid,
              "fly_workspaces",
              adminWs.id,
              "social-posts"
            );

            // Get all posts for this admin workspace
            try {
              const allPostsQuery = query(
                postsRef,
                orderBy("created_at", "desc")
              );
              const allPostsSnapshot = await getDocs(allPostsQuery);

              console.log(
                `Found ${allPostsSnapshot.size} posts in admin workspace ${adminWs.id}`
              );

              allPostsSnapshot.docs.forEach((doc) => {
                const postData = doc.data();
                const postId = doc.id;
                const locations = postData.locations || [];

                // Process published posts for recent post calculation
                if (postData.post_status === "published" && !foundRecentPost) {
                  const postTime = postData.posted_time || postData.created_at;
                  if (postTime) {
                    metrics.recentPost = postTime;
                    const postDate = postTime.toDate
                      ? postTime.toDate()
                      : new Date(postTime);
                    const now = new Date();
                    const diffTime = Math.abs(now - postDate);
                    const diffDays = Math.ceil(
                      diffTime / (1000 * 60 * 60 * 24)
                    );
                    metrics.recentPostDaysAgo = diffDays;
                    foundRecentPost = true;
                    // console.log(`Found recent post: ${diffDays} days ago`);
                  }
                }

                // Process scheduled posts
                if (
                  postData.when_to_post === "schedule" &&
                  postData.scheduled_at
                ) {
                  const scheduledTime = postData.scheduled_at;
                  const scheduledDate = scheduledTime.toDate
                    ? scheduledTime.toDate()
                    : new Date(scheduledTime);
                  const now = new Date();

                  if (scheduledDate > now) {
                    const scheduledPostItem = {
                      scheduledTime: scheduledDate,
                      platforms: postData.social_media || [],
                      text: postData.text
                        ? postData.text.substring(0, 50) + "..."
                        : "No content",
                      locations: locations,
                    };
                    allScheduledPosts.push(scheduledPostItem);
                  }
                }

                // Associate posts with store workspaces based on locations field
                locations.forEach((locationId) => {
                  if (!storePostsData[locationId]) {
                    storePostsData[locationId] = {
                      recentPost: null,
                      recentPostDaysAgo: null,
                      scheduledPosts: 0,
                      scheduledPostsDetails: [],
                      scheduledUpToDate: null,
                      allPosts: [],
                    };
                  }

                  storePostsData[locationId].allPosts.push({
                    id: postId,
                    data: postData,
                  });

                  // Update recent post for this store if this post is more recent
                  if (postData.post_status === "published") {
                    const postTime =
                      postData.posted_time || postData.created_at;
                    if (postTime) {
                      const postDate = postTime.toDate
                        ? postTime.toDate()
                        : new Date(postTime);
                      const existingPostDate = storePostsData[locationId]
                        .recentPost
                        ? storePostsData[locationId].recentPost.toDate
                          ? storePostsData[locationId].recentPost.toDate()
                          : new Date(storePostsData[locationId].recentPost)
                        : null;

                      if (!existingPostDate || postDate > existingPostDate) {
                        storePostsData[locationId].recentPost = postTime;
                        const now = new Date();
                        const diffTime = Math.abs(now - postDate);
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        );
                        storePostsData[locationId].recentPostDaysAgo = diffDays;
                      }
                    }
                  }

                  // Update scheduled posts for this store
                  if (
                    postData.when_to_post === "schedule" &&
                    postData.scheduled_at
                  ) {
                    const scheduledTime = postData.scheduled_at;
                    const scheduledDate = scheduledTime.toDate
                      ? scheduledTime.toDate()
                      : new Date(scheduledTime);
                    const now = new Date();

                    if (scheduledDate > now) {
                      storePostsData[locationId].scheduledPosts++;

                      const dateKey = this.formatDateForDisplay(scheduledDate);
                      let existingDateGroup = storePostsData[
                        locationId
                      ].scheduledPostsDetails.find(
                        (group) => group.date === dateKey
                      );

                      if (!existingDateGroup) {
                        existingDateGroup = {
                          date: dateKey,
                          posts: [],
                          count: 0,
                        };
                        storePostsData[locationId].scheduledPostsDetails.push(
                          existingDateGroup
                        );
                      }

                      existingDateGroup.posts.push({
                        scheduledTime: scheduledDate,
                        platforms: postData.social_media || [],
                        text: postData.text
                          ? postData.text.substring(0, 50) + "..."
                          : "No content",
                      });
                      existingDateGroup.count++;

                      // Update scheduled up to date
                      if (
                        !storePostsData[locationId].scheduledUpToDate ||
                        scheduledDate >
                          storePostsData[locationId].scheduledUpToDate
                      ) {
                        storePostsData[locationId].scheduledUpToDate =
                          scheduledDate;
                      }
                    }
                  }
                });
              });
            } catch (error) {
              console.warn(
                `Error querying posts in admin workspace ${adminWs.id}:`,
                error
              );
            }
          } catch (error) {
            console.warn(
              `Error processing admin workspace ${adminWs.id}:`,
              error
            );
          }
        }

        // console.log(`Store posts data:`, storePostsData);

        // Process scheduled posts
        if (allScheduledPosts.length > 0) {
          metrics.scheduledPosts = allScheduledPosts.length;

          // Group by date
          const scheduledPostsByDate = new Map();
          let latestScheduledDate = null;

          allScheduledPosts.forEach((post) => {
            const dateKey = this.formatDateForDisplay(post.scheduledTime);
            if (!scheduledPostsByDate.has(dateKey)) {
              scheduledPostsByDate.set(dateKey, []);
            }
            scheduledPostsByDate.get(dateKey).push(post);

            if (
              !latestScheduledDate ||
              post.scheduledTime > latestScheduledDate
            ) {
              latestScheduledDate = post.scheduledTime;
            }
          });

          metrics.scheduledPostsDetails = Array.from(
            scheduledPostsByDate.entries()
          ).map(([date, posts]) => ({
            date,
            posts,
            count: posts.length,
          }));
          metrics.scheduledUpToDate = latestScheduledDate;
        }

        // console.log(`✅ Posts metrics calculated for user ${uid}:`, metrics);

        // Return metrics along with admin and store structure for proper aggregation
        return {
          ...metrics,
          adminWorkspaces: adminWorkspaces,
          storeWorkspaces: storeWorkspaces,
          storePostsData: storePostsData,
        };
      } catch (error) {
        console.warn(`Error calculating posts metrics for user ${uid}:`, error);
        return metrics;
      }
    },

    aggregatePostsDataFromStores(storeWorkspaces) {
      // Aggregate posts data from store workspaces to admin level
      const aggregated = {
        posts: 0,
        recentPost: null,
        recentPostDaysAgo: null,
        scheduledPosts: 0,
        scheduledPostsDetails: [],
        scheduledUpToDate: null,
      };

      let mostRecentPost = null;
      let allScheduledPosts = [];

      storeWorkspaces.forEach((store) => {
        aggregated.posts += store.posts || 0;

        // Find most recent post
        if (store.recentPost) {
          const storePostDate = store.recentPost.toDate
            ? store.recentPost.toDate()
            : new Date(store.recentPost);
          if (!mostRecentPost || storePostDate > mostRecentPost) {
            mostRecentPost = storePostDate;
            aggregated.recentPost = store.recentPost;
            aggregated.recentPostDaysAgo = store.recentPostDaysAgo;
          }
        }

        // Aggregate scheduled posts
        aggregated.scheduledPosts += store.scheduledPosts || 0;
        if (
          store.scheduledPostsDetails &&
          store.scheduledPostsDetails.length > 0
        ) {
          allScheduledPosts.push(...store.scheduledPostsDetails);
        }

        // Update latest scheduled date
        if (store.scheduledUpToDate) {
          const storeScheduledDate = store.scheduledUpToDate.toDate
            ? store.scheduledUpToDate.toDate()
            : new Date(store.scheduledUpToDate);
          const currentLatestDate = aggregated.scheduledUpToDate
            ? aggregated.scheduledUpToDate.toDate
              ? aggregated.scheduledUpToDate.toDate()
              : new Date(aggregated.scheduledUpToDate)
            : null;

          if (!currentLatestDate || storeScheduledDate > currentLatestDate) {
            aggregated.scheduledUpToDate = store.scheduledUpToDate;
          }
        }
      });

      // Merge scheduled posts details by date
      const mergedScheduledDetails = new Map();
      allScheduledPosts.forEach((group) => {
        if (mergedScheduledDetails.has(group.date)) {
          const existing = mergedScheduledDetails.get(group.date);
          existing.posts.push(...group.posts);
          existing.count += group.count;
        } else {
          mergedScheduledDetails.set(group.date, {
            date: group.date,
            posts: [...group.posts],
            count: group.count,
          });
        }
      });

      aggregated.scheduledPostsDetails = Array.from(
        mergedScheduledDetails.values()
      ).sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first

      return aggregated;
    },

    aggregatePostsData(storeWorkspaces) {
      // Legacy method - kept for compatibility
      return this.aggregatePostsDataFromStores(storeWorkspaces);
    },

    async calculatePostValidationForUser(uid, selectedDate) {
      // Returns: { [storeId]: { postValidation: "Valid"/"Invalid", postValidationDetails: [ { week, count, valid } ] } }
      const result = {};
      try {
        // Get all admin workspaces for user in a single query
        const adminSnap = await getDocs(
          collection(db, "users", uid, "fly_workspaces")
        );
        const adminIds = [];
        adminSnap.forEach((doc) => {
          const data = doc.data();
          if (!data.admin) adminIds.push(doc.id);
        });

        if (adminIds.length === 0) {
          // console.log("No admin workspaces found for user", uid);
          return result;
        }
        // console.log("Found admin workspaces for user", uid, ":", adminIds);

        // Process admin workspaces in parallel with limited concurrency
        const BATCH_SIZE = 3; // Process 3 admin workspaces at a time to avoid overwhelming Firestore
        for (let i = 0; i < adminIds.length; i += BATCH_SIZE) {
          const batch = adminIds.slice(i, i + BATCH_SIZE);
          await Promise.all(
            batch.map(async (adminId) => {
              try {
                // Get only last 5 weeks for performance (instead of 15)
                const metricsRef = collection(
                  db,
                  "users",
                  uid,
                  "fly_workspaces",
                  adminId,
                  "super_admin_metrices"
                );
                const metricsQuery = query(
                  metricsRef,
                  orderBy("created_at", "desc"),
                  limit(10) // Reduced from 15 to 5 for better performance
                );
                const metricsSnap = await getDocs(metricsQuery);

                // Process weeks in parallel with rate limiting
                const weekDocs = metricsSnap.docs;
                const WEEK_BATCH_SIZE = 2; // Process 2 weeks at a time
                for (let j = 0; j < weekDocs.length; j += WEEK_BATCH_SIZE) {
                  const weekBatch = weekDocs.slice(j, j + WEEK_BATCH_SIZE);
                  await Promise.all(
                    weekBatch.map(async (weekDoc) => {
                      try {
                        const weekId = weekDoc.id;
                        const isSelectedWeek = weekId === selectedDate;
                        // console.log(`Checking week ${weekId} against selectedDate ${selectedDate}, match: ${isSelectedWeek}`);

                        // Get store_wise subcollection for this week
                        const storeWiseRef = collection(
                          metricsRef,
                          weekId,
                          "store_wise"
                        );
                        const storeWiseSnap = await getDocs(storeWiseRef);

                        // Process all stores for this week
                        storeWiseSnap.forEach((storeDoc) => {
                          const storeId = storeDoc.id;
                          const storeData = storeDoc.data();

                          // Count published posts for this week
                          const postsCount = storeData.posts || 0;
                          const valid = postsCount >= 3;

                          // console.log(`Store ${storeId}, Week ${weekId}, Posts: ${postsCount}, Valid: ${valid}, IsSelectedWeek: ${isSelectedWeek}`);

                          // Initialize store data if not exists
                          if (!result[storeId]) {
                            result[storeId] = {
                              postValidation: "-",
                              postValidationDetails: [],
                            };
                          }

                          // Add week data
                          result[storeId].postValidationDetails.push({
                            week: weekId,
                            count: postsCount,
                            valid,
                          });

                          // Set current validation status for selected week
                          if (isSelectedWeek) {
                            result[storeId].postValidation = valid
                              ? "Valid"
                              : "Invalid";
                            // console.log(`Setting validation for store ${storeId} to: ${result[storeId].postValidation}`);
                          }
                        });
                      } catch (weekError) {
                        console.warn(
                          `Error processing week ${weekDoc.id} for admin ${adminId}:`,
                          weekError
                        );
                      }
                    })
                  );
                }
              } catch (adminError) {
                console.warn(
                  `Error processing admin workspace ${adminId}:`,
                  adminError
                );
              }
            })
          );
        }

        // Sort postValidationDetails by week (newest first) for all stores
        Object.keys(result).forEach((storeId) => {
          result[storeId].postValidationDetails.sort((a, b) => {
            return new Date(b.week) - new Date(a.week);
          });
          // If no specific week was found matching selectedDate, use the latest week's data
          if (
            result[storeId].postValidation === "-" &&
            result[storeId].postValidationDetails.length > 0
          ) {
            const latestWeek = result[storeId].postValidationDetails[0];
            result[storeId].postValidation = latestWeek.valid
              ? "Valid"
              : "Invalid";
            // console.log(`🚨 FALLBACK USED - Store ${storeId} had no matching week for ${selectedDate}, using latest week: ${result[storeId].postValidation}`);
          }
        });
      } catch (error) {
        console.error("Error in calculatePostValidationForUser:", error);
      }
      return result;
    },

    // Verification History Methods
    async calculateUserVerificationMetrics(uid, selectedDate = null) {
      // console.log(
      //   `🔍 Calculating verification metrics for user: ${uid}, selectedDate: ${selectedDate}`
      // );

      try {
        // Get all workspaces for this user to identify admin and store workspaces
        let adminWorkspaces = [];
        let storeWorkspaces = [];
        let storeVerificationData = {}; // Store verification data by store workspace ID

        const workspacesRef = collection(db, "users", uid, "fly_workspaces");
        const workspacesSnapshot = await getDocs(workspacesRef);

        workspacesSnapshot.forEach((wsDoc) => {
          const wsData = wsDoc.data();
          const wsId = wsDoc.id;

          if (!wsData.admin || wsData.admin === null) {
            // This is an admin workspace
            adminWorkspaces.push({
              id: wsId,
              data: wsData,
              brandName: wsData.brandName,
              title: wsData.name || wsData.brandName,
            });
          } else if (wsData.admin) {
            // This is a store workspace
            storeWorkspaces.push({
              id: wsId,
              data: wsData,
              adminId: wsData.admin,
              title: wsData.name || `Store ${wsId}`,
            });
          }
        });

        console.log(
          `Found ${storeWorkspaces.length} store workspaces for verification processing`
        );

        // Process each store workspace to get verification history
        for (const storeWs of storeWorkspaces) {
          try {
            const verificationData =
              await this.fetchWorkspaceVerificationHistory(
                uid,
                storeWs.id,
                selectedDate
              );
            if (verificationData) {
              storeVerificationData[storeWs.id] = verificationData;
            }
          } catch (error) {
            console.warn(
              `Error fetching verification data for store ${storeWs.id}:`,
              error
            );
            // Set default values for failed fetches
            storeVerificationData[storeWs.id] = {
              verificationStatus: "unknown",
              lastVerified: null,
              lastVerifiedDaysAgo: null,
              verificationHistoryCount: 0,
              verificationHistoryDetails: [],
              verificationReason: null,
              lastVerificationAttempt: null,
            };
          }
        }

        // console.log(`✅ Verification metrics calculated for user ${uid}:`, {
        //   storeWorkspacesCount: storeWorkspaces.length,
        //   storeVerificationDataKeys: Object.keys(storeVerificationData),
        //   sampleVerificationData: Object.values(storeVerificationData)[0],
        // });

        return {
          adminWorkspaces: adminWorkspaces,
          storeWorkspaces: storeWorkspaces,
          storeVerificationData: storeVerificationData,
        };
      } catch (error) {
        console.warn(
          `Error calculating verification metrics for user ${uid}:`,
          error
        );
        return {
          adminWorkspaces: [],
          storeWorkspaces: [],
          storeVerificationData: {},
        };
      }
    },

    async fetchWorkspaceVerificationHistory(
      uid,
      workspaceId,
      selectedDate = null
    ) {
      try {
        // This is a flexible approach that can be adapted based on your Firestore structure
        // Common patterns for verification history storage:

        // Option 1: Verification history as a subcollection
        const verificationHistoryRef = collection(
          db,
          "users",
          uid,
          "fly_workspaces",
          workspaceId,
          "verification_history"
        );

        // Option 2: Alternative path - you can modify this based on actual structure
        // const verificationHistoryRef = collection(db, "verification_history", uid, "workspaces", workspaceId, "attempts");
        // Option 3: Another alternative - centralized verification collection
        // const verificationHistoryRef = collection(db, "verification_logs");
        // const verificationQuery = query(verificationHistoryRef, where("userId", "==", uid), where("workspaceId", "==", workspaceId));

        // Verification history is independent of selected date
        // Each document has its own verification_date

        // Get all verification history documents (date-based documents yyyy-mm-dd)
        // Don't filter by selected date since verification history has its own dates
        const verificationSnapshot = await getDocs(verificationHistoryRef);
        
        console.log(
          `🔍 Fetching all verification history for workspace: ${workspaceId}, found ${verificationSnapshot.size} documents`
        );

        // console.log(
        //   `📊 Verification query result for workspace ${workspaceId}:`,
        //   {
        //     empty: verificationSnapshot.empty,
        //     docCount: verificationSnapshot.docs.length,
        //     docs: verificationSnapshot.docs.map((doc) => ({
        //       id: doc.id,
        //       created_at: doc.data().created_at,
        //       updated_at: doc.data().updated_at,
        //       status: doc.data().status,
        //       verified: doc.data().verified,
        //     })),
        //   }
        // );

        if (verificationSnapshot.empty) {
          // console.log(
          //   `No verification history found for workspace ${workspaceId}`
          // );
          return {
            verificationStatus: "unknown",
            lastVerified: null,
            lastVerifiedDaysAgo: null,
            verificationHistoryCount: 0,
            verificationHistoryDetails: [],
            verificationReason: null,
            lastVerificationAttempt: null,
          };
        }

        const verificationAttempts = [];
        let latestVerifiedDate = null;
        let latestVerifiedDaysAgo = null;
        let fallbackStatus = "unknown";
        let fallbackReason = null;
        let latestAttemptDate = null;

        verificationSnapshot.docs.forEach((doc) => {
          const attemptData = doc.data();
          const documentId = doc.id; // This is the date (e.g., "2025-08-14")

          // Use verification_date field as the primary date source
          let finalTimestamp = null;
          if (attemptData.verification_date) {
            // Handle Firebase Timestamp or regular date
            if (attemptData.verification_date.seconds) {
              finalTimestamp = new Date(attemptData.verification_date.seconds * 1000);
            } else if (typeof attemptData.verification_date === 'string') {
              finalTimestamp = new Date(attemptData.verification_date);
            } else {
              finalTimestamp = attemptData.verification_date;
            }
          } else if (documentId && /^\d{4}-\d{2}-\d{2}$/.test(documentId)) {
            // Fallback to document ID if it's a valid date format (yyyy-mm-dd)
            finalTimestamp = new Date(documentId);
          } else if (attemptData.created_at) {
            finalTimestamp = attemptData.created_at;
          } else if (attemptData.updated_at) {
            finalTimestamp = attemptData.updated_at;
          }
          // console.log(
          //   `Processing verification attempt ${documentId} with fallbackTimestamp: ${fallbackTimestamp} and timestamp: ${timestamp}`
          // );

          if (finalTimestamp) {
            const attemptDate = finalTimestamp.toDate
              ? finalTimestamp.toDate()
              : new Date(finalTimestamp);

            // Determine status - use multiple indicators from your data structure
            let attemptStatus = "unknown";
            if (attemptData.status) {
              attemptStatus = attemptData.status;
            } else if (attemptData.verified === true) {
              attemptStatus = "verified";
            } else if (attemptData.verified === false) {
              attemptStatus = "unverified";
            }

            // Create reason from available fields in your structure
            let reason = null;
            if (!attemptData.hasBusinessAuthority) {
              reason = "Missing business authority";
            } else if (!attemptData.hasVoiceOfMerchant) {
              reason = "Missing voice of merchant";
            } else if (
              attemptData.status &&
              attemptData.status !== "verified"
            ) {
              reason = `Status: ${attemptData.status}`;
            }

            verificationAttempts.push({
              id: `${documentId}_${
                finalTimestamp.toDate
                  ? finalTimestamp.toDate().getTime()
                  : new Date(finalTimestamp).getTime()
              }`,
              status: attemptStatus,
              timestamp: finalTimestamp,
              verification_date: finalTimestamp, // For sorting in getMostRecentVerificationStatus
              reason: reason,
              verified: attemptData.verified,
              date: documentId,
              uid: attemptData.uid,
              wid: attemptData.wid,
            });

            // Track latest attempt for fallback
            if (!latestAttemptDate || attemptDate > latestAttemptDate) {
              latestAttemptDate = attemptDate;
              fallbackStatus = attemptStatus;
              fallbackReason = reason;
            }

            // Track latest successful verification
            if (
              (attemptData.status === "verified" ||
                attemptData.verified === true) &&
              (!latestVerifiedDate || attemptDate > latestVerifiedDate)
            ) {
              latestVerifiedDate = attemptDate;
              const now = new Date();
              const diffTime = Math.abs(now - attemptDate);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              latestVerifiedDaysAgo = diffDays;
            }
          }
        });

        // Sort attempts by timestamp (newest first) - using verification_date
        verificationAttempts.sort((a, b) => {
          const dateA = a.timestamp.toDate
            ? a.timestamp.toDate()
            : new Date(a.timestamp);
          const dateB = b.timestamp.toDate
            ? b.timestamp.toDate()
            : new Date(b.timestamp);
          return dateB - dateA;
        });
        
        // Limit to most recent 12 attempts for display in UI
        const displayAttempts = verificationAttempts.slice(0, 12);

        // console.log(
        //   `📋 Processed ${verificationAttempts.length} verification attempts for workspace ${workspaceId}:`,
        //   verificationAttempts.map((attempt) => ({
        //     id: attempt.id,
        //     date: attempt.date,
        //     formattedDateTime: this.formatDateTimeForDisplay(attempt.timestamp),
        //     status: attempt.status,
        //     reason: attempt.reason,
        //     originalCreatedAt: attempt.created_at,
        //     originalUpdatedAt: attempt.updated_at,
        //   }))
        // );

        // Use the most recent verification status, not filtered by selected date
        // Since verification history has its own dates independent of selected date
        const finalStatus = fallbackStatus;
        const finalReason = fallbackReason;

        const result = {
          verificationStatus: finalStatus,
          lastVerified: latestVerifiedDate ? latestVerifiedDate : null,
          lastVerifiedDaysAgo: latestVerifiedDaysAgo,
          verificationHistoryCount: verificationAttempts.length, // Total count of all attempts
          verificationHistoryDetails: displayAttempts, // Limited to recent 12 for display
          verificationReason: finalReason,
          lastVerificationAttempt: latestAttemptDate ? latestAttemptDate : null
        };

        // console.log(
        //   `✅ Verification data processed for workspace ${workspaceId}:`,
        //   {
        //     selectedDate: selectedDate,
        //     selectedDateStatus: currentDateStatus,
        //     finalStatus: finalStatus,
        //     historyCount: result.verificationHistoryCount,
        //     hasDetails: result.verificationHistoryDetails.length > 0,
        //   }
        // );
        return result;
      } catch (error) {
        console.error(
          `Error fetching verification history for workspace ${workspaceId}:`,
          error
        );
        return {
          verificationStatus: "error",
          lastVerified: null,
          lastVerifiedDaysAgo: null,
          verificationHistoryCount: 0,
          verificationHistoryDetails: [],
          verificationReason: "Failed to fetch verification data",
          lastVerificationAttempt: null,
          selectedDateStatus: "error",
          selectedDateData: null,
        };
      }
    },

    aggregateVerificationDataFromStores(storeWorkspaces) {
      // Aggregate verification data from store workspaces to admin level
      const aggregated = {
        verificationStatus: "unknown",
        lastVerified: null,
        lastVerifiedDaysAgo: null,
        verificationHistoryCount: 0,
        verificationHistoryDetails: [],
        verificationReason: null,
        lastVerificationAttempt: null,
      };

      let mostRecentVerification = null;
      let mostRecentAttempt = null;
      let allHistoryDetails = [];
      let unverifiedCount = 0;
      let verifiedCount = 0;
      let errorCount = 0;

      storeWorkspaces.forEach((store) => {
        // Count verification status types
        if (store.verificationStatus === "verified") {
          verifiedCount++;
        } else if (
          store.verificationStatus === "unverified" ||
          store.verificationStatus === "failed"
        ) {
          unverifiedCount++;
        } else if (store.verificationStatus === "error") {
          errorCount++;
        }

        // Find most recent verification
        if (store.lastVerified) {
          const storeVerifiedDate = store.lastVerified.toDate
            ? store.lastVerified.toDate()
            : new Date(store.lastVerified);
          if (
            !mostRecentVerification ||
            storeVerifiedDate > mostRecentVerification
          ) {
            mostRecentVerification = storeVerifiedDate;
            aggregated.lastVerified = store.lastVerified;
            aggregated.lastVerifiedDaysAgo = store.lastVerifiedDaysAgo;
          }
        }

        // Find most recent attempt
        if (store.lastVerificationAttempt) {
          const storeAttemptDate = store.lastVerificationAttempt.toDate
            ? store.lastVerificationAttempt.toDate()
            : new Date(store.lastVerificationAttempt);
          if (!mostRecentAttempt || storeAttemptDate > mostRecentAttempt) {
            mostRecentAttempt = storeAttemptDate;
            aggregated.lastVerificationAttempt = store.lastVerificationAttempt;
            aggregated.verificationReason = store.verificationReason;
          }
        }

        // Don't aggregate history count - we'll calculate it after deduplication

        // Collect history details for deduplication
        if (
          store.verificationHistoryDetails &&
          store.verificationHistoryDetails.length > 0
        ) {
          allHistoryDetails.push(
            ...store.verificationHistoryDetails.map((detail) => ({
              ...detail,
              workspaceId: store.wid,
              workspaceName: store.title || store.name,
            }))
          );
        }
      });

      // Determine overall status based on store statuses
      if (verifiedCount === storeWorkspaces.length) {
        aggregated.verificationStatus = "verified";
      } else if (unverifiedCount > 0 || errorCount > 0) {
        aggregated.verificationStatus = "unverified";
      } else if (verifiedCount > 0) {
        aggregated.verificationStatus = "partial";
      }

      // Deduplicate verification attempts by date/timestamp
      const uniqueHistoryMap = new Map();

      allHistoryDetails.forEach((detail) => {
        // Create a unique key based on date and timestamp to avoid duplicates
        const dateKey = detail.date || detail.id;
        const timestampKey = detail.timestamp
          ? detail.timestamp.toDate
            ? detail.timestamp.toDate().getTime()
            : new Date(detail.timestamp).getTime()
          : dateKey;
        const uniqueKey = `${dateKey}_${timestampKey}`;

        // Only add if we haven't seen this exact verification attempt before
        if (!uniqueHistoryMap.has(uniqueKey)) {
          uniqueHistoryMap.set(uniqueKey, {
            ...detail,
            affectedWorkspaces: [detail.workspaceId], // Track which workspaces this affects
          });
        } else {
          // If we've seen this attempt before, add this workspace to the affected list
          const existing = uniqueHistoryMap.get(uniqueKey);
          if (!existing.affectedWorkspaces.includes(detail.workspaceId)) {
            existing.affectedWorkspaces.push(detail.workspaceId);
          }
        }
      });

      // Convert deduplicated map back to array and sort
      const deduplicatedHistory = Array.from(uniqueHistoryMap.values());
      deduplicatedHistory.sort((a, b) => {
        const dateA = a.timestamp.toDate
          ? a.timestamp.toDate()
          : new Date(a.timestamp);
        const dateB = b.timestamp.toDate
          ? b.timestamp.toDate()
          : new Date(b.timestamp);
        return dateB - dateA;
      });

      aggregated.verificationHistoryCount = deduplicatedHistory.length;
      aggregated.verificationHistoryDetails = deduplicatedHistory.slice(0, 30); // Limit for performance

      // console.log(
      //   `🔄 Verification aggregation complete - Original: ${allHistoryDetails.length} attempts, Deduplicated: ${aggregated.verificationHistoryCount}`
      // );

      return aggregated;
    },

    async calculateUserPhotosMetrics(uid) {
      const metrics = {
        photos: 0,
        lastPhotoAdded: null,
        lastPhotoAddedDaysAgo: null,
      };

      try {
        // console.log(`📸 Calculating photos metrics for user: ${uid}`);

        // Get all workspaces for this user to identify admin and store workspaces
        let adminWorkspaces = [];
        let storeWorkspaces = [];

        try {
          const workspacesRef = collection(db, "users", uid, "fly_workspaces");
          const workspacesSnapshot = await getDocs(workspacesRef);

          workspacesSnapshot.forEach((wsDoc) => {
            const wsData = wsDoc.data();
            const wsId = wsDoc.id;

            // Admin workspaces have brandName field and no admin field (or admin is null/undefined)
            if (!wsData.admin || wsData.admin === null) {
              adminWorkspaces.push({
                id: wsId,
                data: wsData,
                brandName: wsData.brandName,
                title: wsData.name || wsData.brandName,
              });
            }
            // Store workspaces have admin field pointing to an admin workspace
            else if (wsData.admin) {
              storeWorkspaces.push({
                id: wsId,
                data: wsData,
                adminId: wsData.admin,
                title: wsData.name || `Store ${wsId}`,
              });
            }
          });
        } catch (error) {
          console.warn(
            "Error getting workspaces for photos calculation:",
            error
          );
        }

        // console.log(
        //   `Found ${adminWorkspaces.length} admin workspaces and ${storeWorkspaces.length} store workspaces for user ${uid}`
        // );

        // Store photos data by store workspace ID
        let storePhotosData = {};
        let latestPhotoDate = null;

        // Process each store workspace to get photos (photos are stored in store workspaces, not admin)
        for (const storeWs of storeWorkspaces) {
          try {
            // console.log(
            //   `Processing store workspace for photos: ${storeWs.title} (${storeWs.id})`
            // );

            // Get photos from the specific "photo" document in photo_gallery subcollection
            const photoDocRef = doc(
              db,
              "users",
              uid,
              "fly_workspaces",
              storeWs.id,
              "photo_gallery",
              "photo"
            );

            try {
              const photoDocSnapshot = await getDoc(photoDocRef);

              if (photoDocSnapshot.exists()) {
                const photoDocData = photoDocSnapshot.data();
                // console.log(
                //   `Found photo document in store workspace ${storeWs.id}:`,
                //   Object.keys(photoDocData)
                // );

                // Each photo document has a mediaItems array (note: capital I)
                const mediaItems = photoDocData.mediaItems || [];

                // console.log(
                //   `Processing ${mediaItems.length} media items for store ${storeWs.id}`
                // );

                // Initialize store photos data if not exists
                if (!storePhotosData[storeWs.id]) {
                  storePhotosData[storeWs.id] = {
                    photos: 0,
                    lastPhotoAdded: null,
                    lastPhotoAddedDaysAgo: null,
                  };
                }

                mediaItems.forEach((mediaItem) => {
                  const createTime = mediaItem.createTime;

                  if (createTime) {
                    const photoDate = new Date(createTime);

                    // Skip invalid dates (like 1970-01-01 which seems to be default)
                    // Accept dates from 2020 onwards (inclusive of future dates that might be incorrect but exist)
                    if (photoDate.getFullYear() >= 2020) {
                      // Update latest photo date globally
                      if (!latestPhotoDate || photoDate > latestPhotoDate) {
                        latestPhotoDate = photoDate;
                        metrics.lastPhotoAdded = createTime;
                        const now = new Date();
                        const diffTime = Math.abs(now - photoDate);
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        );
                        metrics.lastPhotoAddedDaysAgo = diffDays;
                      }

                      // Count photo for this store workspace
                      storePhotosData[storeWs.id].photos++;

                      // Update latest photo for this store workspace if this photo is more recent
                      const existingPhotoDate = storePhotosData[storeWs.id]
                        .lastPhotoAdded
                        ? new Date(storePhotosData[storeWs.id].lastPhotoAdded)
                        : null;

                      if (!existingPhotoDate || photoDate > existingPhotoDate) {
                        storePhotosData[storeWs.id].lastPhotoAdded = createTime;
                        const now = new Date();
                        const diffTime = Math.abs(now - photoDate);
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        );
                        storePhotosData[storeWs.id].lastPhotoAddedDaysAgo =
                          diffDays;
                      }
                    }
                  }
                });

                // Count total photos for user-level metrics (only photos with valid dates)
                const validPhotos = mediaItems.filter((item) => {
                  const createTime = item.createTime;
                  if (createTime) {
                    const photoDate = new Date(createTime);
                    return photoDate.getFullYear() >= 2020;
                  }
                  return false;
                });
                metrics.photos += validPhotos.length;

                // console.log(
                //   `Store ${storeWs.id} has ${
                //     storePhotosData[storeWs.id].photos
                //   } photos`
                // );
              } else {
                // console.log(
                //   `No photo document found in store workspace ${storeWs.id}`
                // );
              }
            } catch (error) {
              console.warn(
                `Error querying photos in store workspace ${storeWs.id}:`,
                error
              );
            }
          } catch (error) {
            console.warn(
              `Error processing store workspace ${storeWs.id} for photos:`,
              error
            );
          }
        }

        // console.log(`✅ Photos metrics calculated for user ${uid}:`, metrics);

        // Return metrics along with admin and store structure for proper aggregation
        return {
          ...metrics,
          adminWorkspaces: adminWorkspaces,
          storeWorkspaces: storeWorkspaces,
          storePhotosData: storePhotosData,
        };
      } catch (error) {
        console.warn(
          `Error calculating photos metrics for user ${uid}:`,
          error
        );
        return metrics;
      }
    },

    aggregatePhotosDataFromStores(storeWorkspaces) {
      // Aggregate photos data from store workspaces to admin level
      const aggregated = {
        photos: 0,
        lastPhotoAdded: null,
        lastPhotoAddedDaysAgo: null,
      };

      let mostRecentPhoto = null;

      storeWorkspaces.forEach((store) => {
        aggregated.photos += store.photos || 0;

        // Find most recent photo
        if (store.lastPhotoAdded) {
          const storePhotoDate = new Date(store.lastPhotoAdded);
          if (!mostRecentPhoto || storePhotoDate > mostRecentPhoto) {
            mostRecentPhoto = storePhotoDate;
            aggregated.lastPhotoAdded = store.lastPhotoAdded;
            aggregated.lastPhotoAddedDaysAgo = store.lastPhotoAddedDaysAgo;
          }
        }
      });

      return aggregated;
    },

    calculateOverallPostsStats() {
      if (this.userPostsData.length === 0) {
        this.overallPostsStats = null;
        return;
      }
      // console.log("userPostsData", this.userPostsData);

      let totalPosts = 0;
      this.userPostsData?.forEach((user) => {
        totalPosts += user.aggregatedMetrics?.posts || 0;
      });

      this.overallPostsStats = {
        totalPosts,
        totalUsers: this.userPostsData.length,
      };
    },

    // Reviews Methods
    async fetchSelectedUsersReviewsData() {
      if (this.selectedUsers.length === 0) return;

      // console.log(
      //   "Starting fetchSelectedUsersReviewsData with flattened structure...",
      //   "Selected date:",
      //   this.selectedDate
      // );
      this.loading = true;
      this.error = null;

      try {
        const userReviewsPromises = this.selectedUsers.map(
          (uid) => this.fetchUserStatsFlattened(uid, this.selectedDate, true) // Reuse the same method since it gets all metrics
        );

        const batchSize = 5;
        const userReviewsResults = [];

        for (let i = 0; i < userReviewsPromises.length; i += batchSize) {
          const batch = userReviewsPromises.slice(i, i + batchSize);
          const batchResults = await Promise.all(batch);
          userReviewsResults.push(...batchResults);
        }

        const filteredResults = userReviewsResults.filter(
          (result) => result !== null
        );
        // console.log("Reviews data fetched:", filteredResults);

        // Use $set to ensure reactivity
        this.$set(this, "userReviewsData", filteredResults);
        this.calculateOverallReviewsStats();
      } catch (error) {
        console.error("Error fetching selected users reviews data:", error);
        this.error = "Failed to load reviews data. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    calculateOverallReviewsStats() {
      if (this.userReviewsData.length === 0) {
        this.overallReviewsStats = null;
        return;
      }

      let totalReviews = 0;
      let totalRating = 0;
      let usersWithRating = 0;

      this.userReviewsData.forEach((user) => {
        totalReviews += user.aggregatedMetrics?.reviews || 0;
        if (user.aggregatedMetrics?.rating) {
          totalRating += user.aggregatedMetrics.rating;
          usersWithRating++;
        }
      });

      this.overallReviewsStats = {
        totalReviews,
        averageRating: usersWithRating > 0 ? totalRating / usersWithRating : 0,
        totalUsers: this.userReviewsData.length,
      };
    },

    // Utility Methods
    calculatePercentageChange(current, previous) {
      if (!previous || previous === 0) {
        return current > 0 ? 100 : 0;
      }
      return ((current - previous) / previous) * 100;
    },

    getChangeDirection(current, previous) {
      const change = this.calculatePercentageChange(current, previous);
      if (change > 0) return "up";
      if (change < 0) return "down";
      return "neutral";
    },

    formatPercentageChange(current, previous) {
      const change = this.calculatePercentageChange(current, previous);
      const direction = this.getChangeDirection(current, previous);
      // show up arrow if change is positive, down arrow if change is negative, and minus if change is 0
      const icon = direction === "up" ? "↑" : direction === "down" ? "↓" : "-";
      const absChange = Math.abs(change);

      return {
        percentage: absChange,
        direction,
        icon,
        text: `${icon} ${absChange.toFixed(1)}%`,
        color:
          direction === "up"
            ? "success"
            : direction === "down"
            ? "error"
            : "grey",
      };
    },

    getPreviousWeekData(historicalData, metric) {
      if (!historicalData || Object.keys(historicalData).length === 0) {
        return null;
      }

      // Get the most recent (first) historical data point
      const dates = Object.keys(historicalData).sort(
        (a, b) => new Date(b) - new Date(a)
      );
      if (dates.length === 0) return null;

      const latestHistorical = historicalData[dates[0]];
      return latestHistorical ? latestHistorical[metric] || 0 : 0;
    },

    calculateAllMetricChanges(currentMetrics, historicalData) {
      const changes = {};

      // console.log("calculateAllMetricChanges called with:", {
      //   currentMetrics: currentMetrics,
      //   historicalDataKeys: Object.keys(historicalData || {}),
      //   historicalData: historicalData,
      // });

      if (!historicalData || Object.keys(historicalData).length === 0) {
        // console.log("No historical data available for changes calculation");
        return changes;
      }

      // Get the most recent historical data
      const dates = Object.keys(historicalData).sort(
        (a, b) => new Date(b) - new Date(a)
      );
      if (dates.length === 0) {
        // console.log("No dates found in historical data");
        return changes;
      }

      const mostRecentDate = dates[0];
      const previousWeekData = historicalData[mostRecentDate];

      // console.log(
      //   "Using historical data from date:",
      //   mostRecentDate,
      //   previousWeekData
      // );

      if (!previousWeekData) {
        // console.log("No previous week data found");
        return changes;
      }

      // Calculate changes for each metric
      Object.keys(currentMetrics).forEach((metric) => {
        const current = currentMetrics[metric];
        const previous = previousWeekData[metric];

        // console.log(`Calculating change for ${metric}:`, { current, previous });

        if (typeof current === "number" && typeof previous === "number") {
          changes[metric] = this.formatPercentageChange(current, previous);
          // console.log(`Change calculated for ${metric}:`, changes[metric]);
        } else {
          // console.log(`Skipping ${metric} - not numeric:`, {
          //   current: typeof current,
          //   previous: typeof previous,
          // });
        }
      });

      // console.log("Final changes object:", changes);
      return changes;
    },

    getHistoricalTooltipData(historicalData, metric) {
      if (!historicalData || Object.keys(historicalData).length === 0) {
        return [];
      }

      const dates = Object.keys(historicalData).sort(
        (a, b) => new Date(b) - new Date(a)
      );
      const limitedDates = dates.slice(0, 12);

      return limitedDates.map((date, index) => {
        const currentValue = historicalData[date]
          ? historicalData[date][metric] || 0
          : 0;
        let percentageChange = null;
        let changeDirection = "neutral";

        // Calculate percentage change compared to previous date (next in array since sorted desc)
        if (index < limitedDates.length - 1) {
          const previousDate = limitedDates[index + 1];
          const previousValue = historicalData[previousDate]
            ? historicalData[previousDate][metric] || 0
            : 0;

          if (previousValue !== 0 || currentValue !== 0) {
            const change = this.calculatePercentageChange(
              currentValue,
              previousValue
            );
            percentageChange = change;
            changeDirection = this.getChangeDirection(
              currentValue,
              previousValue
            );
          }
        }

        return {
          date,
          data: historicalData[date],
          formattedDate: this.formatDateForDisplay(date),
          value: currentValue,
          percentageChange,
          changeDirection,
          changeText:
            percentageChange !== null
              ? this.formatChangeText(percentageChange, changeDirection)
              : null,
        };
      });
    },

    formatChangeText(percentage, direction) {
      const icon = direction === "up" ? "↑" : direction === "down" ? "↓" : "-";
      const absChange = Math.abs(percentage);

      if (!isFinite(percentage)) {
        if (percentage > 0) {
          return `${icon} New`;
        } else if (percentage < 0) {
          return `${icon} Gone`;
        } else {
          return `${icon} 0%`;
        }
      } else {
        return `${icon} ${absChange.toFixed(1)}%`;
      }
    },

    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "0";
      const numValue = Number(num);
      if (numValue >= 1000000) {
        return (numValue / 1000000).toFixed(1) + "M";
      } else if (numValue >= 1000) {
        return (numValue / 1000).toFixed(1) + "K";
      }
      return numValue.toString();
    },

    // Format numbers for GMB Stats with 1 decimal place for all floating values
    formatStatsNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "0";
      const numValue = Number(num);
      if (numValue >= 1000000) {
        return (numValue / 1000000).toFixed(1) + "M";
      } else if (numValue >= 1000) {
        return (numValue / 1000).toFixed(1) + "K";
      }
      // For numbers less than 1000, show 1 decimal place if it's a float, otherwise show as integer
      if (numValue % 1 === 0) {
        return numValue.toString();
      } else {
        return numValue.toFixed(1);
      }
    },

    getRankColorClass(rank) {
      if (!rank || rank === 0) return "";

      const rankNum = Number(rank);
      if (rankNum >= 1 && rankNum <= 5) {
        return "rank-good"; // Green
      } else if (rankNum >= 6 && rankNum <= 10) {
        return "rank-average"; // Orange
      } else if (rankNum > 10) {
        return "rank-poor"; // Red
      }
      return "";
    },

    formatDateForDisplay(dateString) {
      // handle the case when time is firebase timestamp
      if (!dateString) return "";
      try {
        let date;
        if (
          dateString &&
          typeof dateString === "object" &&
          dateString.toDate &&
          dateString.toDate()
        ) {
          date = dateString.toDate();
        } else {
          date = new Date(dateString);
        }
        if (isNaN(date.getTime())) return dateString;

        return date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (error) {
        return dateString;
      }
    },

    formatDateTimeForDisplay(dateString) {
      // Format timestamp with date and time for verification history
      if (!dateString) return "";
      try {
        let date;
        if (
          dateString &&
          typeof dateString === "object" &&
          dateString.toDate &&
          dateString.toDate()
        ) {
          date = dateString.toDate();
        } else {
          date = new Date(dateString);
        }
        if (isNaN(date.getTime())) return dateString;

        const dateStr = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        const timeStr = date.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        return `${dateStr} ${timeStr}`;
      } catch (error) {
        return dateString;
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return "N/A";

      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }

      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${hours}:${minutes}`;
    },

    // Profile Strength Methods
    getProfileStrengthColor(profileStrength, level) {
      if (!profileStrength) return "grey";

      // Use same logic as formatProfileStrengthDisplay to detect store level
      const isStoreLevel =
        level >= 2 &&
        ("score" in profileStrength ||
          "completed_fields" in profileStrength ||
          "completed_count" in profileStrength);

      if (isStoreLevel) {
        // Store level - use score
        const score = profileStrength.score || 0;
        if (score >= 80) return "success";
        if (score >= 60) return "warning";
        return "error";
      } else {
        // User/Admin level - use store counts
        const totalStores = profileStrength.total_stores_analyzed || 0;
        if (totalStores === 0) return "grey";

        const excellentAndPerfect =
          (profileStrength.excellent_stores || 0) +
          (profileStrength.perfect_stores || 0);
        const excellentRatio = excellentAndPerfect / totalStores;

        if (excellentRatio >= 0.7) return "success";
        if (excellentRatio >= 0.4) return "warning";
        return "error";
      }
    },

    formatProfileStrengthDisplay(profileStrength, level) {
      if (!profileStrength) return "No data";

      // Check if this is store-level data by looking for store-specific fields
      // or by level (level 2 in 2-level hierarchy, level 3 in 3-level hierarchy)
      const isStoreLevel =
        level >= 2 &&
        ("score" in profileStrength ||
          "completed_fields" in profileStrength ||
          "completed_count" in profileStrength);

      if (isStoreLevel) {
        // Store level - show score and completion counts
        const score = profileStrength.score || 0;
        const completedCount =
          profileStrength.completed_count ||
          profileStrength.completed_fields?.length ||
          0;
        const totalFields = profileStrength.total_fields || 10;
        return `${score}% (${completedCount}/${totalFields})`;
      } else {
        // User/Admin level - show store distribution
        const totalStores = profileStrength.total_stores_analyzed || 0;
        if (totalStores === 0) return "No stores analyzed for profile strength";

        const excellentAndPerfect =
          (profileStrength.excellent_stores || 0) +
          (profileStrength.perfect_stores || 0);
        const avgScore = Math.round(profileStrength.average_score || 0);
        // return `${avgScore}% avg (${excellentAndPerfect}/${totalStores} strong)`;
        return `${avgScore}% (${excellentAndPerfect}/${totalStores} strong)`;
      }
    },

    getProfileStrengthTooltip(profileStrength, level) {
      if (!profileStrength) return "No profile strength data available";

      // Use same logic as formatProfileStrengthDisplay to detect store level
      const isStoreLevel =
        level >= 2 &&
        ("score" in profileStrength ||
          "completed_fields" in profileStrength ||
          "completed_count" in profileStrength);

      if (isStoreLevel) {
        // Store level - show only missing fields
        const missingFields = profileStrength.missing_fields || [];

        let tooltip = ``;

        if (missingFields.length > 0) {
          tooltip += `<div style="color: #f44336; font-weight: bold;">❌ Missing Fields (${missingFields.length}):</div>`;
          missingFields.forEach((field) => {
            tooltip += `<div style="color: #f44336;">• ${this.formatFieldName(
              field
            )}</div>`;
          });
        } else {
          tooltip += `🎉 All fields completed!`;
        }

        return tooltip;
      } else {
        // User/Admin level - show store distribution
        const totalStores = profileStrength.total_stores_analyzed || 0;
        if (totalStores === 0) return "No stores analyzed for profile strength";

        let tooltip = ``;
        // tooltip += `Average Score: ${Math.round(
        //   profileStrength.average_score || 0
        // )}%\n\n`;

        // tooltip += `📊 Store Distribution:\n`;
        // tooltip += `🟢 Perfect (100%): ${
        //   profileStrength.perfect_stores || 0
        // } stores\n`;
        // tooltip += `🟡 Excellent (80-99%): ${
        //   profileStrength.excellent_stores || 0
        // } stores\n`;
        // tooltip += `🟠 Good (60-79%): ${
        //   profileStrength.good_stores || 0
        // } stores\n`;
        // tooltip += `🔴 Fair (40-59%): ${
        //   profileStrength.fair_stores || 0
        // } stores\n`;
        // tooltip += `⚫ Poor (20-39%): ${
        //   profileStrength.poor_stores || 0
        // } stores\n`;
        // tooltip += `🔴 Critical (1-19%): ${
        //   profileStrength.critical_stores || 0
        // } stores\n`;
        // tooltip += `⭕ Empty (0%): ${
        //   profileStrength.empty_stores || 0
        // } stores\n`;

        // Show field completion rates if available
        if (profileStrength.field_completion_rates) {
          const fieldRates = profileStrength.field_completion_rates;

          // Sort fields by completed count in ascending order (lowest first)
          const sortedFields = Object.keys(fieldRates).sort((a, b) => {
            const aCount = fieldRates[a].completed_count || 0;
            const bCount = fieldRates[b].completed_count || 0;
            return aCount - bCount;
          });

          // Create table
          tooltip += `<table style="width: 100%; border-collapse: collapse;">`;
          tooltip += `<thead>`;
          tooltip += `<tr style="border-bottom: 1px solid #ddd;">`;
          tooltip += `<th style="text-align: left; padding: 4px 8px; font-weight: bold;">Field</th>`;
          tooltip += `<th style="text-align: right; padding: 4px 8px; font-weight: bold;">Completed</th>`;
          tooltip += `</tr>`;
          tooltip += `</thead>`;
          tooltip += `<tbody>`;

          sortedFields.forEach((field) => {
            const fieldData = fieldRates[field];
            const completedCount = fieldData.completed_count || 0;
            const completionRate = fieldData.completion_rate || 0;

            // Add color coding based on completion rate
            let color = "";
            if (completedCount === 0) {
              color = "#e53e3e"; // Red - 0 stores
            } else if (completionRate === 100) {
              color = "#38a169"; // Green - 100%
            } else if (completionRate >= 50) {
              color = "#ecc94b"; // Yellow - >= 50%
            } else {
              color = "#ed8936"; // Orange - < 50%
            }

            tooltip += `<tr style="border-bottom: 1px solid #eee;">`;
            tooltip += `<td style="text-align: left; padding: 4px 8px;">${this.formatFieldName(
              field
            )}</td>`;
            tooltip += `<td style="text-align: right; padding: 4px 8px; color: ${color}; font-weight: bold;">${completedCount}</td>`;
            tooltip += `</tr>`;
          });

          tooltip += `</tbody>`;
          tooltip += `</table>`;
        }

        return tooltip;
      }
    },

    formatFieldName(field) {
      const fieldNames = {
        description: "Description",
        websiteUri: "Website URL",
        primaryCategory: "Primary Category",
        phoneNumber: "Phone Number",
        hasLogo: "Logo",
        hasCoverPhoto: "Cover Photo",
        hasPhotos: "Photos",
        regularHours: "Regular Hours",
        additionalCategories: "Additional Categories",
        attributeCount: "Attributes",
      };
      return fieldNames[field] || field;
    },

    // Profile Field Methods
    getProfileFieldDisplay(item, fieldName) {
      if (!item.profileStrength) return "N/A";

      // Check if this is store level (has field_completion_rates) vs user/admin level
      if (item.profileStrength.field_completion_rates) {
        // User/Admin level - show completion count
        const fieldData =
          item.profileStrength.field_completion_rates[fieldName];
        if (!fieldData) return "0";
        return fieldData.completed_count || 0;
      } else {
        // Store level - show completion status
        const completedFields = item.profileStrength.completed_fields || [];
        return completedFields.includes(fieldName) ? "✓" : "✗";
      }
    },

    getProfileFieldColor(item, fieldName) {
      if (!item.profileStrength) return "grey";

      // Check if this is store level vs user/admin level
      if (item.profileStrength.field_completion_rates) {
        // User/Admin level - color based on completion rate
        const fieldData =
          item.profileStrength.field_completion_rates[fieldName];
        if (!fieldData) return "red";

        const completedCount = fieldData.completed_count || 0;
        const completionRate = fieldData.completion_rate || 0;

        if (completedCount === 0) {
          return "red"; // 0 stores
        } else if (completionRate === 100) {
          return "green"; // 100%
        } else if (completionRate >= 50) {
          return "orange"; // >= 50%
        } else {
          return "deep-orange"; // < 50%
        }
      } else {
        // Store level - green for completed, red for missing
        const completedFields = item.profileStrength.completed_fields || [];
        return completedFields.includes(fieldName) ? "green" : "red";
      }
    },

    // Refreshes Helper Methods
    formatFootfallDate(dateString) {
      if (!dateString) return "N/A";

      try {
        // Handle YYYY-MM-DD format
        const date = new Date(dateString);

        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];

        const day = date.getDate().toString().padStart(2, "0");
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        return `${day} ${month} ${year}`;
      } catch (error) {
        console.warn("Error formatting footfall date:", dateString, error);
        return dateString; // Return original if formatting fails
      }
    },

    formatDateFull(timestamp) {
      if (!timestamp) return "N/A";

      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else {
        date = new Date(timestamp);
      }

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      const day = date.getDate().toString().padStart(2, "0");
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    },

    checkKeywordInsightsUpToDate(monthYearString) {
      try {
        // Parse the YYYY-MM format
        const [year, month] = monthYearString.split("-").map(Number);
        const summaryMonth = new Date(year, month - 1, 1);

        const now = new Date();
        const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        // If summary is from a previous month, it's definitely outdated
        if (summaryMonth < currentMonth) {
          return false;
        }

        // If summary is from current month, check if any Monday has passed this month
        if (summaryMonth.getTime() === currentMonth.getTime()) {
          const firstMondayThisMonth = this.getFirstMondayOfMonth(
            now.getFullYear(),
            now.getMonth()
          );
          return now < firstMondayThisMonth;
        }

        // If summary is from future month, it's up to date
        return true;
      } catch (error) {
        console.warn("Error checking keyword insights date:", error);
        return false;
      }
    },

    getFirstMondayOfMonth(year, month) {
      const firstDay = new Date(year, month, 1);
      const dayOfWeek = firstDay.getDay();
      // Calculate days to add to get to first Monday (0 = Sunday, 1 = Monday)
      const daysToAdd = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7;
      return new Date(year, month, 1 + daysToAdd);
    },

    checkMondayRefresh(dateString) {
      try {
        // Parse YYYY-MM-DD format
        const documentDate = new Date(dateString);
        const now = new Date();

        // Find the most recent Monday
        const daysSinceMonday = (now.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0 system
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() - daysSinceMonday);
        lastMonday.setHours(0, 0, 0, 0);

        // If document is older than last Monday, it needs refresh
        return documentDate >= lastMonday;
      } catch (error) {
        console.warn(
          "Error checking Monday refresh for date:",
          dateString,
          error
        );
        return false;
      }
    },

    checkMondayRefreshFromTimestamp(timestamp) {
      try {
        let updateDate;
        if (timestamp.toDate) {
          // Firestore Timestamp
          updateDate = timestamp.toDate();
        } else if (typeof timestamp === "string") {
          // String format like "July 31, 2025 at 6:13:04 PM UTC+5:30"
          updateDate = new Date(timestamp);
        } else {
          // Already a Date object
          updateDate = timestamp;
        }

        const now = new Date();

        // Find the most recent Monday
        const daysSinceMonday = (now.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0 system
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() - daysSinceMonday);
        lastMonday.setHours(0, 0, 0, 0);

        // If update is older than last Monday, it needs refresh
        return updateDate >= lastMonday;
      } catch (error) {
        console.warn(
          "Error checking Monday refresh from timestamp:",
          timestamp,
          error
        );
        return false;
      }
    },

    // Cache management utilities
    getCachedRefreshData(userId) {
      const cached = this.refreshDataCache.get(userId);
      if (!cached) return null;

      const isExpired = Date.now() - cached.timestamp > this.cacheTimeout;
      if (isExpired) {
        this.refreshDataCache.delete(userId);
        return null;
      }

      return cached.data;
    },

    setCachedRefreshData(userId, data) {
      this.refreshDataCache.set(userId, {
        data: data,
        timestamp: Date.now(),
      });
    },

    clearRefreshCache() {
      this.refreshDataCache.clear();
    },

    async clearRefreshCacheAndRefresh() {
      // console.log("Clearing refresh cache and refreshing data...");
      this.clearRefreshCache();
      await this.fetchSelectedUsersRefreshes();
    },

    // Rate limiting utility
    async rateLimitedDelay() {
      if (this.rateLimitDelay > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, this.rateLimitDelay)
        );
      }
    },

    // Optimized Batch processing utility with better performance
    async processBatch(items, batchSize, processor) {
      const results = [];

      // Process items in batches for better memory management and rate limiting
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);

        try {
          // Process batch items in parallel
          const batchResults = await Promise.all(
            batch.map(async (item, index) => {
              try {
                // Add small staggered delay to prevent overwhelming Firestore
                if (index > 0) {
                  await new Promise((resolve) =>
                    setTimeout(resolve, index * 10)
                  );
                }
                return await processor(item);
              } catch (error) {
                console.warn(`Error processing batch item:`, error);
                return null; // Return null for failed items instead of breaking the batch
              }
            })
          );

          results.push(...batchResults);

          // Add adaptive delay between batches based on batch size
          if (i + batchSize < items.length) {
            const adaptiveDelay = Math.min(
              this.rateLimitDelay * (batchSize / 5),
              200
            );
            await new Promise((resolve) => setTimeout(resolve, adaptiveDelay));
          }
        } catch (error) {
          console.error(`Error processing batch ${i}-${i + batchSize}:`, error);
          // Continue with next batch instead of failing completely
        }
      }

      return results;
    },

    async fetchSelectedUsersRefreshes() {
      if (!this.selectedUsers || this.selectedUsers.length === 0) {
        // console.log("No users selected for refreshes fetch - clearing data");
        this.userRefreshesData = [];
        this.overallRefreshesStats = null;
        return;
      }

      // Reset loading states
      this.refreshesLoading = {
        users: true,
        workspaces: false,
        metrics: false,
        overall: false,
      };
      this.error = null;

      try {
        // console.log("Fetching refreshes data for users:", this.selectedUsers);

        // Check cache first for performance
        const cachedResults = [];
        const usersToFetch = [];

        for (const userId of this.selectedUsers) {
          const cached = this.getCachedRefreshData(userId);
          if (cached) {
            cachedResults.push(cached);
            // console.log(`Using cached data for user: ${userId}`);
          } else {
            usersToFetch.push(userId);
          }
        }

        this.refreshesLoading.users = false;
        this.refreshesLoading.workspaces = true;

        // Fetch remaining users in batches to prevent rate limiting
        const batchProcessor = async (userId) => {
          try {
            const result = await this.fetchUserRefreshesDataOptimized(userId);
            if (result) {
              this.setCachedRefreshData(userId, result);
            }
            return result;
          } catch (error) {
            console.error(`Error fetching data for user ${userId}:`, error);
            // Return null but don't break the entire operation
            return null;
          }
        };

        const freshResults = await this.processBatch(
          usersToFetch,
          this.maxConcurrentQueries,
          batchProcessor
        );

        this.refreshesLoading.workspaces = false;
        this.refreshesLoading.overall = true;

        // Combine cached and fresh results
        const allResults = [...cachedResults, ...freshResults];
        const filteredResults = allResults.filter((result) => result !== null);

        // console.log("Refreshes data fetched:", {
        //   total: filteredResults.length,
        //   cached: cachedResults.length,
        //   fresh: freshResults.filter((r) => r !== null).length,
        // });

        // Use $set to ensure reactivity
        this.$set(this, "userRefreshesData", filteredResults);
        this.calculateOverallRefreshesStats();

        // console.log(
        //   "Overall refreshes stats calculated:",
        //   this.overallRefreshesStats
        // );
      } catch (error) {
        console.error("Error fetching selected users refreshes data:", error);
        this.error = "Failed to load refreshes data. Please try again.";
      } finally {
        // Reset all loading states
        this.refreshesLoading = {
          users: false,
          workspaces: false,
          metrics: false,
          overall: false,
        };
      }
    },

    // Optimized version with parallel queries and better error handling
    async fetchUserRefreshesDataOptimized(userId) {
      try {
        // console.log(`Fetching refreshes data for user: ${userId}`);

        // Get user document and workspaces in parallel
        const [userDocSnap, adminWorkspacesSnap] = await Promise.all([
          getDoc(doc(db, "users", userId)),
          getDocs(collection(db, "users", userId, "fly_workspaces")),
        ]);

        if (!userDocSnap.exists()) {
          console.warn(`User document not found: ${userId}`);
          return null;
        }

        const userData = userDocSnap.data();
        console.log(
          `Found ${adminWorkspacesSnap.size} total workspaces for user ${userId}`
        );

        // Separate admin and store workspaces
        const allWorkspaces = [];
        const adminWorkspaceIds = [];
        const storeWorkspacesByAdmin = new Map();

        console.log(
          `RefreshesTab - Analyzing ${adminWorkspacesSnap.size} workspaces for user ${userId}`
        );

        adminWorkspacesSnap.docs.forEach((doc) => {
          const data = doc.data();
          const id = doc.id;

          allWorkspaces.push({ id, data });

          if (!data.admin) {
            // This is an admin workspace
            adminWorkspaceIds.push(id);
            storeWorkspacesByAdmin.set(id, []);
          } else if (data.admin) {
            // This is a store workspace
            if (!storeWorkspacesByAdmin.has(data.admin)) {
              storeWorkspacesByAdmin.set(data.admin, []);
            }
            storeWorkspacesByAdmin.get(data.admin).push({ id, data });
          }
        });

        if (adminWorkspaceIds.length === 0) {
          console.log(`No admin workspaces found for user ${userId}`);
          return {
            uid: userId,
            email: userData.email,
            displayName: userData.display_name || userData.name,
            adminWorkspaces: [],
            aggregatedMetrics: {},
          };
        }

        this.refreshesLoading.metrics = true;

        // Process admin workspaces in parallel batches
        const adminWorkspaces = await this.processBatch(
          adminWorkspaceIds,
          this.maxConcurrentQueries,
          async (adminId) => {
            const adminData = allWorkspaces.find((w) => w.id === adminId)?.data;
            if (!adminData) return null;

            console.log(
              `Processing admin workspace: ${adminId} with brandName: ${adminData.brandName}`
            );

            // Get admin refresh data and store workspaces in parallel
            const storeWorkspaces = storeWorkspacesByAdmin.get(adminId) || [];

            const [adminRefreshData, ...storeRefreshData] = await Promise.all([
              this.calculateRefreshMetricsOptimized(userId, adminId, adminData),
              ...storeWorkspaces.map((store) =>
                this.calculateRefreshMetricsOptimized(
                  userId,
                  store.id,
                  store.data
                )
              ),
            ]);

            // Process store workspaces
            const processedStoreWorkspaces = storeWorkspaces
              .map((store, index) => ({
                id: store.id,
                wid: store.id,
                title: store.data.name || `Store ${store.id}`,
                short_address: store.data.short_address,
                ...storeRefreshData[index],
              }))
              .filter((store) => store !== null);

            // Aggregate admin-level metrics from store workspaces
            const aggregatedAdminMetrics = this.aggregateRefreshMetrics(
              processedStoreWorkspaces,
              adminRefreshData
            );

            return {
              id: adminId,
              wid: adminId,
              title:
                adminData.brandName || adminData.name || `Admin ${adminId}`,
              storeWorkspaces: processedStoreWorkspaces,
              aggregatedMetrics: aggregatedAdminMetrics,
            };
          }
        );

        const validAdminWorkspaces = adminWorkspaces.filter(
          (admin) => admin !== null
        );

        // Calculate user-level aggregated metrics
        const allStoreWorkspaces = validAdminWorkspaces.flatMap(
          (admin) => admin.storeWorkspaces
        );
        const userAggregatedMetrics = this.aggregateRefreshMetrics(
          allStoreWorkspaces,
          {}
        );

        console.log(`User ${userId} refresh data:`, {
          adminWorkspacesCount: validAdminWorkspaces.length,
          totalStoreWorkspaces: allStoreWorkspaces.length,
          userAggregatedMetrics,
        });

        return {
          uid: userId,
          email: userData.email,
          displayName: userData.display_name || userData.name,
          adminWorkspaces: validAdminWorkspaces,
          aggregatedMetrics: userAggregatedMetrics,
        };
      } catch (error) {
        console.error(
          `Error fetching refreshes data for user ${userId}:`,
          error
        );
        return null;
      }
    },

    // Keep original method for fallback compatibility
    async fetchUserRefreshesData(userId) {
      return this.fetchUserRefreshesDataOptimized(userId);
    },

    // Optimized version with parallel queries
    async calculateRefreshMetricsOptimized(userId, workspaceId, workspaceData) {
      const metrics = {
        rankRefresh: null,
        reviewInsights: null,
        footfallAnalyticsDefault: null,
        keywordInsightsSummary: null,
        competitors: null,
        apifyFetch: null,
        footfallInsights: null,
      };

      try {
        // Prepare all queries that we'll run in parallel
        const queryPromises = [];
        const queryKeys = [];

        // Rank tracker runs query
        queryPromises.push(
          getDocs(
            query(
              collection(db, "users", userId, "rank_tracker_runs"),
              where("workspace_id", "==", workspaceId),
              orderBy("created_at", "desc"),
              limit(1)
            )
          ).catch((error) => {
            console.warn(
              `Error fetching rank tracker for ${workspaceId}:`,
              error
            );
            return { empty: true };
          })
        );
        queryKeys.push("rankTracker");

        // Review insights query
        queryPromises.push(
          getDocs(
            query(
              collection(
                db,
                "users",
                userId,
                "fly_workspaces",
                workspaceId,
                "review_insights"
              ),
              orderBy("lastUpdated", "desc"),
              limit(1)
            )
          ).catch((error) => {
            console.warn(
              `Error fetching review insights for ${workspaceId}:`,
              error
            );
            return { empty: true };
          })
        );
        queryKeys.push("reviewInsights");

        // Competitors query
        queryPromises.push(
          getDocs(
            query(
              collection(
                db,
                "users",
                userId,
                "fly_workspaces",
                workspaceId,
                "competitors"
              ),
              orderBy("lastUpdated", "desc"),
              limit(1)
            )
          ).catch((error) => {
            console.warn(
              `Error fetching competitors for ${workspaceId}:`,
              error
            );
            return { empty: true };
          })
        );
        queryKeys.push("competitors");

        // Apify data query
        queryPromises.push(
          getDocs(
            query(
              collection(
                db,
                "users",
                userId,
                "fly_workspaces",
                workspaceId,
                "apify_data"
              ),
              orderBy("lastUpdated", "desc"),
              limit(1)
            )
          ).catch((error) => {
            console.warn(
              `Error fetching apify data for ${workspaceId}:`,
              error
            );
            return { empty: true };
          })
        );
        queryKeys.push("apifyData");

        // Footfall analytics query
        queryPromises.push(
          getDocs(
            query(
              collection(
                db,
                "users",
                userId,
                "fly_workspaces",
                workspaceId,
                "footfall_analytics"
              ),
              orderBy("generated_at", "desc"),
              limit(1)
            )
          ).catch((error) => {
            console.warn(
              `Error fetching footfall analytics for ${workspaceId}:`,
              error
            );
            return { empty: true };
          })
        );
        queryKeys.push("footfallAnalytics");

        // For admin workspaces, add admin workspace specific queries
        if (!workspaceData.admin) {
          // Footfall analytics query for this specific admin workspace
          queryPromises.push(
            getDocs(
              query(
                collection(
                  db,
                  "users",
                  userId,
                  "fly_workspaces",
                  workspaceId,
                  "footfall_analytics"
                ),
                orderBy("generated_at", "desc"),
                limit(1)
              )
            ).catch((error) => {
              console.warn(
                `Error fetching footfall analytics for admin workspace ${workspaceId}:`,
                error
              );
              return { empty: true };
            })
          );
          queryKeys.push("footfallDefault");

          // Keyword insights summary query for this specific admin workspace
          queryPromises.push(
            getDocs(
              query(
                collection(
                  db,
                  "users",
                  userId,
                  "fly_workspaces",
                  workspaceId,
                  "search_insights_summary"
                ),
                orderBy("lastUpdated", "desc"),
                limit(1)
              )
            ).catch((error) => {
              console.warn(
                `Error fetching keyword insights summary for admin workspace ${workspaceId}:`,
                error
              );
              return { empty: true };
            })
          );
          queryKeys.push("keywordSummary");
        }

        // Execute all queries in parallel
        const results = await Promise.all(queryPromises);

        // Process results
        results.forEach((result, index) => {
          const key = queryKeys[index];

          if (!result.empty) {
            switch (key) {
              case "rankTracker":
                metrics.rankRefresh = result.docs[0].data().created_at;
                break;
              case "reviewInsights":
                metrics.reviewInsights = result.docs[0].data().lastUpdated;
                break;
              case "competitors":
                metrics.competitors = result.docs[0].data().lastUpdated;
                break;
              case "apifyData":
                metrics.apifyFetch = result.docs[0].data().lastUpdated;
                break;
              case "footfallAnalytics":
                metrics.footfallInsights = result.docs[0].data().generated_at;
                break;
              case "footfallDefault": {
                const footfallDoc = result.docs[0];
                const footfallData = footfallDoc.data();
                const documentDate = footfallDoc.id;
                const isUpToDate = this.checkMondayRefresh(documentDate);
                metrics.footfallAnalyticsDefault = {
                  date: documentDate,
                  generated_at: footfallData.generated_at,
                  isUpToDate: isUpToDate,
                };
                break;
              }
              case "keywordSummary": {
                const summaryDoc = result.docs[0];
                const summaryData = summaryDoc.data();
                const summaryId = summaryDoc.id;
                const keywordUpToDate =
                  this.checkKeywordInsightsUpToDate(summaryId);
                metrics.keywordInsightsSummary = {
                  monthYear: summaryId,
                  lastUpdated: summaryData.lastUpdated,
                  isUpToDate: keywordUpToDate,
                };
                break;
              }
            }
          }
        });

        return metrics;
      } catch (error) {
        console.warn(
          `Error calculating refresh metrics for ${workspaceId}:`,
          error
        );
        return metrics;
      }
    },

    // Keep original method for fallback compatibility
    async calculateRefreshMetrics(userId, workspaceId, workspaceData) {
      return this.calculateRefreshMetricsOptimized(
        userId,
        workspaceId,
        workspaceData
      );
    },

    aggregateRefreshMetrics(storeWorkspaces, baseMetrics = {}) {
      const aggregated = { ...baseMetrics };

      // Find the most recent date for each metric type
      storeWorkspaces.forEach((store) => {
        Object.keys(aggregated).forEach((key) => {
          if (store[key]) {
            if (
              !aggregated[key] ||
              new Date(store[key]) > new Date(aggregated[key])
            ) {
              aggregated[key] = store[key];
            }
          }
        });
      });

      return aggregated;
    },

    calculateOverallRefreshesStats() {
      if (this.userRefreshesData.length === 0) {
        this.overallRefreshesStats = null;
        return;
      }

      // For refreshes, we mainly show summary counts
      let totalAdminWorkspaces = 0;
      let totalStoreWorkspaces = 0;

      this.userRefreshesData.forEach((userData) => {
        const adminWorkspaces = userData.adminWorkspaces || [];
        totalAdminWorkspaces += adminWorkspaces.length;

        adminWorkspaces.forEach((adminWs) => {
          totalStoreWorkspaces += (adminWs.storeWorkspaces || []).length;
        });
      });

      this.overallRefreshesStats = {
        totalUsers: this.userRefreshesData.length,
        totalAdminWorkspaces,
        totalStoreWorkspaces,
      };
    },

    getPlatformIcon(platform) {
      try {
        if (!platform || typeof platform !== "string") {
          return "mdi-share-variant";
        }

        const platformIcons = {
          facebook: "mdi-facebook",
          instagram: "mdi-instagram",
          twitter: "mdi-twitter",
          x: "mdi-twitter",
          linkedin: "mdi-linkedin",
          pinterest: "mdi-pinterest",
          youtube: "mdi-youtube",
          tiktok: "mdi-music-note",
          google_business_profile: "mdi-google",
          google: "mdi-google",
          threads: "mdi-at",
        };
        return platformIcons[platform.toLowerCase()] || "mdi-share-variant";
      } catch (error) {
        console.warn("Error in getPlatformIcon:", error, "platform:", platform);
        return "mdi-share-variant";
      }
    },

    getPlatformColor(platform) {
      try {
        if (!platform || typeof platform !== "string") {
          return "grey";
        }

        const platformColors = {
          facebook: "#1877F2",
          instagram: "#E4405F",
          twitter: "#1DA1F2",
          x: "#000000",
          linkedin: "#0A66C2",
          pinterest: "#BD081C",
          youtube: "#FF0000",
          tiktok: "#000000",
          google_business_profile: "#4285F4",
          google: "#4285F4",
          threads: "#000000",
        };
        return platformColors[platform.toLowerCase()] || "grey";
      } catch (error) {
        console.warn(
          "Error in getPlatformColor:",
          error,
          "platform:",
          platform
        );
        return "grey";
      }
    },

    getPlatformName(platform) {
      try {
        if (!platform || typeof platform !== "string") {
          return "Unknown Platform";
        }

        const platformNames = {
          facebook: "Facebook",
          instagram: "Instagram",
          twitter: "Twitter",
          x: "X (Twitter)",
          linkedin: "LinkedIn",
          pinterest: "Pinterest",
          youtube: "YouTube",
          tiktok: "TikTok",
          google_business_profile: "Google Business Profile",
          google: "Google",
          threads: "Threads",
        };
        return platformNames[platform.toLowerCase()] || platform;
      } catch (error) {
        console.warn("Error in getPlatformName:", error, "platform:", platform);
        return "Unknown Platform";
      }
    },

    formatRank(rank) {
      if (!rank || isNaN(rank)) return "N/A";
      const rankValue = Number(rank);
      if (rankValue === 21) {
        return "21+";
      }
      return rankValue.toFixed(1);
    },

    formatPeriod(period) {
      if (!period || !period.start_date || !period.end_date) return "N/A";
      return `${period.start_date} - ${period.end_date}`;
    },

    formatMetricName(key) {
      if (!key || typeof key !== "string") return "Unknown Metric";

      const metricNames = {
        // New flattened structure fields
        maps_impressions: "Maps Impressions",
        search_impressions: "Search Impressions",
        directions: "Directions",
        call_clicks: "Call Clicks",
        website_clicks: "Website Clicks",
        bookings: "Bookings",
        posts: "Posts",
        reviews: "Reviews",
        rating: "Rating",
        total_stores: "Total Stores",

        // Legacy fields for backward compatibility
        mobile_maps_impressions: "Mobile Maps",
        desktop_maps_impressions: "Desktop Maps",
        mobile_search_impressions: "Mobile Search",
        desktop_search_impressions: "Desktop Search",
        actions_driving_directions: "Directions",
        actions_phone: "Call Clicks",
        actions_website: "Website",
        actions_booking: "Bookings",
        actions_food_orders: "Food Orders",
        actions_food_menu: "Food Menu",
        views_maps: "Views on Maps",
        views_search: "Views on Search",
        photos_views_merchant: "Merchant Photos Views",
        photos_views_customers: "Customer Photos Views",
        photos_count_merchant: "Merchant Photos Count",
        photos_count_customers: "Customer Photos Count",
        local_post_views_search: "Local Post Views (Search)",
        local_post_actions_call_to_action: "Local Post Call-to-Action",
        queries_direct: "Direct Queries",
        queries_indirect: "Indirect Queries",
        queries_branded: "Branded Queries",
        queries_chain: "Chain Queries",
        total_views: "Total Views",
        totalDesktopImpressions: "Total Desktop Impressions",
        totalMobileImpressions: "Total Mobile Impressions",
      };

      if (metricNames[key]) {
        return metricNames[key];
      }

      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    formatRankMetricName(key) {
      if (!key || typeof key !== "string") return "Unknown Metric";

      const metricNames = {
        // New flattened structure fields
        rank: "Rank",
        total_keywords: "Keywords",
        reviews: "Reviews",
        rating: "Rating",
        posts: "Posts",
        total_stores: "Total Stores",

        // Legacy fields for backward compatibility
        average_rank: "Avg Rank",
        total_business_ranks: "Total/Grid ID",
        businesses_with_actual_rank: "Ranked/Title",
        businesses_with_rank_21: "21+ Ranks",
        average_excluding_21: "Avg (Ex. 21+)",
        workspace_count: "Workspaces",
        total_businesses: "Businesses",
      };

      if (metricNames[key]) {
        return metricNames[key];
      }

      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    extractAllMetrics(stats) {
      const allMetrics = {};
      const excludeFields = [
        "wid",
        "name",
        "id",
        "uid",
        "week_period",
        "calculated_at",
        "stored_at",
        "week_stored_on",
        "stored_date",
        "created_at",
        "updated_at",
        "allMetrics",
      ];

      for (const key in stats) {
        if (excludeFields.includes(key)) continue;
        const value = stats[key];
        if (typeof value === "number" && !isNaN(value)) {
          allMetrics[key] = value;
        }
      }

      return allMetrics;
    },

    extractAllRankMetrics(rankData) {
      const allMetrics = {};
      const excludeFields = [
        "wid",
        "name",
        "id",
        "uid",
        "date",
        "stored_at",
        "created_at",
        "updated_at",
        "allMetrics",
        "my_business_ranks",
        "workspace_metrics",
        "rank_calculation_details",
      ];

      for (const key in rankData) {
        if (excludeFields.includes(key)) continue;
        const value = rankData[key];
        if (typeof value === "number" && !isNaN(value)) {
          allMetrics[key] = value;
        }
      }

      if (rankData.rank_calculation_details) {
        Object.keys(rankData.rank_calculation_details).forEach((key) => {
          const value = rankData.rank_calculation_details[key];
          if (typeof value === "number" && !isNaN(value)) {
            allMetrics[key] = value;
          }
        });
      }

      return allMetrics;
    },

    // New flattened structure utility methods
    extractAllMetricsFlattened(data) {
      const allMetrics = {};
      const excludeFields = [
        "wid",
        "uid",
        "stored_at",
        "last_updated",
        "metric_type",
      ];

      for (const key in data) {
        if (excludeFields.includes(key)) continue;
        const value = data[key];
        if (typeof value === "number" && !isNaN(value)) {
          allMetrics[key] = value;
        }
      }

      return allMetrics;
    },

    createMockKeywords(storeData) {
      // Create mock keywords for display purposes
      // In the new structure, individual keywords might not be stored
      const totalKeywords = storeData.total_keywords || 0;
      const keywords = [];

      for (let i = 0; i < Math.min(totalKeywords, 5); i++) {
        keywords.push({
          keyword: `Keyword ${i + 1}`,
          rank: storeData.rank || 21,
          location_variable_keyword: `Location ${i + 1}`,
          grid_id: `grid_${i + 1}`,
          title: `Business Title ${i + 1}`,
        });
      }

      return keywords;
    },

    getRowClass(item) {
      if (!item) return {};

      return {
        "user-row": item.level === 1,
        "admin-row": item.level === 2,
        "workspace-row": item.level === 3,
      };
    },

    toggleChildExpand(child) {
      if (!child) return;

      const tabName = this.getTabName(this.currentTab);
      if (
        tabName === "content" ||
        tabName === "performance" ||
        tabName === "refreshes"
      ) {
        if (child.expanded === undefined) {
          this.$set(child, "expanded", false);
        }
        child.expanded = !child.expanded;
        this.$forceUpdate();
      } else if (tabName === "rank") {
        this.$set(this.expandedItems, child.id, !this.expandedItems[child.id]);
      }
    },

    // Date Range Methods - Optimized
    async fetchAvailableDates() {
      if (this.selectedUsers.length === 0) return;

      try {
        console.log(
          "📅 Fetching available dates for users:",
          this.selectedUsers
        );
        const allDates = new Set();

        // Process users in parallel for better performance
        const userDatePromises = this.selectedUsers.map(async (uid) => {
          try {
            // Get admin workspaces for this user
            const adminWorkspacesSnap = await getDocs(
              collection(db, "users", uid, "fly_workspaces")
            );
            const adminWorkspaceIds = [];

            adminWorkspacesSnap.docs.forEach((doc) => {
              const data = doc.data();
              const id = doc.id;

              if (!data.admin) {
                // This is an admin workspace
                adminWorkspaceIds.push(id);
              }
            });

            // Get dates from all admin workspaces in parallel
            const adminDatePromises = adminWorkspaceIds.map(async (adminId) => {
              try {
                const metricsRef = collection(
                  db,
                  "users",
                  uid,
                  "fly_workspaces",
                  adminId,
                  "super_admin_metrices"
                );
                const snapshot = await getDocs(metricsRef);
                return snapshot.docs.map((doc) => doc.id);
              } catch (error) {
                console.warn(
                  `Error fetching dates for admin ${adminId}:`,
                  error
                );
                return [];
              }
            });

            const adminDatesResults = await Promise.all(adminDatePromises);
            return adminDatesResults.flat();
          } catch (error) {
            console.warn(`Error fetching dates for user ${uid}:`, error);
            return [];
          }
        });

        const allUserDatesResults = await Promise.all(userDatePromises);
        const allUserDates = allUserDatesResults.flat();

        // Add all unique dates
        allUserDates.forEach((date) => allDates.add(date));

        this.availableDates = Array.from(allDates).sort(
          (a, b) => new Date(b) - new Date(a)
        );

        if (this.availableDates.length > 0) {
          this.selectedDateIndex = 0; // Latest date
        }

        console.log(
          `✅ Found ${this.availableDates.length} available dates:`,
          this.availableDates.slice(0, 5)
        );
      } catch (error) {
        console.error("Error fetching available dates:", error);
      }
    },

    async onDateSelectionChange() {
      console.log(
        `Date selection changed to index: ${this.selectedDateIndex}, date: ${this.selectedDate}`
      );

      if (!this.selectedDate) {
        console.warn("No selected date available");
        return;
      }

      if (this.selectedUsers.length === 0) {
        console.warn("No users selected");
        return;
      }

      try {
        console.log(`Refreshing data for selected date: ${this.selectedDate}`);

        // Reset processed tabs state for new date
        this.processedTabs = {
          content: false,
          performance: false,
          refreshes: false,
        };

        // Clear existing tab data to show loading state
        this.userStatsData = [];
        this.userRankData = [];
        this.userPostsData = [];
        this.userReviewsData = [];
        this.overallStats = null;
        this.overallRankStats = null;
        this.overallPostsStats = null;
        this.overallReviewsStats = null;
        this.error = null;

        // Fetch data for the new date
        const tabName = this.getTabName(this.currentTab);

        // Fetch fresh data for the new date
        if (tabName === "content" || tabName === "historical") {
          await this.fetchSelectedUsersPostsData();
        }
        if (tabName === "performance") {
          await this.fetchSelectedUsersStats();
        }

        if (this.showTrendChart) {
          this.updateTrendChart();
        }
      } catch (error) {
        console.error("Error during date selection change:", error);
        this.error = `Failed to load data for ${this.formatDateForDisplay(
          this.selectedDate
        )}. Please try again.`;
      }
    },

    async fetchTimelineData() {
      if (this.selectedUsers.length === 0) return;

      try {
        const timelineData = {};

        for (const uid of this.selectedUsers) {
          const userTimeline = {};

          for (const date of this.availableDates) {
            const userData = await this.fetchUserStatsFlattened(uid, date);
            if (userData) {
              userTimeline[date] = userData;
            }
          }

          if (Object.keys(userTimeline).length > 0) {
            timelineData[uid] = userTimeline;
          }
        }

        this.timelineData = timelineData;
      } catch (error) {
        console.error("Error fetching timeline data:", error);
      }
    },

    // Trend Chart Methods (placeholder for now)
    updateTrendChart() {
      // TODO: Implement trend chart update logic
      console.log("Updating trend chart for selected date:", this.selectedDate);
    },

    // Tooltip Methods
    showTooltip(item, metric, event) {
      console.log("Tooltip triggered for item:", {
        level: item.level,
        displayName: item.displayName,
        metric: metric,
        hasHistoricalData: !!(
          item.historicalData && Object.keys(item.historicalData).length > 0
        ),
        historicalDataKeys: Object.keys(item.historicalData || {}),
        currentValue: item[metric],
      });

      if (
        !item.historicalData ||
        Object.keys(item.historicalData).length === 0
      ) {
        console.warn("No historical data available for tooltip");
        return; // No historical data to show
      }

      this.tooltipMetric = metric;
      this.tooltipMetricName = this.formatMetricName(metric);
      this.tooltipData = this.getHistoricalTooltipData(
        item.historicalData,
        metric
      );

      console.log("Generated tooltip data:", {
        metric: metric,
        tooltipDataLength: this.tooltipData.length,
        tooltipData: this.tooltipData,
      });

      // Add current week data at the beginning with change from previous week
      const currentWeekData = {
        date: this.selectedDate,
        data: { [metric]: item[metric] },
        formattedDate:
          this.formatDateForDisplay(this.selectedDate) + " (Current)",
        value: item[metric] || 0,
        percentageChange: null,
        changeDirection: "neutral",
        changeText: null,
      };

      // Calculate change from current to most recent historical week
      if (this.tooltipData.length > 0) {
        const mostRecentHistorical = this.tooltipData[0];
        const currentValue = item[metric] || 0;
        const previousValue = mostRecentHistorical.value || 0;

        if (previousValue !== 0 || currentValue !== 0) {
          const change = this.calculatePercentageChange(
            currentValue,
            previousValue
          );
          currentWeekData.percentageChange = change;
          currentWeekData.changeDirection = this.getChangeDirection(
            currentValue,
            previousValue
          );
          currentWeekData.changeText = this.formatChangeText(
            change,
            currentWeekData.changeDirection
          );
        }
      }

      this.tooltipData.unshift(currentWeekData);

      // Position tooltip
      const rect = event.target.getBoundingClientRect();
      this.tooltipStyle = {
        position: "fixed",
        top: rect.top - 10 + "px",
        left: rect.right + 10 + "px",
        zIndex: 1000,
      };

      this.showHistoricalTooltip = true;
    },

    hideTooltip() {
      this.showHistoricalTooltip = false;
      this.tooltipData = [];
      this.tooltipMetric = "";
      this.tooltipMetricName = "";
    },

    // Verification Status Helper Methods
    getMostRecentVerificationStatus(verificationHistoryDetails) {
      if (
        !verificationHistoryDetails ||
        verificationHistoryDetails.length === 0
      ) {
        return "unknown";
      }

      // Sort by date (most recent first) and get the first entry
      const sortedHistory = [...verificationHistoryDetails].sort((a, b) => {
        const dateA = a.verification_date || a.date || a.timestamp;
        const dateB = b.verification_date || b.date || b.timestamp;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        // Convert to timestamp for comparison
        const timeA = dateA.toDate
          ? dateA.toDate().getTime()
          : new Date(dateA).getTime();
        const timeB = dateB.toDate
          ? dateB.toDate().getTime()
          : new Date(dateB).getTime();

        return timeB - timeA; // Most recent first
      });

      const mostRecent = sortedHistory[0];

      // Use verified boolean first, fallback to status string
      if (typeof mostRecent.verified === "boolean") {
        return mostRecent.verified ? "verified" : "unverified";
      }

      return mostRecent.status || "unknown";
    },

    getSortedVerificationHistory(verificationHistoryDetails) {
      if (
        !verificationHistoryDetails ||
        verificationHistoryDetails.length === 0
      ) {
        return [];
      }

      // Sort by date (most recent first)
      return [...verificationHistoryDetails].sort((a, b) => {
        const dateA = a.verification_date || a.date || a.timestamp;
        const dateB = b.verification_date || b.date || b.timestamp;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        // Convert to timestamp for comparison
        const timeA = dateA.toDate
          ? dateA.toDate().getTime()
          : new Date(dateA).getTime();
        const timeB = dateB.toDate
          ? dateB.toDate().getTime()
          : new Date(dateB).getTime();

        return timeB - timeA; // Most recent first
      });
    },

    getSortedScheduledPosts(scheduledPostsDetails) {
      if (!scheduledPostsDetails || scheduledPostsDetails.length === 0) {
        return [];
      }

      // Sort by date (most recent first) and also sort posts within each date group
      return [...scheduledPostsDetails]
        .sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB.getTime() - dateA.getTime(); // Most recent first
        })
        .map((dateGroup) => ({
          ...dateGroup,
          posts: [...(dateGroup.posts || [])].sort((a, b) => {
            // Sort posts within the date by scheduled time (most recent first)
            const timeA = new Date(
              `${dateGroup.date} ${a.scheduledTime || "00:00"}`
            );
            const timeB = new Date(
              `${dateGroup.date} ${b.scheduledTime || "00:00"}`
            );
            return timeB.getTime() - timeA.getTime();
          }),
        }));
    },

    formatVerificationStatus(status) {
      if (!status || typeof status !== "string") return "Unknown";

      const statusMap = {
        verified: "Verified",
        unverified: "Unverified",
        failed: "Failed",
        pending: "Pending",
        partial: "Partial",
        error: "Error",
        unknown: "Unknown",
        in_progress: "In Progress",
        expired: "Expired",
        suspended: "Suspended",
      };

      return (
        statusMap[status.toLowerCase()] ||
        status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
      );
    },

    getVerificationStatusColor(status) {
      if (!status || typeof status !== "string") return "grey";

      const colorMap = {
        verified: "success",
        unverified: "error",
        failed: "error",
        pending: "warning",
        partial: "warning",
        error: "red",
        unknown: "grey",
        in_progress: "info",
        expired: "orange",
        suspended: "red",
      };

      return colorMap[status.toLowerCase()] || "grey";
    },

    getVerificationStatusIcon(status) {
      if (!status || typeof status !== "string") return "mdi-help-circle";

      const iconMap = {
        verified: "mdi-check-circle",
        unverified: "mdi-alert-circle",
        failed: "mdi-close-circle",
        pending: "mdi-clock",
        partial: "mdi-check-circle-outline",
        error: "mdi-alert",
        unknown: "mdi-help-circle",
        in_progress: "mdi-progress-clock",
        expired: "mdi-timer-off",
        suspended: "mdi-pause-circle",
      };

      return iconMap[status.toLowerCase()] || "mdi-help-circle";
    },

    // Helper methods for verification summary at admin/user level
    getVerificationSummary(item) {
      if (!item.children || item.children.length === 0) {
        return "No stores";
      }

      const verifiedStores = item.children.filter(
        (child) => child.verificationStatus === "verified"
      ).length;
      const totalStores = item.children.length;

      if (verifiedStores === totalStores) {
        return "All verified";
      } else if (verifiedStores === 0) {
        return "NA";
      } else {
        return `${verifiedStores}/${totalStores} verified`;
      }
    },

    getVerificationSummaryColor(item) {
      if (!item.children || item.children.length === 0) {
        return "grey";
      }

      const verifiedStores = item.children.filter(
        (child) => child.verificationStatus === "verified"
      ).length;
      const totalStores = item.children.length;
      const verificationRatio = verifiedStores / totalStores;

      if (verificationRatio === 1) {
        return "success"; // All verified
      } else if (verificationRatio === 0) {
        return "error"; // None verified
      } else if (verificationRatio >= 0.8) {
        return "warning"; // Most verified
      } else {
        return "orange"; // Some verified
      }
    },

    isProfileComplete(profileStrength, level) {
      if (!profileStrength) return false;

      // Check if this is store level
      const isStoreLevel =
        level >= 2 &&
        ("score" in profileStrength ||
          "completed_fields" in profileStrength ||
          "completed_count" in profileStrength);

      if (isStoreLevel) {
        // For store level, check if score is 100%
        const score = profileStrength.score || 0;
        return score === 100;
      } else {
        // For user/admin level, we don't disable tooltips as they show store distribution
        return false;
      }
    },
  },
};
</script>

<style scoped>
/* User selection wrapper */
.user-select-wrapper {
  display: inline-block;
  position: relative;
}

/* Apply button wrapper */
.apply-button-wrapper {
  display: flex;
  align-items: center;
}

/* Background loading banner */
.background-loading-banner {
  margin-bottom: 16px;
}

/* Timeline loading overlay */
.timeline-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

.profile-details-container >>> br {
  margin-bottom: 4px;
}

.profile-details-container >>> div {
  line-height: 1.4;
  font-size: 13px;
}

.profile-details-container >>> ul {
  padding-left: 20px;
  margin-bottom: 10px;
}

.profile-details-container >>> li {
  margin-bottom: 2px;
  font-size: 12px;
}

.profile-details-container >>> table {
  font-size: 12px;
}

.profile-details-container >>> th {
  font-size: 12px;
}

.profile-details-container >>> td {
  font-size: 12px;
}

.profile-details-container >>> h4 {
  font-size: 13px;
}

/* Ensure profile menu is always visible */
.profile-menu-content {
  z-index: 9999 !important;
}

.profile-menu-content .v-menu__content {
  max-height: 80vh;
  overflow-y: auto;
  border: none !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.profile-menu-content .v-card {
  border: none !important;
  border-radius: 8px !important;
}

/* Store count badge styling */
.store-count-badge {
  margin-left: 12px;
  background: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #bbdefb;
}
.ops-dashboard-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.ops-tabs {
  border-bottom: 1px solid #e9ecef;
}

.ops-tab-content {
  background: #f5f5f5;
  min-height: 600px;
}

.tab-container {
  padding: 20px;
  padding-top: 30px;
  min-height: 400px;
}

.controls-group {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

/* User select wrapper */
.user-select-wrapper {
  position: relative;
  z-index: 10;
  margin-bottom: 10px;
}

/* User search select styling */
.controls-group .v-autocomplete {
  min-width: 300px;
  flex-shrink: 0;
  position: relative;
}

.controls-group .v-autocomplete .v-select__slot {
  margin-bottom: 8px;
}

.controls-group .v-autocomplete .v-menu__content {
  margin-top: 8px !important;
  z-index: 1000 !important;
}

/* Ensure search input is visible */
.v-autocomplete.v-select--is-menu-active .v-input__control {
  z-index: 1001;
}

/* User dropdown menu positioning */
.user-dropdown-menu {
  z-index: 1000 !important;
  margin-top: 10px !important;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
}

.selection-info {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.loading-text {
  margin-top: 16px;
  color: #666;
}

.error-container {
  padding: 20px;
}

.data-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
}

.no-data-text {
  font-size: 18px;
  color: #666;
  margin: 0;
  text-align: center;
}

.no-data-subtext {
  font-size: 14px;
  color: #999;
  margin: 8px 0 0 0;
  text-align: center;
}

/* User dropdown styles */
.user-dropdown-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  width: 100%;
}

.user-main-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-display-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.user-email {
  font-size: 12px;
  color: #666;
  margin-top: 1px;
}

.user-email-only {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin-top: 0;
}

.no-users-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  color: #666;
  font-size: 14px;
}

/* Hierarchical table styles */
.hierarchical-table-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-main-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.table-description {
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
}

/* Override Vuetify table styles for better hierarchy display */
.hierarchical-stats-table,
.hierarchical-rank-table {
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.hierarchical-stats-table .v-data-table__wrapper,
.hierarchical-rank-table .v-data-table__wrapper {
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: auto;
  position: relative;
}

/* Fixed height table container */
.table-fixed-height .v-data-table__wrapper {
  height: 600px !important;
  max-height: 600px !important;
  overflow-y: auto !important;
}

/* Ensure headers stay fixed */
.table-fixed-height .v-data-table__wrapper table {
  position: relative;
}

.table-fixed-height .v-data-table__wrapper thead {
  position: sticky !important;
  top: 0 !important;
  z-index: 100 !important;
  background-color: #f8f9fa !important;
}

.hierarchical-stats-table .v-data-table__wrapper table,
.hierarchical-rank-table .v-data-table__wrapper table {
  width: 100%;
  table-layout: fixed;
}

.hierarchical-stats-table .v-data-table__wrapper table tbody tr td,
.hierarchical-rank-table .v-data-table__wrapper table tbody tr td {
  border-bottom: 1px solid #e9ecef;
  padding: 8px 12px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hierarchical-stats-table .v-data-table__wrapper table thead tr th,
.hierarchical-rank-table .v-data-table__wrapper table thead tr th {
  background-color: #f8f9fa !important;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
  padding: 12px 8px !important;
  white-space: nowrap;
  position: sticky !important;
  top: 0 !important;
  z-index: 99 !important;
}

/* Name column specific styling */
.hierarchical-stats-table .v-data-table__wrapper table tbody tr td:first-child,
.hierarchical-stats-table .v-data-table__wrapper table thead tr th:first-child,
.hierarchical-rank-table .v-data-table__wrapper table tbody tr td:first-child,
.hierarchical-rank-table .v-data-table__wrapper table thead tr th:first-child {
  width: 250px;
  min-width: 250px;
  max-width: 250px;
  text-align: left;
  padding-left: 16px;
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: white;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
}

/* Sticky header for first column needs higher z-index and proper positioning */
.hierarchical-stats-table .v-data-table__wrapper table thead tr th:first-child,
.hierarchical-rank-table .v-data-table__wrapper table thead tr th:first-child {
  position: sticky !important;
  left: 0 !important;
  top: 0 !important;
  z-index: 102 !important;
  background-color: #f8f9fa !important;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  border-radius: 0 !important;
}

/* Row-specific backgrounds for sticky first column */
.hierarchical-stats-table
  .v-data-table__wrapper
  table
  tbody
  tr.user-row
  td:first-child,
.hierarchical-rank-table
  .v-data-table__wrapper
  table
  tbody
  tr.user-row
  td:first-child {
  background-color: #f8f9fa;
}

.hierarchical-stats-table
  .v-data-table__wrapper
  table
  tbody
  tr.admin-row
  td:first-child,
.hierarchical-rank-table
  .v-data-table__wrapper
  table
  tbody
  tr.admin-row
  td:first-child {
  background-color: #f1f8e9;
}

.hierarchical-stats-table
  .v-data-table__wrapper
  table
  tbody
  tr.workspace-row
  td:first-child,
.hierarchical-rank-table
  .v-data-table__wrapper
  table
  tbody
  tr.workspace-row
  td:first-child {
  background-color: #fff3e0;
}

/* Metric columns styling */
.metric-cell {
  font-weight: 500;
  color: #333;
  text-align: right !important;
  width: 130px;
  min-width: 130px;
  max-width: 130px;
}

/* Profile Strength specific styling */
.profile-strength-display {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.profile-strength-display .v-chip {
  min-width: 80px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 4px;
}

/* Tooltip content styling for profile strength */
.v-tooltip__content {
  white-space: pre-line;
  line-height: 1.4;
  max-width: 400px;
}

/* Alignment utility classes */
.text-left {
  text-align: left !important;
}

.text-right {
  text-align: right !important;
}

.text-center {
  text-align: center !important;
}

/* Level indicator styles for hierarchical display */
.level-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

.level-1 {
  padding-left: 4px;
}

.level-2,
.level-3 {
  padding-left: 8px;
}

.expand-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
  color: #666;
  min-width: 20px;
}

.expand-icon:hover {
  color: #1976d2;
}

.rotate-90 {
  transform: rotate(90deg);
}

.expand-placeholder {
  width: 20px;
  height: 20px;
  display: inline-block;
  flex-shrink: 0;
}

.item-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.user-name {
  color: #1976d2;
  font-size: 16px;
  font-weight: 600;
}

.admin-name {
  color: #4caf50;
  font-size: 15px;
  font-weight: 600;
}

.workspace-name {
  color: #ff9800;
  font-size: 14px;
  font-weight: 500;
}

.item-subtitle {
  font-size: 12px;
  color: #4caf50;
  margin-top: 2px;
  line-height: 1.2;
  /* border: 1px solid #1976d2;
  padding: 2px 4px;
  border-radius: 15px; */
  max-width: 80px;
  text-align: center;
}

.user-row {
  background-color: #f8f9fa;
  border-left: 4px solid #1976d2;
  border-top: 1px solid #dee2e6;
  font-weight: 600;
}

.admin-row {
  background-color: #f1f8e9;
  border-left: 4px solid #4caf50;
  border-top: 1px solid #e8f5e8;
}

.workspace-row {
  background-color: #fff3e0;
  border-left: 4px solid #ff9800;
  border-top: 1px solid #fff3e0;
}

/* Expand icon hover effect */
.expand-icon:hover {
  background-color: rgba(25, 118, 210, 0.08);
  border-radius: 50%;
}

/* Sort column button styling */
.sort-column-btn {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.sort-column-btn:hover {
  opacity: 1;
  background-color: rgba(25, 118, 210, 0.08);
}

tr:hover .sort-column-btn {
  opacity: 0.8;
}

/* Timeline Styles */
.timeline-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #1976d2;
}

.timeline-header {
  margin-bottom: 16px;
}

.timeline-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.timeline-description {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.timeline-dates {
  margin-top: 12px;
}

.timeline-chip-wrapper {
  position: relative;
  display: inline-block;
  margin: 4px 8px 4px 0;
}

.timeline-chip {
  transition: all 0.3s ease;
  cursor: pointer;
}

.timeline-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.timeline-chip.v-chip--active {
  background-color: #1976d2 !important;
  color: white !important;
}

/* Fix for primary text color in chip groups */
.timeline-chip.primary--text {
  color: #1976d2 !important;
}

/* Ensure outlined chips with primary color have proper text color */
.timeline-chip.v-chip--outlined.primary--text {
  color: #1976d2 !important;
  border-color: #1976d2 !important;
}

/* Force proper text color for all timeline chip content */
.timeline-chip .v-chip__content {
  color: inherit !important;
}

/* Ensure icons in timeline chips are visible */
.timeline-chip .v-icon {
  color: inherit !important;
}

.timeline-chip:not(.primary--text):not(.v-chip--active) {
  color: rgba(0, 0, 0, 0.87) !important;
}

.timeline-chip:not(.primary--text):not(.v-chip--active) .v-icon {
  color: rgba(0, 0, 0, 0.54) !important;
}

.latest-chip {
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 2;
  font-size: 10px !important;
  height: 18px !important;
  min-width: 45px !important;
  padding: 0 4px !important;
}

/* Trend Chart Styles */
.trend-chart-container {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #4caf50;
}

.chart-header {
  margin-bottom: 16px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
}

.chart-description {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.chart-wrapper {
  position: relative;
  height: 300px;
  width: 100%;
}

.trend-chart {
  max-width: 100%;
  height: 100%;
}

/* Metric Value Container */
.metric-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.metric-value {
  font-weight: 500;
  color: #333;
}

.percentage-change {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
  opacity: 0.9;
}

.change-up {
  color: #4caf50;
}

.change-down {
  color: #f44336;
}

.change-neutral {
  color: #757575;
}

/* Historical Tooltip */
.historical-tooltip {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 12px;
  min-width: 250px;
  max-width: 350px;
  z-index: 1000;
}

.tooltip-header {
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e0e0;
}

.tooltip-content {
  max-height: 200px;
  overflow-y: auto;
}

.tooltip-week {
  padding: 4px 0;
  font-size: 11px;
  border-bottom: 1px solid #f5f5f5;
}

.tooltip-week:last-child {
  border-bottom: none;
}

.tooltip-week.current-week {
  background-color: #e3f2fd;
  padding: 4px 6px;
  border-radius: 4px;
  font-weight: 600;
  color: #1976d2;
}

.week-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.week-date {
  color: #666;
  font-weight: 500;
  flex-shrink: 0;
}

.week-metrics {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.week-value {
  color: #333;
  font-weight: 600;
}

.week-change {
  font-size: 9px;
  font-weight: 600;
  opacity: 0.8;
}

.week-change.change-up {
  color: #4caf50;
}

.week-change.change-down {
  color: #f44336;
}

.week-change.change-neutral {
  color: #757575;
}

.current-week .week-date,
.current-week .week-value {
  color: #1976d2;
}

.current-week .week-change.change-up {
  color: #2e7d32;
}

.current-week .week-change.change-down {
  color: #c62828;
}

.current-week .week-change.change-neutral {
  color: #424242;
}

/* Posts specific styles */
.posts-container {
  padding: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.coming-soon {
  text-align: center;
  max-width: 400px;
}

.coming-soon h2 {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 20px 0 10px 0;
}

.coming-soon p {
  font-size: 16px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.5;
}

/* Timeline Styles for Scheduled Posts */
.timeline-connector {
  width: 2px;
  height: 20px;
  background-color: #e0e0e0;
  margin-top: 2px;
}

.timeline-item {
  position: relative;
}

.timeline-content {
  padding-bottom: 8px;
}

.post-item {
  font-size: 0.75rem;
  margin-left: 8px;
}

/* Rank color coding */
.rank-good {
  color: #4caf50 !important;
  font-weight: 600;
}

.rank-average {
  color: #ff9800 !important;
  font-weight: 600;
}

.rank-poor {
  color: #f44336 !important;
  font-weight: 600;
}

/* White tooltip styling */
.white-tooltip {
  background-color: white !important;
  color: black !important;
  border: 1px solid #e0e0e0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Verification History Styles */
.verification-timeline {
  padding: 8px 0;
}

.verification-attempt {
  position: relative;
  margin-bottom: 12px;
}

.verification-attempt:last-child {
  margin-bottom: 0;
}

.verification-indicator {
  position: relative;
  min-width: 20px;
}

.verification-connector {
  width: 2px;
  height: 24px;
  background-color: #e0e0e0;
  margin-top: 4px;
}

.verification-content {
  padding-left: 8px;
  min-width: 0;
}

.verification-attempt .text-subtitle-2 {
  font-size: 0.875rem;
  line-height: 1.2;
  margin-bottom: 2px;
}

.verification-attempt .text-caption {
  font-size: 0.75rem;
  line-height: 1.2;
  margin-bottom: 2px;
}

/* Verification status specific colors for chips */
.v-chip.success {
  background-color: #4caf50 !important;
  color: white !important;
}

.v-chip.error {
  background-color: #f44336 !important;
  color: white !important;
}

.v-chip.warning {
  background-color: #ff9800 !important;
  color: white !important;
}

.v-chip.info {
  background-color: #2196f3 !important;
  color: white !important;
}

.v-chip.grey {
  background-color: #9e9e9e !important;
  color: white !important;
}

.v-chip.red {
  background-color: #d32f2f !important;
  color: white !important;
}

.v-chip.orange {
  background-color: #ff5722 !important;
  color: white !important;
}

/* Unverified locations highlighting */
.user-row.unverified-user {
  background-color: #ffebee !important;
  border-left: 4px solid #f44336 !important;
}

.admin-row.unverified-admin {
  background-color: #fff3e0 !important;
  border-left: 4px solid #ff9800 !important;
}

.workspace-row.unverified-workspace {
  background-color: #ffebee !important;
  border-left: 4px solid #f44336 !important;
}

/* Priority indicator for high verification attempt counts */
.verification-high-priority {
  background-color: #ffebee !important;
  border: 1px solid #ffcdd2 !important;
}

.verification-medium-priority {
  background-color: #fff3e0 !important;
  border: 1px solid #ffe0b2 !important;
}

/* Enhanced verification status chips */
.verification-status-chip {
  font-weight: 600 !important;
  font-size: 0.75rem !important;
}

.verification-status-chip.verified {
  background-color: #4caf50 !important;
  color: white !important;
}

.verification-status-chip.unverified {
  background-color: #f44336 !important;
  color: white !important;
  animation: pulse-unverified 2s infinite;
}

.verification-status-chip.failed {
  background-color: #d32f2f !important;
  color: white !important;
}

.verification-status-chip.pending {
  background-color: #ff9800 !important;
  color: white !important;
  animation: pulse-pending 1.5s infinite;
}

@keyframes pulse-unverified {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}

@keyframes pulse-pending {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 152, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0);
  }
}

/* User dropdown styling */
.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  width: 400px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: 500px;
  overflow-y: auto;
  padding: 0 1.25rem 0.5rem 1.25rem;
  margin-top: 0.25rem;
}

/* Right-align dropdown when explicitly needed */
.user-dropdown.right-aligned {
  left: auto;
  right: 0;
}

/* Responsive design for smaller screens */
@media (max-width: 600px) {
  .user-dropdown {
    width: 300px;
  }
}
</style>
